/*
// this is how to use the deliverable namespaces within the app for the components to open more than one deliverable.
// For example: opening a social media plan, then opening a social media post within that plan while still keeping the plan open.
// you will need to make sure you have two different namespaces
// pass "" value to namespace prop to use the default store.
// pass "relatedDeliverable" value to use a copy of the default store.
// import { mapGetters, mapState, mapActions } from "vuex";
{
	props: ['namespace'],
  computed: {
  ...mapState({
    state (state) {
      return state[this.namespace]
    },
    someGetter (state, getters) {
      return getters[this.namespace + 'someGetter']
    },
  }),
  methods: {
    ...mapActions({
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
    }),

    ...mapMutations({
      someMutation (commit, payload) {
        return commit(this.namespace + 'someMutation', payload)
      },
    }),

    ...mapActions({
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
      someAction (dispatch, payload) {
        return dispatch(this.namespace + 'someAction', payload)
      },
    }),

  }
}
*/
require("es6-promise").polyfill();
import axios from "axios";
import Vue from "vue";
import router from "../../routes";
import toast from "../../toast";

import { sortTaskFields, sortTasks } from "../../mixin";

import axiosRetry from 'axios-retry';

axiosRetry(axios, {
    retries: 3,
    retryDelay: (retryCount) => {
        return retryCount * 1000;
    },
    retryCondition: (error) => {
        if(error.message && error.message == 'Network Error') return true
    }
});

axios.interceptors.response.use(
    response => {
        return response;
    },
    error => {
        if (!error.response) {
            // network error
            toast.error(
                "Request could not be completed at the moment. There is something wrong with your network connection."
            );
            return new Promise((resolve, reject) => {
                reject(error);
            });
        } else {
            const code = error.response.status;
            const response = error.response.data;
            const message = error.message;
            const headers = error.response.headers;

            if (code === 404) {
                const silent404 = error.config && (error.config.silent404 || error.config.silent === true);
                if (silent404) {
                    return new Promise((resolve, reject) => {
                        reject(error);
                    });
                }
                if (error.response.data.message) {
                    toast.error(error.response.data.message);
                } else {
                    toast.error(message);
                }

                return new Promise((resolve, reject) => {
                    reject(error);
                });
            }
            else if (code === 422) {
                const contentType = headers['content-type'];
                const errorMessages = error.response.data.errors;
                if (errorMessages) {
                    toast.error("Request could not be completed.");
                } else if (contentType.includes('application/json')) {
                    // If it's JSON within Blob, handle it as a JSON response
                    const reader = new FileReader();
                    reader.onload = () => {
                        const jsonResponse = JSON.parse(reader.result);
                        toast.error(jsonResponse.message);
                    };
                    reader.readAsText(error.response.data);
                } 
                return new Promise((resolve, reject) => {
                    reject(error.response.data);
                });
            }
            else if (code === 423) {
                // 423 Locked - Project is being duplicated
                const data = error.response.data;
                
                // Show toast message
                toast.error(data.message || 'This project is currently locked.');
                     
                return new Promise((resolve, reject) => {
                    reject(error);
                });
            }
            else if (code === 401) {
                if(typeof response.message != 'undefined')
                    toast.error(response.message);
                else
                    toast.error('You do not have permission to perform this action.');
                return new Promise((resolve, reject) => {
                    reject(error);
                });
            }
            else {
                // All other error codes (including network errors)
                toast.error(message);
                return new Promise((resolve, reject) => {
                    reject(error);
                });
            }
        }
    }
);

const RECENT_PROJECTS_KEY = 'recentProjects';
const MAX_RECENT = 10;

const defaultFilterParams = {
    query: "",
    sort: '',
    multiSort: [],
    priorityFilter: 0,
    outletCompany: [],
    filterByDate: false,
    filterByDateCreated: false,
    filterByDateCompleted: false,
    filterByPublishDate: false,
    dateRange: {
        startDate: new Date(),
        endDate: new Date()
    },
    dateCreatedRange: {
        startDate: new Date(),
        endDate: new Date()
    },
    dateCompletedRange: {
        startDate: new Date(),
        endDate: new Date()
    },
    datePublishRange: {
        startDate: new Date(),
        endDate: new Date()
    },
    status: [],
    publishStatus: [],
    outlet: [],
    deliverableType: [],
    tag: [],
    department: [],
    coordinator: [],
    project: [],
    noDeparment: 'not_accepted',
    noOutlet: 'not_accepted',
};
const loadPersistedGlobalSearchTabFilters = () => {
    try {
        const raw = localStorage.getItem('globalSearchTabFilters');
        if (!raw) return {};
        return JSON.parse(raw);
    } catch (e) {
        return {};
    }
};
const getDefaultGlobalSearchFilter = (tabType = 'all') => {
    const baseFilter = {
        query: "",
        sort: '',
        multiSort: [],
        priorityFilter: 0,
        outletCompany: [],
        filterByDate: false,
        filterByDateCreated: false,
        filterByDateCompleted: false,
        dateRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCreatedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCompletedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        status: tabType === 'all' ? [] : ['Active'],
        outlet: [],
        deliverableType: [],
        tag: [],
        department: [],
        coordinator: [],
        project: [],
        noDeparment: 'not_accepted',
        noOutlet: 'not_accepted',
        weekFilterOptions: {
            overdue: false,
            today: false,
            thisWeek: false,
            nextWeek: false,
            upcoming: false,
            noDeadline: false,
        }
    };
    return baseFilter;
};
const state = () => ({
	recentProjectIds: JSON.parse(localStorage.getItem(RECENT_PROJECTS_KEY)) || [],
	showPostContentLangInListing:1,
	showPostContentNetworkInListing:1,
	openWithFocus:null,
	refreshKey:0,
	isDirty:false,
	componentState:localStorage.getItem('componentState') ? JSON.parse(localStorage.getItem('componentState')) : [],
	activeTab:localStorage.getItem('activeTab') ? JSON.parse(localStorage.getItem('activeTab')) : [],
	_namespace: "",
	_namespaceKey: "",
    _recentSearchTimer: null,
    storedSortFieldId: localStorage.getItem('storedSortFieldId'),
    storedSortKey: localStorage.getItem('storedSortKey'),
    nextSortState: localStorage.getItem('nextSortState'),
    storedSortState: localStorage.getItem('storedSortState'),
    storedTaskFieldsOrder: [{1: [14,12,4,2,13]}],
    storedTaskOrder: null,
    forceUpdateCounter: 0,
    preferenceData: null,
    loggedInUserData: null,
    duplicationWarning: {
        show: false,
        message: '',
        startedBy: '',
        progress: 0,
        jobKey: '',
        startedAt: '',
        context: '',
        jobMessage: ''
    },
    auditLogs: [],
    auditLogTags: [],
    auditLogContacts: [],
    auditLogDepartments: [],
    auditLogCoordinators: [],
    auditLogDeliverables: [],
    auditLogProjects: [],
    activateUndo: false,
    activateUndoText: '',
    activateUndoNoScroll: false,
    activateMyProjectUndo: false,
    activateMyProjectUndoId: '',
    breadcrumbs: {},
    newNotificationPopupClose: false,
    userViews: [],
    GSRecentSearch: (() => {
        try {
            const saved = localStorage.getItem('GSRecentSearch');
            if (saved) {
                return JSON.parse(saved);
            }
        } catch (e) {
        }
        return {
        all: [],
        tags: [],
        departments: [],
        contacts: [],
        coordinators: []
        };
    })(),
    allRoles: [],
    allAbilities: [],
    allCompanies: [],
    initialContacts: [],
    initialTags: [],
    initialDepartments: [],
    globalSearchData: {
        tags: {
            total: false
        },
        departments: {
            total: false
        },
        coordinators: {
            total: false
        },
        contacts: {
            total: false
        }
    },
    currentPage: false,
    deliverables: [],
    mergeDeliverables: [],
    projectDeliverables: [],
    calendarDeliverables: [],
    yearCalendarDeliverables: [],
    settings:null,
    projects: [],
    userProjects: [],
    userManageProjects: [],
    userManageSocialCampaigns: [],
    duplicatingDeliverable: {
    	status:'',
    	details:null,
    	projectId:null,
    	projectNumber:null,
    	deliverableId:null,
    	newDeliverableId:null,
    	newProjectNumber:null,
    	details:null,
    	message:null,
    	beforeTotal:0,
    	progress:0,
    	progressLabel:null,
    	progressDetails:null,
    	jobKey:null
    },
    duplicateDeliverableId:null,
    duplicatedNewProjectNumber:null,
    allProjects: [],
    userHasProjects: false,
    defaultFilterParams: defaultFilterParams,
    deliverableFilter: {
        query: "",
        sort: '',
        multiSort: [],
        priorityFilter: 0,
        parentDeliverableId: 0,
        outletCompany: [],
        filterByDate: false,
        filterByDateCreated: false,
        filterByDateCompleted: false,
    	filterByPublishDate: false,
        dateRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCreatedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCompletedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        datePublishRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        status: [],
        publishStatus: [],
        socialNetwork: [],
        outlet: [],
        deliverableType: [],
        tag: [],
        department: [],
        coordinator: [],
        project: [],
        noDeparment: 'not_accepted',
        noOutlet: 'not_accepted',
        weekFilterOptions: {
            overdue: false,
            today: false,
            thisWeek: false,
            nextWeek: false,
            upcoming: false,
            noDeadline: false,
        }
    },
    socialMediaFilter: {
        query: "",
        sort: '',
        multiSort: [],
        priorityFilter: 0,
        parentDeliverableId: 0,
        outletCompany: [],
        filterByDate: false,
        filterByDateCreated: false,
        filterByDateCompleted: false,
    	filterByPublishDate: false,
        hasPostDate: null,
    	listByPublishDate: false,
    	listByDate:true,
    	listByDateField:"publish_date",
    	listByDateRange: {
    	    startDate: new Date(),
    	    endDate: new Date()
    	},
        dateRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCreatedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCompletedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        datePublishRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        listDatePublishRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        status: [],
        publishStatus: [],
        socialNetwork: [],
        socialSeries: [],
        listSocialSeries: [],
        listSocialCampaign: [],
        socialCampaign: [],
        outlet: [],
        deliverableType: [],
        tag: [],
        department: [],
        coordinator: [],
        project: [],
        noDeparment: "not_accepted",
        noOutlet: "not_accepted",
        weekFilterOptions: {
            overdue: false,
            today: false,
            thisWeek: false,
            nextWeek: false,
            upcoming: false,
            noDeadline: false,
        }
    },
    socialMediaCalendarFilter: {
        query: "",
        sort: '',
        multiSort: [],
        priorityFilter: 0,
        parentDeliverableId: 0,
        outletCompany: [],
        filterByDate: false,
        filterByDateCreated: false,
        filterByDateCompleted: false,
    	filterByPublishDate: false,
    	listByPublishDate: false,
    	listByDate:false,
    	listByDateField:"publish_date",
    	listByDateRange: {
    	    startDate: new Date(),
    	    endDate: new Date()
    	},
        dateRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCreatedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCompletedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        datePublishRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        listDatePublishRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        status: [],
        publishStatus: [],
        socialNetwork: [],
        socialSeries: [],
        listSocialSeries: [],
        listSocialCampaign: [],
        socialCampaign: [],
        outlet: [],
        deliverableType: [],
        tag: [],
        department: [],
        coordinator: [],
        project: [],
        noDeparment: "not_accepted",
        noOutlet: "not_accepted",
        weekFilterOptions: {
            overdue: false,
            today: false,
            thisWeek: false,
            nextWeek: false,
            upcoming: false,
            noDeadline: false,
        }
    },
    mediaCallFilter: {
        query: "",
        sort: '',
        multiSort: [],
        priorityFilter: 0,
        outletCompany: [],
        filterByDate: false,
        filterByDateCreated: false,
        filterByDateCompleted: false,
        dateRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCreatedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCompletedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        status: [],
        outlet: [],
        deliverableType: [],
        tag: [],
        department: [],
        coordinator: [],
        project: [],
        noDeparment: 'not_accepted',
        noOutlet: 'not_accepted',
        weekFilterOptions: {
            overdue: false,
            today: false,
            thisWeek: false,
            nextWeek: false,
            upcoming: false,
            noDeadline: false,
        }
    },
    addProjectDeliverableFilter: {
        query: "",
        sort: '',
        multiSort: [],
        priorityFilter: 0,
        parentDeliverableId: 0,
        outletCompany: [],
        filterByDate: false,
        filterByDateCreated: false,
        filterByDateCompleted: false,
        dateRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCreatedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCompletedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        status: [],
        outlet: [],
        deliverableType: [],
        tag: [],
        department: [],
        coordinator: [],
        noDeparment: 'not_accepted',
        noOutlet: 'not_accepted',
        weekFilterOptions: {
            overdue: false,
            today: false,
            thisWeek: false,
            nextWeek: false,
            upcoming: false,
            noDeadline: false,
        }
    },
    projectDeliverableListFilter: {
        query: "",
        sort: '',
        multiSort: [],
        priorityFilter: 0,
        outletCompany: [],
        filterByDate: false,
        filterByDateCreated: false,
        filterByDateCompleted: false,
        dateRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCreatedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCompletedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        status: [],
        outlet: [],
        deliverableType: [],
        tag: [],
        department: [],
        coordinator: [],
        noDeparment: 'not_accepted',
        noOutlet: 'not_accepted',
        weekFilterOptions: {
            overdue: false,
            today: false,
            thisWeek: false,
            nextWeek: false,
            upcoming: false,
            noDeadline: false,
        }
    },
    myWeekDeliverableFilter: {
        query: "",
        sort: '',
        multiSort: [],
        priorityFilter: 0,
        parentDeliverableId: 0,
        outletCompany: [],
        filterByDate: false,
        filterByDateCreated: false,
        filterByDateCompleted: false,
        dateRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCreatedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCompletedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        status: [],
        outlet: [],
        deliverableType: [],
        tag: [],
        department: [],
        coordinator: [],
        project: [],
        noDeparment: 'not_accepted',
        noOutlet: 'not_accepted',
        weekFilterOptions: {
            overdue: false,
            today: false,
            thisWeek: false,
            nextWeek: false,
            upcoming: false,
            noDeadline: false,
        }
    },
    dashboardMediaCallsFilter: {
        query: "",
        sort: '',
        multiSort: [],
        priorityFilter: 0,
        outletCompany: [],
        filterByDate: false,
        filterByDateCreated: false,
        filterByDateCompleted: false,
        dateRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCreatedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCompletedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        status: [],
        outlet: [],
        deliverableType: [],
        tag: [],
        department: [],
        coordinator: [],
        project: [],
        noDeparment: 'not_accepted',
        noOutlet: 'not_accepted',
        weekFilterOptions: {
            overdue: false,
            today: false,
            thisWeek: false,
            nextWeek: false,
            upcoming: false,
            noDeadline: false,
        },
        dashboardType: '',
    },
    calendarFilter: {
        query: "",
        sort: '',
        dataSetType:"",
        calendarViewType: "",
        multiSort: [],
        priorityFilter: 0,
        outletCompany: [],
        filterByDate: false,
        filterByDateCreated: false,
        filterByDateCompleted: false,
    	filterByPublishDate: false,
        dateRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCreatedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCompletedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        datePublishRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        listByDate: true,
    	listByDateRange: {
    	    startDate: new Date(),
    	    endDate: new Date()
    	},
        status: [],
        publishStatus: [],
        socialNetwork: [],
        outlet: [],
        deliverableType: [],
        tag: [],
        department: [],
        coordinator: [],
        project: [],
        noDeparment: 'not_accepted',
        noOutlet: 'not_accepted',
        weekFilterOptions: {
            overdue: false,
            today: false,
            thisWeek: false,
            nextWeek: false,
            upcoming: false,
            noDeadline: false,
        }
    },
    projectCalendarFilter: {
        query: "",
        sort: '',
        dataSetType:"projects",
        calendarViewType: "",
        multiSort: [],
        priorityFilter: 0,
        outletCompany: [],
        filterByDate: false,
        filterByDateCreated: false,
        filterByDateCompleted: false,
    	filterByPublishDate: false,
        dateRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCreatedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCompletedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        datePublishRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        listByDate: true,
    	listByDateRange: {
    	    startDate: new Date(),
    	    endDate: new Date()
    	},
        status: [],
        publishStatus: [],
        socialNetwork: [],
        outlet: [],
        deliverableType: [],
        tag: [],
        department: [],
        coordinator: [],
        project: [],
        noDeparment: 'not_accepted',
        noOutlet: 'not_accepted',
        weekFilterOptions: {
            overdue: false,
            today: false,
            thisWeek: false,
            nextWeek: false,
            upcoming: false,
            noDeadline: false,
        }
    },
    globalSearchTabFilters: {
        all: null,
        departments: null,
        contacts: null,
        coordinators: null,
        tags: null,
        ...loadPersistedGlobalSearchTabFilters(),
    },
    globalSearchFilter: (() => {
        const persisted = loadPersistedGlobalSearchTabFilters();
        return persisted.all ? persisted.all : getDefaultGlobalSearchFilter('all');
    })(),
    projectFilter: {
        query: "",
        sort: '',
        multiSort: [],
        priorityFilter: 0,
        filterByDate: false,
        filterByDateCreated: false,
        dateRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCreatedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        department: [],
        coordinator: [],
        noDepartment: 'not_accepted',
        noCoordinator: 'not_accepted',
        weekFilterOptions: {
            overdue: false,
            today: false,
            thisWeek: false,
            nextWeek: false,
            upcoming: false,
            noDeadline: false,
        },
        status: [],
    },
    myProjectFilter: {
        query: "",
        sort: '',
        multiSort: [],
        priorityFilter: 0,
        filterByDate: false,
        filterByDateCreated: false,
        dateRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCreatedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        department: [],
        coordinator: [],
        noDepartment: 'not_accepted',
        noCoordinator: 'not_accepted',
        weekFilterOptions: {
            overdue: false,
            today: false,
            thisWeek: false,
            nextWeek: false,
            upcoming: false,
            noDeadline: false,
        },
        status: []
    },
    reportFilter: {
        query: "",
        sort: '',
        multiSort: [],
        priorityFilter: 0,
        outletCompany: [],
        filterByDate: false,
        filterByDateCreated: false,
        filterByDateCompleted: false,
        dateRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCreatedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        dateCompletedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        filterByDuration: false,
        filterDuration: {
            durationMin: null,
            durationMax: null
        },
        status: [],
        outlet: [],
        deliverableType: [],
        tag: [],
        department: [],
        coordinator: [],
        noDeparment: 'not_accepted',
        noOutlet: 'not_accepted',
        weekFilterOptions: {
            overdue: false,
            today: false,
            thisWeek: false,
            nextWeek: false,
            upcoming: false,
            noDeadline: false,
        }
    },
    auditLogFilter: {
        query: "",
        filterByDateCreated: false,
        dateCreatedRange: {
            startDate: new Date(),
            endDate: new Date()
        },
        coordinator: []
    },
    allDeliverablesPaginationData: {},
    allProjectsPaginationData: {},
    allUserProjectsPaginationData: {},
    allProjectDeliverablesPaginationData: {},
    userDeliverablesPaginationData: {},
    userDeliverables: {
        overdue: [],
        today: [],
        thisWeek: [],
        nextWeek: [],
        upcoming: [],
        noDeadline: [],
        projectOnHold: [],
        completed: []
    },
    contacts: [],
    activeContacts: [],
    users: [],
    activeUsers: [],
    deliverableDetail: [],
    parentDeliverableDetail: [],
    projectDetail: [],
    departments: [],
    activeDepartments: [],
    subjects: [],
    tags: [],
    trendingTags: [],
    editDeliverableModal: false,
    editProjectModal: false,
    addContactModal: false,
    editContactModal: false,
    editSocialModal: false,
    newContactName: "",
    deliverableId: "",
    countries: [],
    regions: [],
    countryStates: [],
    currentContactId: "",
    currentContactData: {},
    currentFieldKey: "",
    currentContactTemplateType: "",
    customFields: [],
    editingField: false,
    unGroupedUserDeliverables: [],
    outletCount: 0,
    userAlerts: {},
    allAlerts: {},
    globalAlertTypes: [],
    userNotificationPreferences: [],
    notification: {},
    allDeliverableTypes: [],
    deliverableTypes: [],
    newDeliverableTypes:[],
    preferenceDeliverableTypes: [],
    calendarEvents: [],
    projectCalendarEvents: [],
    dataLoading: false,
    deliverableCount: {},
    tasksToHighlight: [],
    taskHighlighted: null,
    taskHighlightTimeout: null,
    newProjectCreated: false,
    projectSort: '',
    myProjectSort: '',
    numberStat: {},
    widgetSettings: {},
    systemSettings: {},
    listSocialSeries: [],
    listSocialCampaigns: [],
    socialSeries: [],
    socialCampaigns: [],
    socialCampaignDetail: {},
    socialSeriesDetail: {},
    socialGroupBy: 'socialMedia', // what user last selected, group by date or campaign/series
});

const mutations = {
	SET_RECENT_PROJECTS(state, ids) {
	   state.recentProjectIds = ids;
	   localStorage.setItem(RECENT_PROJECTS_KEY, JSON.stringify(ids));
	 },

	showPostContentNetworkInListing:(state, showPostContentNetworkInListing) => {
	    state.showPostContentNetworkInListing = showPostContentNetworkInListing;
	},
	showPostContentLangInListing:(state, showPostContentLangInListing) => {
	    state.showPostContentLangInListing = showPostContentLangInListing;
	},

    updateNewContactRelatedValues(state, { 
        newContactName, 
        deliverableId, 
        fieldKey, 
        currentContactTemplateType 
    }) {
        state.newContactName = newContactName;
        state.deliverableId = deliverableId;
        state.currentFieldKey = fieldKey;
        state.currentContactTemplateType = currentContactTemplateType;
    },
    updateEditContactRelatedValues(state, { 
        currentContactData, 
        currentContactId, 
        currentFieldKey, 
        currentContactTemplateType 
    }) {
        state.currentContactData = currentContactData;
        state.currentContactId = currentContactId;
        state.currentFieldKey = currentFieldKey;
        state.currentContactTemplateType = currentContactTemplateType;
    },

    setIsDirty:(state, isDirty) => {
        state.isDirty = isDirty;
    },

    SHOW_DUPLICATION_WARNING(state, payload) {
        state.duplicationWarning = {
            show: true,
            message: payload.message || '',
            startedBy: payload.startedBy || '',
            progress: payload.progress || 0,
            jobKey: payload.jobKey || '',
            startedAt: payload.startedAt || '',
            context: payload.context || '',
            jobMessage: payload.jobMessage || ''
        };
    },
    
    HIDE_DUPLICATION_WARNING(state) {
        state.duplicationWarning = {
            show: false,
            message: '',
            startedBy: '',
            progress: 0,
            jobKey: '',
            startedAt: '',
            context: '',
            jobMessage: ''
        };
    },

    setNumberStat:(state, numberStat) => {
        state.numberStat = numberStat;
    },
    setWidgetSettings:(state, widgetSettings) => {
        state.widgetSettings = widgetSettings;
    },
    updateDepartmentList: (state, departments) => {
        let departmentsArr = [];
        departments.forEach(function(item, index) {
            departmentsArr.push({ id: item.id, title: item.name });
        });
        state.departments = departmentsArr
    },
    resetProjectDeliverables: (state) => {
        state.projectDeliverables = []
    },
    setCalendarEvents: (state, eventsData) => {
        state.calendarEvents = eventsData
    },
    setProjectCalendarEvents: (state, eventsData) => {
        state.projectCalendarEvents = eventsData
    },
    setUserPreference: (state, preferenceData) => {
        state.preferenceData = preferenceData ? preferenceData : {deliverable_type: null};
        state.storedTaskFieldsOrder = preferenceData && typeof preferenceData.taskFieldOrder != 'undefined' ? preferenceData.taskFieldOrder : [{1: [14,12,4,2,13]}]
        state.storedTaskOrder = preferenceData && typeof preferenceData.taskOrder != 'undefined' ? preferenceData.taskOrder : null
    },
    setLoggedInUserData: (state, loggedInUserData) => {
        state.loggedInUserData = loggedInUserData;
    },
    changeForceUpdateCounter: (state) => {
        state.forceUpdateCounter += 1;
    },

    setDeliverableTypes: (state, deliverableTypes) => {
        state.deliverableTypes = deliverableTypes;
    },
    setAllDeliverableTypes: (state, deliverableTypes) => {
        state.allDeliverableTypes = deliverableTypes;
    },
    setNewDeliverableTypes: (state, deliverableTypes) => {
        state.newDeliverableTypes = deliverableTypes;
    },
    setPreferenceDeliverableTypes: (state, deliverableTypes) => {
        state.preferenceDeliverableTypes = deliverableTypes;
    },
    setAuditLogs: (state, auditLogs) => {
        state.auditLogs = auditLogs.all;
        state.auditLogTags = auditLogs.tags;
        state.auditLogContacts = auditLogs.contacts;
        state.auditLogDepartments = auditLogs.departments;
        state.auditLogCoordinators = auditLogs.coordinators;
        state.auditLogDeliverables = auditLogs.deliverables;
        state.auditLogProjects = auditLogs.projects;
    },
    setActivateUndo: (state, status) => {
        state.activateUndo = status;
    },
    setActivateUndoText: (state, text) => {
        state.activateUndoText = text;
    },
    setActivateUndoNoScroll: (state, noScroll) => {
        state.activateUndoNoScroll = noScroll;
    },
    setActivateMyProjectUndo: (state, status) => {
        state.activateMyProjectUndo = status;
    },
    setActivateMyProjectUndoId: (state, id) => {
        state.activateMyProjectUndoId = id;
    },
    setUserAlerts: (state, alerts) => {
        state.userAlerts = alerts;
    },
    setUserNotificationPreferences: (state, preferences) => {
        state.userNotificationPreferences = preferences;
    },
    setAllAlerts: (state, alerts) => {
        state.allAlerts = alerts;
    },
    setGlobalAlertTypes: (state, alertTypes) => {
        state.globalAlertTypes = alertTypes;
    },
    setBreadcrumbs: (state, breadcrumbs) => {
        state.breadcrumbs = breadcrumbs;
    },
    setNotification: (state, notification) => {
        state.notification = notification;
    },
    setRecentSearch: (state, {searchQuery, activeTab, immediate = false}) => {
        if(searchQuery != 'resetSearch') {
            const trimmedQuery = searchQuery.trim();
            if(trimmedQuery === '') return;
            if (state._recentSearchTimer) {
                clearTimeout(state._recentSearchTimer);
            }
            
            const saveSearch = () => {
                const index = state.GSRecentSearch[activeTab].indexOf(trimmedQuery);
                if(index !== -1) {
                    state.GSRecentSearch[activeTab].splice(index, 1);
                }
                state.GSRecentSearch[activeTab].unshift(trimmedQuery);
                if(state.GSRecentSearch[activeTab].length > 30) {
                    state.GSRecentSearch[activeTab] = state.GSRecentSearch[activeTab].slice(0, 30);
                }
                try {
                    const recentSearchData = {
                        all: state.GSRecentSearch.all,
                        tags: state.GSRecentSearch.tags,
                        departments: state.GSRecentSearch.departments,
                        contacts: state.GSRecentSearch.contacts,
                        coordinators: state.GSRecentSearch.coordinators
                    };
                    localStorage.setItem('GSRecentSearch', JSON.stringify(recentSearchData));
                } catch (e) {
                    }
            };
            if (immediate) {
                saveSearch();
            } else {
                state._recentSearchTimer = setTimeout(saveSearch, 5000);
            }
        } else {
            if(activeTab == 'all') {
                state.GSRecentSearch = {
                    all: [],
                    tags: [],
                    departments: [],
                    contacts: [],
                    coordinators: []
                };
            } else {
                state.GSRecentSearch[activeTab] = [];
            }
                try {
                    localStorage.setItem('GSRecentSearch', JSON.stringify(state.GSRecentSearch));
                } catch (e) {
            }
        }
    },
    setAllCompanies: (state, data) => {
        state.allCompanies = data
    },
    setInitialData: (state, data) => {
        if(typeof data.contacts != 'undefined') {
            state.initialContacts = data.contacts;
        } else if(typeof data.tags != 'undefined') {
            state.initialTags = data.tags;
        } else if(typeof data.departments != 'undefined') {
            state.initialDepartments = data.departments;
        }
    },
    setDeliverableFilter: (state, filter) => {
        const globalSearchRoutes = ['globalSearchAll', 'globalSearchDepartments', 'globalSearchContacts', 'globalSearchUsers', 'globalSearchTags']; 
        const calendarRoutes = ['calendar', 'calendarDeliverableDetail'];
        if (router.currentRoute.name == "myWeek") {
            state.myWeekDeliverableFilter = filter;
        } else if(router.currentRoute.name == "dashboard") {
            state.deliverableFilter = filter;
        } else if(calendarRoutes.includes(router.currentRoute.name)) {
            state.calendarFilter = filter;
        } else if(globalSearchRoutes.includes(router.currentRoute.name)) {
            state.globalSearchFilter = filter;
        } else if(router.currentRoute.name == "report") {
            state.reportFilter = filter;
        } else if(router.currentRoute.name == "project") {
            state.projectFilter = filter;
        } else if(router.currentRoute.name == "myProjects") {
            state.myProjectFilter = filter;
        } else if(router.currentRoute.name == "socialMedia") {
            state.socialMediaFilter = filter;
        } else if(router.currentRoute.name == "socialMediaGrouped") {
            state.socialMediaFilter = filter;
        } else if(router.currentRoute.name == "socialMediaSchedule") {
            state.socialMediaCalendarFilter = filter;
        }
    },
    setGlobalSearchTabFilter: (state, { tabType, filter }) => {
        if (state.globalSearchTabFilters.hasOwnProperty(tabType)) {
            state.globalSearchTabFilters[tabType] = JSON.parse(JSON.stringify(filter));
            try {
                localStorage.setItem('globalSearchTabFilters', JSON.stringify(state.globalSearchTabFilters));
            } catch (e) {
            }
        }
    },
    restoreGlobalSearchTabFilter: (state, tabType) => {
        if (state.globalSearchTabFilters[tabType]) {
            state.globalSearchFilter = JSON.parse(JSON.stringify(state.globalSearchTabFilters[tabType]));
        } else {
            state.globalSearchFilter = getDefaultGlobalSearchFilter(tabType);
        }
    },
    setDashboardMediaCallsFilter: (state, filter) => {
            state.dashboardMediaCallsFilter = filter;
    },
    setProjectCalendarFilter: (state, filter) => {
        state.projectCalendarFilter = filter;
    },
    setAuditLogFilter: (state, filter) => {
        state.auditLogFilter = filter;
    },
    setDashboardDeliverableFilter: (state, filter) => {
        Vue.set(state, 'deliverableFilter', JSON.parse(filter));
    },
    setMediaCallFilter: (state, filter) => {
        Vue.set(state, 'mediaCallFilter', JSON.parse(filter));
    },
    setMyWeekDeliverableFilter: (state, filter) => {
        Vue.set(state, 'myWeekDeliverableFilter', JSON.parse(filter));
    },
    setSocialMediaFilter: (state, filter) => {
        state.socialMediaFilter = filter;
    },
    setSocialMediaCalendarFilter: (state, filter) => {
        state.socialMediaCalendarFilter = filter;
    },
    setAddProjectDeliverableFilter: (state, filter) => {
        Vue.set(state, 'addProjectDeliverableFilter', JSON.parse(filter));
    },
    setProjectDeliverableListFilter: (state, filter) => {
        state.projectDeliverableListFilter = filter;
    },
    setProjectFilter: (state, filter) => {
        Vue.set(state, 'projectFilter', JSON.parse(filter));
    },
    setMyProjectFilter: (state, filter) => {
        Vue.set(state, 'myProjectFilter', JSON.parse(filter));
    },
    setViews: (state, views) => {
        state.userViews = views;
    },
    setDashboardFilter: (state, filter) => {
        Vue.set(state, 'deliverableFilter', filter);
    },
    setGlobalSearchData: (state, searchData) => {
        state.globalSearchData = searchData;
    },
    setOutletCount: (state, outletCount) => {
        state.outletCount = outletCount;
    },
    increaseOutletCount: state => {
        state.outletCount += 1;
    },
    increaseRefreshKey: state => {
        state.refreshKey += 1;
    },
    setProjects: (state, projects) => {
        state.projects = projects.allProjects.data;
        state.allProjectsPaginationData = projects.allProjects;
    },
    setAllProjects: (state, projects) => {
        state.allProjects = projects.allProjects;
    },
    setUserProjects: (state, projects) => {
        state.userProjects = projects.userProjects.data;
        state.allUserProjectsPaginationData = projects.userProjects;
    },
    setUserProjectsOnly: (state, projects) => {
        state.userProjects = projects;
    },
    setProjectsOnly: (state, projects) => {
        state.projects = projects;
    },
    setUserManageProjects: (state, projects) => {
        state.userManageProjects = projects.userProjects;
    },
    setUserManageSocialCampaigns: (state, socialCampaigns) => {
        state.userManageSocialCampaigns = socialCampaigns.userCampaigns;
    },
    setUserHasProjects: (state, count) => {
        if (count) {
            state.userHasProjects = count > 0;
        } else {
            state.userHasProjects = false;
        }
    },


    setProjectEngagementManagerLock: (state,responseData) => {
		if (state.projectDetail.id) {
	    	if (state.projectDetail.id != responseData.projectDetail.id) {
		    } else {
		    	state.projectDetail.engagement_manager_locked = responseData.projectDetail.engagement_manager_locked;
		    }
		}
		let x = 0;
		if (
		    typeof state.projects != "undefined"
		) {
		    for (x = 0; x < Object.keys(state.projects).length; x++) {
		        if (typeof state.projects[x] != 'undefined' && state.projects[x].id == responseData.projectDetail.id) {
		            Vue.set(state.projects[x], "engagement_manager_locked", responseData.projectDetail.engagement_manager_locked);
		        }
		    }
		}
		if (
		    typeof state.userProjects != "undefined"
		) {
		    for (x = 0; x < Object.keys(state.userProjects).length; x++) {
		        if (typeof state.userProjects[x] != 'undefined' && state.userProjects[x].id == responseData.projectDetail.id) {
		            Vue.set(state.userProjects[x], "engagement_manager_locked", responseData.projectDetail.engagement_manager_locked);
		        }
		    }
		}

	},


    setProjectWithField: (state,responseData) => {
    	// state.projectDetail = responseData.projectDetail;
    	var projectDetail = null;
    	if (responseData.original
    		&& responseData.original.projectDetail
    	) {
    		projectDetail = responseData.original.projectDetail;
    	} else {
    		projectDetail = responseData.projectDetail;
    	}
    	if (state.projectDetail.id) {
	    	if (state.projectDetail.id != projectDetail.id) {
    			state.projectDetail = projectDetail;
			  	// console.log(projectAndField.field);
		    	// console.log(projectDetail);
		    	// deepMergeJsonObjects(projectDetail, state.projectDetail);
		    } else {
		    	if (responseData.updatedProjectFieldValueResponse) {
		    		if (responseData.updatedProjectFieldValueResponse.ProjectPreviouslyUpdatedAt != state.projectDetail.updated_at) {
		    			// console.log(responseData.updatedProjectFieldValueResponse.ProjectPreviouslyUpdatedAt);
		    			// console.log(state.projectDetail.updated_at);
    					state.projectDetail = projectDetail;
		    		} else {
    					state.projectDetail.updated_at = projectDetail.updated_at;
		    		}
		    	}
		    }
		}
    	// loop through all deliverables and update the deliverable.
    	let x = 0;
    	if (
    	    typeof state.projects != "undefined"
    	) {
    	    for (x = 0; x < Object.keys(state.projects).length; x++) {
    	        if (typeof state.projects[x] != 'undefined' && state.projects[x].id == projectDetail.id) {
    	            Vue.set(state.projects, x, projectDetail);
    	        }
    	    }
    	}

    	if (
    	    typeof state.userProjects != "undefined"
    	) {
    	    for (x = 0; x < Object.keys(state.userProjects).length; x++) {
    	        if (typeof state.userProjects[x] != 'undefined' && state.userProjects[x].id == projectDetail.id) {
    	            Vue.set(state.userProjects, x, projectDetail);
    	        }
    	    }
    	}
    },



    setProject: (state, project) => {
        state.projectDetail = project;


        // loop through all deliverables and update the deliverable.
        let x = 0;
        if (
            typeof state.projects != "undefined"
        ) {
            for (x = 0; x < Object.keys(state.projects).length; x++) {
                if (typeof state.projects[x] != 'undefined' && state.projects[x].id == project.id) {
                    Vue.set(state.projects, x, project);
                }
            }
        }
    },
    setProjectDeliverablesOnly: (state, deliverables) => {
        state.projectDeliverables = deliverables;
        state.projectDeliverables = sortTasks({sortKey: false, deliverables: state.projectDeliverables, return: true, sortFields: true})
    },
    setProjectDeliverables: (state, deliverables) => {
        state.projectDeliverables = deliverables.projectDeliverables.data;
        state.projectDeliverables = sortTasks({sortKey: false, deliverables: state.projectDeliverables, return: true, sortFields: true, namespace:state._namespace})
        state.allProjectDeliverablesPaginationData = deliverables.projectDeliverables;
    },
    setProjectDeliverable: (state, deliverable) => {
        if(state.projectDeliverables.length > 0) {
            state.projectDeliverables.forEach((value, key) => {
                if(value.id == deliverable.id) {
                    state.projectDeliverables[key].tasks = deliverable.tasks
                }
            })
        }
        // state.projectDeliverables = deliverables.projectDeliverables.data;
        // state.projectDeliverables = sortTasks({sortKey: false, deliverables: state.projectDeliverables, return: true, sortFields: true, namespace:state._namespace})
        // state.allProjectDeliverablesPaginationData = deliverables.projectDeliverables;
    },
    resetProjectDeliverables: (state, deliverables) => {
        state.projectDeliverables = [];
        state.allProjectDeliverablesPaginationData = {};
    },
    clearMergeDeliverables: (state, deliverables) => {
    	state.mergeDeliverables = [];
	},
	setProjectStatusForDeliverables: (state, payload) => {
		if(typeof state.projectDeliverables != 'undefined') {
		    for (let x = 0; x < Object.keys(state.projectDeliverables).length; x++) {
		        if (typeof state.projectDeliverables[x] != 'undefined') {
		        	if (state.projectDeliverables[x].project_id.id == payload.project_id) {
			        	state.projectDeliverables[x].project_id.project_status = payload.value;
			        }
		        }
		    }
		}

	},



    mergeDeliverables: (state, deliverables) => {
        if (typeof deliverables.allDeliverables != "undefined") {
            // delete deliverables.allDeliverables.data.totalOutlets
            state.mergeDeliverables = deliverables.allDeliverables.data;
            sortTasks({sortKey: false, deliverables: state.mergeDeliverables, sortFields: true, namespace:state._namespace})

        	for(let x = 0; x < state.mergeDeliverables.length; x++) {
        		let found = false;
            	for(let y = 0; y < state.deliverables.length; y++) {
            		if (state.deliverables[y].id == state.mergeDeliverables[x].id) {
            			found = true;
            		}
            	}
            	if (!found) {
					state.deliverables.push(state.mergeDeliverables[x]);
            		// resort it by publish date.
            		// state.deliverables = _.sortBy(state.deliverables,"is_master", "publish_date");
            	}
			}
			state.deliverables = _.chain(state.deliverables)
				.sortBy(function(deliverable) {
				   if (deliverable.is_master)
				       return Number.NEGATIVE_INFINITY;
				   else
				       return +deliverable.is_master;
				})
				.sortBy(function(deliverable) {
					if (deliverable.publish_date == null && deliverable.is_master == 0)
				           return Number.POSITIVE_INFINITY;
				       else
				           return Vue.moment(deliverable.publish_date);

				})
			  .value();
        }
    },





    setDeliverables: (state, deliverables) => {
        let now = Vue.moment().format("YYYY-MM-DD"),
            thisWeekStart = Vue.moment()
                .startOf("week")
                .format("YYYY-MM-DD"),
            thisWeekEnd = Vue.moment()
                .endOf("week")
                .format("YYYY-MM-DD"),
            nextWeekStart = Vue.moment()
                .add(1, "weeks")
                .startOf("week")
                .format("YYYY-MM-DD"),
            nextWeekEnd = Vue.moment()
                .add(1, "weeks")
                .endOf("week")
                .format("YYYY-MM-DD"),
            today = [],
            thisWeek = [],
            nextWeek = [],
            upcoming = [],
            noDeadline = [],
            overdue = [],
            projectOnHold = [],
            completed = [];

        if (typeof deliverables.allDeliverables != "undefined") {

            if (state.mergeDeliverables) {
            	for(let x = 0; x < state.mergeDeliverables.length; x++) {
            		let found = false;
	            	for(let y = 0; y < deliverables.allDeliverables.data.length; y++) {
	            		if (deliverables.allDeliverables.data[y].id == state.mergeDeliverables[x].id) {
	            			found = true;
	            		}
	            	}
	            	if (!found) {
	            		deliverables.allDeliverables.data.push(state.mergeDeliverables[x]);
	            	}
    			}
            }

            state.mergeDeliverables = [];

            state.deliverables = deliverables.allDeliverables.data;
            state.allDeliverablesPaginationData = deliverables.allDeliverables;

            sortTasks({sortKey: false, deliverables: state.deliverables, sortFields: true, namespace:state._namespace})
        }

        if (typeof deliverables.userDeliverables != "undefined") {
            if (typeof deliverables.userDeliverables.data != "undefined") {
                state.userDeliverablesPaginationData =
                    deliverables.userDeliverables;
                deliverables.userDeliverables.data.forEach(deliverable => {

                    let dueDate = false,
                        userId = "";
                    let taskDueDate = false;
            		let isThisWeek = false;
            		let isNextWeek = false;
            		let isToday = false;
            		let isOverdue = false;
            		let isNoDeadline = false;
            		let isComplete = false;
            		let isUpcoming = false;
            		let matchingUsers = false;
            		let taskMatchingUsers = false;
            		let isProjectOnHold = false;

                    if (
                        deliverable &&
                        deliverable.project_id &&
                        deliverable.project_id.project_status
                    ) {
                        if (
                            typeof deliverables.userDeliverables != "undefined" &&
                            deliverable.project_id.project_status.slug == "on-hold"
                        ) {
                            isProjectOnHold = true;
                        }
                    }

                    deliverable.tasks.forEach(task => {
                    	taskDueDate = false;
                    	taskMatchingUsers = false;
                    	userId = "";
                        task.taskFields.forEach((taskField, index) => {
                            if (taskField.field_id == 14) {
                                userId = taskField.value;
                                return;
                            }
                            if (taskField.field_id == 12) {
                                dueDate = taskField.value;
                                taskDueDate = taskField.value;
                            }

                            if (taskField.field_id == 4) {
                                if (typeof taskField.value == "object") {
                                    if (taskField.value.name == "Completed" || taskField.value.name == "Cancelled") {
                                        dueDate = false;
                    					taskDueDate = false;
                                    }
                                } else {
                                    if (JSON.parse(taskField.value).name == "Completed" || JSON.parse(taskField.value).name == "Cancelled") {
                                        dueDate = false;
                    					taskDueDate = false;
                                    }
                                }
                            }
                        });


                        if (userId == localStorage.getItem("userId")) {
                        	taskMatchingUsers = true;
                        	matchingUsers = true;
                        } else {
                        	// also match if they are filtering by user...
	                        if (state.myWeekDeliverableFilter) {
	                        	if (state.myWeekDeliverableFilter.coordinator) {
	                        		let x = 0;
	                        		for (x = 0; x < state.myWeekDeliverableFilter.coordinator.length; x++) {
	                        			if (state.myWeekDeliverableFilter.coordinator[x].id == userId) {
	                        				taskMatchingUsers = true;
	                        				matchingUsers = true;
	                        			}
	                        		}
	                        	}
	                        }
                        }

                        if (taskDueDate) {
                        	if (taskMatchingUsers) {
	                        	let compareDate = Vue.moment(taskDueDate).format(
	                        	    "YYYY-MM-DD"
	                        	);
	                        	if (now > compareDate) {
	                        	    if (deliverable.is_complete) {
	                        	        isComplete = true;
	                        	    } else {
	                        	        isOverdue = true;
	                        	    }
	                        	} else if (now == compareDate) {
	                        	    isToday = true;
	                        	} else if (
	                        	    compareDate >= thisWeekStart &&
	                        	    compareDate <= thisWeekEnd
	                        	) {
	                        	    isThisWeek = true;
	                        	} else if (
	                        	    compareDate >= nextWeekStart &&
	                        	    compareDate <= nextWeekEnd
	                        	) {
	                        	    isNextWeek = true;
	                        	} else {
	                        	    isUpcoming = true;
	                        	}
	                        }
                        } else {
                        	isNoDeadline = true;
                        }
                    });

                    if (isProjectOnHold) {
                        projectOnHold.push(deliverable);
                	} else if (isOverdue) {
                        overdue.push(deliverable);
                    } else if (isToday) {
                        today.push(deliverable);
                    } else if (isThisWeek) {
                        thisWeek.push(deliverable);
                    } else if (isNextWeek) {
                        nextWeek.push(deliverable);
                    } else if (isUpcoming) {
                        upcoming.push(deliverable);
                    } else if (isNoDeadline) {
                        noDeadline.push(deliverable);
                    } else if (isComplete) {
                        completed.push(deliverable);
                	}
                });
            } else {
            deliverables.userDeliverables.forEach(deliverable => {
                     let dueDate = false,
                         userId = "";
                     let taskDueDate = false;
             		let isThisWeek = false;
             		let isNextWeek = false;
             		let isToday = false;
             		let isOverdue = false;
             		let isNoDeadline = false;
             		let isComplete = false;
             		let isUpcoming = false;
             		let matchingUsers = false;
             		let taskMatchingUsers = false;

                     deliverable.tasks.forEach(task => {
                     	taskDueDate = false;
                     	taskMatchingUsers = false;
                     	userId = "";
                         task.taskFields.forEach((taskField, index) => {
                             if (taskField.field_id == 14) {
                                 userId = taskField.value;
                                 return;
                             }
                             if (taskField.field_id == 12) {
                                 dueDate = taskField.value;
                                 taskDueDate = taskField.value;
                             }
                             if (taskField.field_id == 4) {
                                 if (typeof taskField.value == "object") {
                                     if (taskField.value.name == "Completed" || taskField.value.name == "Cancelled") {
                                         dueDate = false;
                     					taskDueDate = false;
                                     }
                                 } else {
                                     if (JSON.parse(taskField.value).name == "Completed" || JSON.parse(taskField.value).name == "Cancelled") {
                                         dueDate = false;
                     					taskDueDate = false;
                                     }
                                 }
                             }
                         });


                         if (userId == localStorage.getItem("userId")) {
                         	taskMatchingUsers = true;
                         	matchingUsers = true;
                        } else {
                        	// also match if they are filtering by user...
	                        if (state.myWeekDeliverableFilter) {
	                        	if (state.myWeekDeliverableFilter.coordinator) {
	                        		let x = 0;
	                        		for (x = 0; x < state.myWeekDeliverableFilter.coordinator.length; x++) {
	                        			if (state.myWeekDeliverableFilter.coordinator[x].id == userId) {
	                        				taskMatchingUsers = true;
	                        				matchingUsers = true;
	                        			}
	                        		}
	                        	}
	                        }
                        }
                         if (taskDueDate) {
                         	if (taskMatchingUsers) {
 	                        	let compareDate = Vue.moment(taskDueDate).format(
 	                        	    "YYYY-MM-DD"
 	                        	);
 	                        	if (now > compareDate) {
 	                        	    if (deliverable.is_complete) {
 	                        	        isComplete = true;
 	                        	    } else {
 	                        	        isOverdue = true;
 	                        	    }
 	                        	} else if (now == compareDate) {
 	                        	    isToday = true;
 	                        	} else if (
 	                        	    compareDate >= thisWeekStart &&
 	                        	    compareDate <= thisWeekEnd
 	                        	) {
 	                        	    isThisWeek = true;
 	                        	} else if (
 	                        	    compareDate >= nextWeekStart &&
 	                        	    compareDate <= nextWeekEnd
 	                        	) {
 	                        	    isNextWeek = true;
 	                        	} else {
 	                        	    isUpcoming = true;
 	                        	}
 	                        }
                         } else {
                         	isNoDeadline = true;
                         }
                         });
                 	if (isOverdue) {
                         overdue.push(deliverable);
                     } else if (isToday) {
                         today.push(deliverable);
                     } else if (isThisWeek) {
                         thisWeek.push(deliverable);
                     } else if (isNextWeek) {
                         nextWeek.push(deliverable);
                     } else if (isUpcoming) {
                         upcoming.push(deliverable);
                     } else if (isNoDeadline) {
                         noDeadline.push(deliverable);
                     } else if (isComplete) {
                         completed.push(deliverable);
                 	}

                 });

            }
            state.unGroupedUserDeliverables = deliverables.userDeliverables;
            state.userDeliverables = {
                overdue,
                today,
                thisWeek,
                nextWeek,
                upcoming,
                noDeadline,
                projectOnHold,
                // completed
            };
            sortTasks({sortKey: false, deliverables: state.userDeliverables, sortFields: true, namespace:state._namespace})
        }
    },
    setCalendarDeliverables: (state, payload) => {
        state.calendarDeliverables = payload.allDeliverables.deliverables.data;
    },
    setYearCalendarDeliverables: (state, payload) => {
        state.yearCalendarDeliverables = payload.allDeliverables.deliverables.data;
    },
    setCalendarDeliverable: (state, deliverable) => {
        // loop through all deliverables and update the deliverable.
        let x = 0;
        // deliverable = sortTasks({sortKey: false, deliverables: [deliverable], return: true, sortFields: true, namespace:state._namespace})[0]
        if (
            typeof state.calendarDeliverables != "undefined"
        ) {
            for (x = 0; x < Object.keys(state.calendarDeliverables).length; x++) {
                if (typeof state.calendarDeliverables[x] != 'undefined' && state.calendarDeliverables[x].id == deliverable.deliverableId) {
                    Vue.set(state.calendarDeliverables, x, deliverable);
                }
            }
        }
    },
    updateTaskFieldOrder: (state, payload) => {
    	if (typeof payload.namespace == "undefined") {
    		payload.namespace = state._namespace;
    	}
        sortTaskFields(payload)
    },
    updateTasksDisplayOrderValue: (state, payload) => {
        if(typeof state.deliverables != 'undefined') {
            for (let x = 0; x < Object.keys(state.deliverables).length; x++) {
                if (typeof state.deliverables[x] != 'undefined' && state.deliverables[x].id == payload.deliverableId) {
                    for(let y = 0; y < state.deliverables[x].tasks.length; y++) {
                        let tempTask = payload.taskData.filter((el) => {
                            return el.id == state.deliverables[x].tasks[y].id
                        })
                        state.deliverables[x].tasks[y].display_order = tempTask[0].display_order
                    }
                }
            }
        }

        if (typeof state.unGroupedUserDeliverables.data != "undefined" && state.unGroupedUserDeliverables.data.length) {
            for (let x = 0; x < Object.keys(state.unGroupedUserDeliverables.data).length; x++) {
                if (state.unGroupedUserDeliverables.data[x].id == payload.deliverableId) {
                    for(let y = 0; y < state.unGroupedUserDeliverables.data[x].tasks.length; y++) {
                        let tempTask = payload.taskData.filter((el) => {
                            return el.id == state.unGroupedUserDeliverables.data[x].tasks[y].id
                        })
                        state.unGroupedUserDeliverables.data[x].tasks[y].display_order = tempTask[0].display_order
                    }
                }
            }
        }

        if (typeof state.deliverableDetail != "undefined") {
            if (state.deliverableDetail.id == payload.deliverableId) {
                for(let y = 0; y < state.deliverableDetail.tasks.length; y++) {
                    let tempTask = payload.taskData.filter((el) => {
                        return el.id == state.deliverableDetail.tasks[y].id
                    })
                    state.deliverableDetail.tasks[y].display_order = tempTask[0].display_order
                }
            }
        }
    },
    updateReorderedTasks: (state, payload) => {
        if(typeof state.deliverables != 'undefined') {
            for (let x = 0; x < Object.keys(state.deliverables).length; x++) {
                if (typeof state.deliverables[x] != 'undefined' && state.deliverables[x].id == payload.deliverableId) {
                    state.deliverables[x].tasks = payload.taskData
                }
            }
        }

        if(typeof state.projectDeliverables != 'undefined') {
            for (let x = 0; x < Object.keys(state.projectDeliverables).length; x++) {
                if (typeof state.projectDeliverables[x] != 'undefined' && state.projectDeliverables[x].id == payload.deliverableId) {
                    state.projectDeliverables[x].tasks = payload.taskData
                }
            }
        }

        if (typeof state.unGroupedUserDeliverables.data != "undefined" && state.unGroupedUserDeliverables.data.length) {
            for (let x = 0; x < Object.keys(state.unGroupedUserDeliverables.data).length; x++) {
                if (state.unGroupedUserDeliverables.data[x].id == payload.deliverableId) {
                    state.unGroupedUserDeliverables.data[x].tasks = payload.taskData
                }
            }
        }

        if (typeof state.deliverableDetail != "undefined") {
            if (state.deliverableDetail.id == payload.deliverableId) {
                state.deliverableDetail.tasks = payload.taskData
            }
        }
    },
    updateFieldLabelText: (state, labelData) => {
        if(typeof state.deliverables != 'undefined') {
            for (let x = 0; x < Object.keys(state.deliverables).length; x++) {
                if (typeof state.deliverables[x] != 'undefined') {
                    for(let j = 0; j < Object.keys(state.deliverables[x].tasks).length; j++) {
                        if (typeof state.deliverables[x].tasks[j] != 'undefined') {
                            for(let k = 0; k < Object.keys(state.deliverables[x].tasks[j].taskFields).length; k++) {
                                if (typeof state.deliverables[x].tasks[j].taskFields[k] != 'undefined' && state.deliverables[x].tasks[j].taskFields[k].label_id === labelData.labelId) {
                                    state.deliverables[x].tasks[j].taskFields[k].label = labelData.labelText
                                }
                            }
                        }
                    }
                }
            }
        }

        if(typeof state.unGroupedUserDeliverables.data != 'undefined') {
            for (let x = 0; x < Object.keys(state.unGroupedUserDeliverables.data).length; x++) {
                if (typeof state.unGroupedUserDeliverables.data[x] != 'undefined') {
                    for(let j = 0; j < Object.keys(state.unGroupedUserDeliverables.data[x].tasks).length; j++) {
                        if (typeof state.unGroupedUserDeliverables.data[x].tasks[j] != 'undefined') {
                            for(let k = 0; k < Object.keys(state.unGroupedUserDeliverables.data[x].tasks[j].taskFields).length; k++) {
                                if (typeof state.unGroupedUserDeliverables.data[x].tasks[j].taskFields[k] != 'undefined' && state.unGroupedUserDeliverables.data[x].tasks[j].taskFields[k].label_id === labelData.labelId) {
                                    state.unGroupedUserDeliverables.data[x].tasks[j].taskFields[k].label = labelData.labelText
                                }
                            }
                        }
                    }
                }
            }
        }
    },
    removeFieldFromAllTasks(state, labelId) {
        if(typeof state.deliverables != 'undefined') {
            for (let x = 0; x < Object.keys(state.deliverables).length; x++) {
                if (typeof state.deliverables[x] != 'undefined') {
                    for(let j = 0; j < Object.keys(state.deliverables[x].tasks).length; j++) {
                        if (typeof state.deliverables[x].tasks[j] != 'undefined') {
                            for(let k = 0; k < Object.keys(state.deliverables[x].tasks[j].taskFields).length; k++) {
                                if (typeof state.deliverables[x].tasks[j].taskFields[k] != 'undefined' && state.deliverables[x].tasks[j].taskFields[k].label_id === labelId) {
                                    state.deliverables[x].tasks[j].taskFields.splice(k, 1)
                                }
                            }
                        }
                    }
                }
            }
        }

        if(typeof state.unGroupedUserDeliverables.data != 'undefined') {
            for (let x = 0; x < Object.keys(state.unGroupedUserDeliverables.data).length; x++) {
                if (typeof state.unGroupedUserDeliverables.data[x] != 'undefined') {
                    for(let j = 0; j < Object.keys(state.unGroupedUserDeliverables.data[x].tasks).length; j++) {
                        if (typeof state.unGroupedUserDeliverables.data[x].tasks[j] != 'undefined') {
                            for(let k = 0; k < Object.keys(state.unGroupedUserDeliverables.data[x].tasks[j].taskFields).length; k++) {
                                if (typeof state.unGroupedUserDeliverables.data[x].tasks[j].taskFields[k] != 'undefined' && state.unGroupedUserDeliverables.data[x].tasks[j].taskFields[k].label_id === labelId) {
                                    state.unGroupedUserDeliverables.data[x].tasks[j].taskFields.splice(k, 1)
                                }
                            }
                        }
                    }
                }
            }
        }
    },
    updateTaskData: (state, {deliverableData, deliverableId}) => {
        if(typeof state.deliverables != 'undefined') {
            for (let x = 0; x < Object.keys(state.deliverables).length; x++) {
                if (typeof state.deliverables[x] != 'undefined' && state.deliverables[x].id == deliverableId) {
                    state.deliverables[x].tasks = deliverableData.tasks
                }
            }
        }

        if (typeof state.unGroupedUserDeliverables.data != "undefined" && state.unGroupedUserDeliverables.data.length) {
            for (let x = 0; x < Object.keys(state.unGroupedUserDeliverables.data).length; x++) {
                if (state.unGroupedUserDeliverables.data[x].id == deliverableId) {
                    state.unGroupedUserDeliverables.data[x].tasks = deliverableData.tasks
                }
            }
        }

        if (typeof state.deliverableDetail != "undefined") {
            if (state.deliverableDetail.id == deliverableId) {
                state.deliverableDetail.tasks = deliverableData.tasks
            }
        }
    },
    setGroupedUserDeliverablesOnly: (state, deliverable) => {
        state.userDeliverables = deliverable;
    },
    setDeliverablesOnly: (state, deliverable) => {
        state.deliverables = deliverable;
    },
       setDeliverablePublishDate: (state, payload) => {
        //{id:payload.id, publish_date:value});
        // loop through all deliverables and update the deliverable publish date based on what was sent.
        let x = 0;
        let xx = 0;
        if (
            typeof state.deliverables != "undefined"
        ) {
            for (x = 0; x < Object.keys(state.deliverables).length; x++) {
                if (typeof state.deliverables[x] != 'undefined') {

                    if (state.deliverables[x].id == payload.id) {
                        Vue.set(state.deliverables[x], "publish_date", payload.value);
                    }
                    if (typeof state.deliverableDetail.social_series_deliverables != "undefined") {
                         for (xx = 0; xx < Object.keys(state.deliverableDetail.social_series_deliverables).length; xx++) {
                            if (state.deliverableDetail.social_series_deliverables[xx].id == payload.id) {
                                Vue.set(state.deliverableDetail.social_series_deliverables[xx], "publish_date", payload.value);
                            }
                         }
                    }
                }


            }
        }
    },
    setDeliverable: (state, deliverable) => {
        // loop through all deliverables and update the deliverable.
        let x = 0;
        deliverable = sortTasks({sortKey: false, deliverables: [deliverable], return: true, sortFields: true, namespace:state._namespace})[0]
        if (
            typeof state.deliverables != "undefined"
	        ) {
	            for (x = 0; x < Object.keys(state.deliverables).length; x++) {
	                if (typeof state.deliverables[x] != 'undefined' && state.deliverables[x].id == deliverable.deliverableId) {
	                    Vue.set(state.deliverables, x, deliverable);
	                }
	            }
	        }
	        if (
	            typeof state.unGroupedUserDeliverables.data != "undefined" &&
	            state.unGroupedUserDeliverables.data.length
	        ) {

	            for (
	                x = 0;
	                x < Object.keys(state.unGroupedUserDeliverables.data).length;
	                x++
	            ) {
	                if (
	                    state.unGroupedUserDeliverables.data[x].id ==
	                    deliverable.deliverableId
	                ) {
	                    Vue.set(
	                        state.unGroupedUserDeliverables.data,
	                        x,
	                        deliverable
	                    );
                }
            }
        }
    },
    groupUserDeliverable: state => {
        if (
            typeof state.unGroupedUserDeliverables.data == "undefined" ||
            !state.unGroupedUserDeliverables.data.length
        ) {
            return;
        }
        let now = Vue.moment().format("YYYY-MM-DD"),
            thisWeekStart = Vue.moment()
                .startOf("week")
                .format("YYYY-MM-DD"),
            thisWeekEnd = Vue.moment()
                .endOf("week")
                .format("YYYY-MM-DD"),
            nextWeekStart = Vue.moment()
                .add(1, "weeks")
                .startOf("week")
                .format("YYYY-MM-DD"),
            nextWeekEnd = Vue.moment()
                .add(1, "weeks")
                .endOf("week")
                .format("YYYY-MM-DD"),
            today = [],
            thisWeek = [],
            nextWeek = [],
            upcoming = [],
            noDeadline = [],
            overdue = [],
            completed = [];

        state.unGroupedUserDeliverables.data.forEach(deliverable => {
        	let dueDate = false,
        	    userId = "";
        	let taskDueDate = false;
        	let isThisWeek = false;
        	let isNextWeek = false;
        	let isToday = false;
        	let isOverdue = false;
        	let isNoDeadline = false;
        	let isComplete = false;
        	let isUpcoming = false;
            let matchingUsers = false;
        	let taskMatchingUsers = false;

        	deliverable.tasks.forEach(task => {
        		taskDueDate = false;
        		taskMatchingUsers = false;
        		userId = "";
        	    task.taskFields.forEach((taskField, index) => {
        	        if (taskField.field_id == 14) {
        	            userId = taskField.value;
        	            return;
        	        }
        	        if (taskField.field_id == 12) {
        	            dueDate = taskField.value;
        	            taskDueDate = taskField.value;
        	        }
        	        if (taskField.field_id == 4) {
        	            if (typeof taskField.value == "object") {
        	                if (taskField.value.name == "Completed" || taskField.value.name == "Cancelled") {
        	                    dueDate = false;
        						taskDueDate = false;
        	                }
        	            } else {
        	                if (JSON.parse(taskField.value).name == "Completed" || JSON.parse(taskField.value).name == "Cancelled") {
        	                    dueDate = false;
        						taskDueDate = false;
        	                }
        	            }
        	        }
        	    });


        	    if (userId == localStorage.getItem("userId")) {
        	    	taskMatchingUsers = true;
        	    	matchingUsers = true;
	            } else {
	            	// also match if they are filtering by user...
	                if (state.myWeekDeliverableFilter) {
	                	if (state.myWeekDeliverableFilter.coordinator) {
	                		let x = 0;
	                		for (x = 0; x < state.myWeekDeliverableFilter.coordinator.length; x++) {
	                			if (state.myWeekDeliverableFilter.coordinator[x].id == userId) {
	                				taskMatchingUsers = true;
	                				matchingUsers = true;
	                			}
	                		}
	                	}
	                }
	            }


        	    if (taskDueDate) {
        	    	if (taskMatchingUsers) {
        	        	let compareDate = Vue.moment(taskDueDate).format(
        	        	    "YYYY-MM-DD"
        	        	);
        	        	if (now > compareDate) {
        	        	    if (deliverable.is_complete) {
        	        	        isComplete = true;
        	        	    } else {
        	        	        isOverdue = true;
        	        	    }
        	        	} else if (now == compareDate) {
        	        	    isToday = true;
        	        	} else if (
        	        	    compareDate >= thisWeekStart &&
        	        	    compareDate <= thisWeekEnd
        	        	) {
        	        	    isThisWeek = true;
        	        	} else if (
        	        	    compareDate >= nextWeekStart &&
        	        	    compareDate <= nextWeekEnd
        	        	) {
        	        	    isNextWeek = true;
        	        	} else {
        	        	    isUpcoming = true;
        	        	}
        	        }
        	    } else {
        	    	isNoDeadline = true;
        	    }
        	    });

        	if (isOverdue) {
        	    overdue.push(deliverable);
        	} else if (isToday) {
        	    today.push(deliverable);
        	} else if (isThisWeek) {
        	    thisWeek.push(deliverable);
        	} else if (isNextWeek) {
        	    nextWeek.push(deliverable);
        	} else if (isUpcoming) {
        	    upcoming.push(deliverable);
        	} else if (isNoDeadline) {
        	    noDeadline.push(deliverable);
        	} else if (isComplete) {
        	    completed.push(deliverable);
        	}

        });
        state.userDeliverables = {
            overdue,
            today,
            thisWeek,
            nextWeek,
            upcoming,
            noDeadline
            // completed
        };
    },
    setDeliverableContacts: (state, data) => {
        let x = 0;
        for (x = 0; x < state.deliverables.length; x++) {
            if (state.deliverables[x].id == data.deliverableId) {
                Vue.set(state.deliverables[x].contacts, data.contacts);
            }
        }
        if (
            typeof state.unGroupedUserDeliverables.data != "undefined" &&
            state.unGroupedUserDeliverables.data.length
        ) {
            for (x = 0; x < state.unGroupedUserDeliverables.length; x++) {
                if (
                    state.unGroupedUserDeliverables[x].id == data.deliverableId
                ) {
                    Vue.set(
                        state.unGroupedUserDeliverables[x].contacts,
                        data.contacts
                    );
                }
            }
        }
    },

	setComponentState:(state, component) => {
    	let x = 0;
    	let found = false;

    	if (typeof state.componentState.length == "undefined" || state.componentState == null) {
    		state.componentState = array();
    	}
    	for (x = 0; x < state.componentState.length; x++) {
    	    if (state.componentState[x].slug == component.slug) {
    	    	found = true;
    	    }
    	}
    	if (!found) {
    		if (typeof component.slug != "undefined") {
    			state.componentState.push(component);
    			return;
    		} else {
    			// bad component.
    			return;
    		}
    	}
    	for (x = 0; x < state.componentState.length; x++) {
    	    if (state.componentState[x].slug == component.slug) {
	          Vue.set(state.componentState, x, component);
    	    }
    	}
    	localStorage.setItem('componentState', JSON.stringify(state.componentState));
	},


    setActiveTabIndex:(state, tab) => {
    	let x = 0;
    	let found = false;

    	if (typeof state.activeTab.length == "undefined" || state.activeTab == null) {
    		state.activeTab = array();
    	}
    	for (x = 0; x < state.activeTab.length; x++) {
    	    if (state.activeTab[x].slug == tab.slug) {
    	    	found = true;
    	    }
    	}
    	if (!found) {
    		state.activeTab.push({slug:tab.slug, index:tab.index});
    		return;
    	}
    	for (x = 0; x < state.activeTab.length; x++) {
    	    if (state.activeTab[x].slug == tab.slug) {
	          Vue.set(state.activeTab, x, tab);
    	    }
    	}
    	localStorage.setItem('activeTab', JSON.stringify(state.activeTab));

    },

    setTask: (state, task) => {
        let x = 0;
        let xx = 0;
        for (x = 0; x < state.deliverables.length; x++) {
            if (state.deliverables[x].id == task.deliverableId) {
                for (xx = 0; xx < state.deliverables[x].tasks.length; xx++) {
                    if (state.deliverables[x].tasks[xx].id == task.task.id) {
                        Vue.set(state.deliverables[x].tasks, xx, task.task);
                    }
                }
            }
        }
    },

    setCustomFields: (state, customFields) =>
        (state.customFields = customFields),
    setContacts: (state, contacts) => (state.contacts = contacts),
    setActiveContacts: (state, contacts) => (state.activeContacts = contacts),
    setProjectDetail: (state, projectDetail) => {
        state.projectDetail = typeof projectDetail.data == "undefined" ? projectDetail : projectDetail.data
    },
    resetProjectDetail: (state) => {
        state.projectDetail = []
    },

    setDeliverableDetailPublishDetails: (state, deliverableDetail) => {
			// updating a normal deliverable (not master series deliverable)
			if (state.deliverableDetail.id == deliverableDetail.id) {
				// set the publish status, etc.
				state.deliverableDetail.publish_date = deliverableDetail.publish_date;
				state.deliverableDetail.publish_status = deliverableDetail.publish_status;
				state.deliverableDetail.publish_status_id = deliverableDetail.publish_status_id;
				state.deliverableDetail.manually_updated_at = deliverableDetail.manually_updated_at;
				if (typeof deliverableDetail.actions != "undefined") {
					state.deliverableDetail.actions = deliverableDetail.actions;
				}

			}

			if (typeof state.deliverableDetail.social_series_deliverables != "undefined") {
				// loop through the social series deliverables to see if this one is in there.
				for(let x = 0; x < state.deliverableDetail.social_series_deliverables.length; x++) {
					if (state.deliverableDetail.social_series_deliverables[x].id == deliverableDetail.id) {
						// set the publish status, etc.
						state.deliverableDetail.social_series_deliverables[x].publish_date = deliverableDetail.publish_date;
						state.deliverableDetail.social_series_deliverables[x].publish_status = deliverableDetail.publish_status;
						state.deliverableDetail.social_series_deliverables[x].publish_status_id = deliverableDetail.publish_status_id;
						state.deliverableDetail.social_series_deliverables[x].manually_updated_at = deliverableDetail.manually_updated_at;

						if (typeof deliverableDetail.actions != "undefined") {
							state.deliverableDetail.social_series_deliverables[x].actions = deliverableDetail.actions;
						}

					}
				}
			}
	},

	setShowPostContentInListing:(state, data) => {
		state.showPostContentLangInListing = data.lang_id;
		state.showPostContentNetworkInListing = data.social_network_type_id;
	},

    setDeliverableDetailSocialPostNetworkShareUrl: (state, data) => {
    	/*
    	{
    		payload:REQUEST data sent to server
    		resopnse: response from
    	}
    	*/
		if (state.deliverables) {
			for(let z = 0; z < state.deliverables.length; z++) {
				if (state.deliverables[z].id == state.deliverableDetail.id) {
					if (state.deliverables[z].social_posts) {
						for(let zz = 0; zz < state.deliverables[z].social_posts.length; zz++) {
							if (state.deliverables[z].social_posts[zz].id == data.payload.id) {

								// now find the share url.
								for(let zzz = 0; zzz < state.deliverables[z].social_posts[zz].share_urls.length; zzz++) {
										if (state.deliverables[z].social_posts[zz].share_urls[zzz].id == data.payload.social_network_id) {
		        							Vue.set(state.deliverables[z].social_posts[zz].share_urls[zzz], url, data.payload.value);
		        							if (data.payload.only_share_url) {
		        								Vue.set(state.deliverables[z].social_posts[zz].share_urls[zzz], only_share_url, 1);
		        							} else  {
		        								Vue.set(state.deliverables[z].social_posts[zz].share_urls[zzz], only_share_url, 0);

		        							}
										}

								}
							}
						}
					}
		        }
			}
		}

    },

        setDeliverableDetailOnly: (state, deliverableDetail) => {
            (state.deliverableDetail =
                typeof deliverableDetail.data == "undefined"
                    ? deliverableDetail
                    : deliverableDetail.data)


            sortTasks({sortKey: false, deliverables: state.deliverableDetail, sortFields: true, namespace:state._namespace})
        },

        setParentDeliverableDetail: (state, deliverableDetail) => {
            (state.parentDeliverableDetail =
                typeof deliverableDetail.data == "undefined"
                    ? deliverableDetail
                    : deliverableDetail.data)

            sortTasks({sortKey: false, deliverables: state.parentDeliverableDetail, sortFields: true, namespace:state._namespace})
        },



    replaceDeliverableDetail:(state, payload)=> {
		if (state.deliverables) {
			for(let z = 0; z < state.deliverables.length; z++) {
				if (state.deliverables[z].id == payload.id) {
		        	Vue.set(state.deliverables, z, _.cloneDeep(payload.deliverableDetail));
		        }
			}
		}
    },

    resetDuplicatingDeliverable: (state, id) => {
    	state.duplicatingDeliverable.status = '';
    	state.duplicatingDeliverable.details = null;
    	state.duplicatingDeliverable.projectId = null;
    	state.duplicatingDeliverable.projectNumber = null;
    	state.duplicatingDeliverable.deliverableId = null;
    	state.duplicatingDeliverable.newDeliverableId = null;
    	state.duplicatingDeliverable.newProjectNumber = null;
    	state.duplicatingDeliverable.details = null;
    	state.duplicatingDeliverable.jobKey = null;
    	state.duplicatingDeliverable.message = null;
    	state.duplicatingDeliverable.beforeTotal = 0;
    	state.duplicatingDeliverable.progress = 0;
    	state.duplicatingDeliverable.progressLabel = null;
    	state.duplicatingDeliverable.progressDetails = null;
    },


    setDuplicatingDeliverableProgressDetails: (state, details) => {
    	state.duplicatingDeliverable.progressDetails = details;
    },

    setDuplicatingDeliverableProgress: (state, val) => {
    	state.duplicatingDeliverable.progressDetails = val;
    },

    setDuplicatingDeliverableStatus: (state, val) => {
    	state.duplicatingDeliverable.status = val;
    },

    setDuplicatingDeliverableBeforeTotal: (state, val) => {
    	state.duplicatingDeliverable.beforeTotal = val;
    },

    setDuplicatingDeliverableMessage: (state, val) => {
    	state.duplicatingDeliverable.message = val;
    },

	setDuplicatingDeliverableId: (state, id) => {
		state.duplicatingDeliverable.deliverableId = id;
		// changing the old one means that the new one is likely incorrect..
		state.duplicatingDeliverable.newDeliverableId = null;
	},

	setDuplicatingDeliverableJobkey: (state, id) => {
		state.duplicatingDeliverable.jobKey = id;
	},



	setDuplicatingDeliverableNewDeliverableId: (state, id) => {
		state.duplicatingDeliverable.newDeliverableId = id;
	},

	setDuplicatingDeliverableNewProjectNumber: (state, id) => {
		state.duplicatingDeliverable.newProjectNumber = id;
	},


    setDeliverableDetail: (state, deliverableDetail) => {
        (state.deliverableDetail =
            typeof deliverableDetail.data == "undefined"
                ? deliverableDetail
                : deliverableDetail.data)

        if (state.deliverableDetail.social_series_deliverables) {
        	// set the full data in state.deliverables;
        	if (state.deliverables) {
	        	for(let z = 0; z < state.deliverables.length; z++) {
	        		if (state.deliverables[z].id == state.deliverableDetail.id) {
	        		    let updatedDeliverable = _.cloneDeep(state.deliverableDetail);
	        		    if (state.deliverables[z].matching_posts_count !== undefined) {
	        		        updatedDeliverable.matching_posts_count = state.deliverables[z].matching_posts_count;
	        		    }
			        	Vue.set(state.deliverables, z, updatedDeliverable);
			        }
	        	}
        	}
        	// set the full data in state.projectDeliverables;
        	if (state.projectDeliverables) {
	        	for(let z = 0; z < state.projectDeliverables.length; z++) {
	        		if (state.projectDeliverables[z].id == state.deliverableDetail.id) {
	        		    let updatedDeliverable = _.cloneDeep(state.deliverableDetail);
	        		    if (state.projectDeliverables[z].matching_posts_count !== undefined) {
	        		        updatedDeliverable.matching_posts_count = state.projectDeliverables[z].matching_posts_count;
	        		    }
			        	Vue.set(state.projectDeliverables, z, updatedDeliverable);
			        }
	        	}
        	}
	    	// set the full data in state.projectDeliverables;
	    	if (state.userDeliverables) {
	        	for(let z = 0; z < state.userDeliverables.length; z++) {
	        		if (state.userDeliverables[z].id == state.deliverableDetail.id) {
	        		    let updatedDeliverable = _.cloneDeep(state.deliverableDetail);
	        		    if (state.userDeliverables[z].matching_posts_count !== undefined) {
	        		        updatedDeliverable.matching_posts_count = state.userDeliverables[z].matching_posts_count;
	        		    }
			        	Vue.set(state.userDeliverables, z, updatedDeliverable);
			        }
	        	}
	    	}
        	for(let x = 0; x < state.deliverableDetail.social_series_deliverables.length; x++) {
	        	for(let y = 0; y < state.deliverables.length; y++) {
	        		if (state.deliverables[y].id == state.deliverableDetail.social_series_deliverables[x].id && state.deliverables[y].id != state.deliverableDetail.id) {
	        			let my_deliverable = _.cloneDeep(state.deliverables[y]);
	        			my_deliverable.publish_date = state.deliverableDetail.social_series_deliverables[x].publish_date;
	        			my_deliverable.is_master = state.deliverableDetail.social_series_deliverables[x].is_master;
	        			my_deliverable.manually_updated_at = state.deliverableDetail.social_series_deliverables[x].manually_updated_at;
	        			if (state.deliverableDetail.is_master) {
		        			if (my_deliverable.manually_updated_at == null && my_deliverable.published_at == null ) {
		        				my_deliverable.title = state.deliverableDetail.title;
		        				my_deliverable.social_networks = state.deliverableDetail.social_networks;
		        				my_deliverable.description = state.deliverableDetail.description;
		        				my_deliverable.languages = state.deliverableDetail.languages;
		        				my_deliverable.status = state.deliverableDetail.status;
		        				my_deliverable.publish_status = state.deliverableDetail.publish_status;
		        				my_deliverable.publish_status_id = state.deliverableDetail.publish_status_id;
		        				my_deliverable.lead_coordinator = state.deliverableDetail.lead_coordinator;
		        				my_deliverable.is_complete = state.deliverableDetail.is_complete;
		        				// set the caption, image, platforms, etc.
		        				my_deliverable.social_networks = state.deliverableDetail.social_networks;
		        				if (state.deliverableDetail.social_posts) {
		        					if (state.deliverableDetail.social_posts[0]) {
				        				if (my_deliverable.social_posts)  {
				        					for(let x2 = 0; x2 < state.deliverableDetail.social_posts.length; x2++) {
				        						for(let y2 = 0; y2 < my_deliverable.social_posts.length; y2++) {
				        							if (state.deliverableDetail.social_posts[x2].lang_id == my_deliverable.social_posts[y2].lang_id) {
						        						my_deliverable.social_posts[y2].caption = state.deliverableDetail.social_posts[x2].caption;
						        						my_deliverable.social_posts[y2].url = state.deliverableDetail.social_posts[x2].url;
						        						my_deliverable.social_posts[y2].short_url = state.deliverableDetail.social_posts[x2].short_url;
						        						my_deliverable.social_posts[y2].social_networks = _.cloneDeep(state.deliverableDetail.social_posts[x2].social_networks);
						        						my_deliverable.social_posts[y2].share_urls = _.cloneDeep(state.deliverableDetail.social_posts[x2].share_urls);
														my_deliverable.social_posts[y2].files = _.cloneDeep(state.deliverableDetail.social_posts[x2].files);
						        						for (let yy = 0; yy < my_deliverable.social_posts[y2].files.length; yy++) {
						        							my_deliverable.social_posts[y2].files[yy].social_post_id = my_deliverable.social_posts[y2].id;
						        						}
				        							}
                                                }
				        					}
										}
		        					}
		        				} 
		        			}
		        		}
		        		Vue.set(state.deliverables, y, my_deliverable);
	        		}
	        	}
	        }
        }

        sortTasks({sortKey: false, deliverables: state.deliverableDetail, sortFields: true, namespace:state._namespace})
    },

    setSocialSeriesDetail: (state, socialSeriesDetail) => (state.socialSeriesDetail = socialSeriesDetail),
    setDepartments: (state, departments) => (state.departments = departments),
    setAllSocialSeries: (state, socialSeries) => {
    	state.socialSeries = socialSeries;
     },
    setAllSocialCampaigns: (state, socialCampaigns) => (state.socialCampaigns = socialCampaigns),
    setListSocialSeries: (state, socialSeries) => (state.listSocialSeries = socialSeries),
    setListSocialCampaigns: (state, socialCampaigns) => {
	    state.listSocialCampaigns = socialCampaigns;
    },
    setSocialSeries: (state, socialSeries) => {
        let x = 0;
        let y = 0;
        if (
            typeof state.socialSeries != "undefined"
        ) {
            for (y = 0; y < Object.keys(socialSeries).length; y++) {
	            for (x = 0; x < Object.keys(state.socialSeries).length; x++) {
	                if (typeof state.socialSeries[x] != 'undefined' && state.socialSeries[x].id == socialSeries[y].id) {
	                    Vue.set(state.socialSeries, x, socialSeries[y]);
	                }
	            }
	        }
        }
        if (
            typeof state.listSocialSeries != "undefined"
        ) {
            for (y = 0; y < Object.keys(socialSeries).length; y++) {
	            for (x = 0; x < Object.keys(state.listSocialSeries).length; x++) {
	                if (typeof state.listSocialSeries[x] != 'undefined' && state.listSocialSeries[x].id == socialSeries[y].id) {
	                    Vue.set(state.listSocialSeries, x, socialSeries[y]);
	                }
	            }
	        }
        }
    },


    setOpenWithFocus: (state, control) => {
    	state.openWithFocus = control;
    },

    setSocialCampaign: (state, socialCampaign) => {

    	let resortCampaigns = false;
        // loop through all deliverables and update the deliverable.
        let x = 0;
        // deliverable = sortTasks({sortKey: false, deliverables: [deliverable], return: true, sortFields: true}, namespace:state._namespace)[0]
        if (
            typeof state.socialCampaigns != "undefined"
        ) {
            for (x = 0; x < Object.keys(state.socialCampaigns).length; x++) {
                if (typeof state.socialCampaigns[x] != 'undefined' && state.socialCampaigns[x].id == socialCampaign.id) {
                    Vue.set(state.socialCampaigns, x, socialCampaign);
                }
            }
        }

        if (
            typeof state.listSocialCampaigns != "undefined"
        ) {
            for (x = 0; x < Object.keys(state.listSocialCampaigns).length; x++) {
                if (typeof state.listSocialCampaigns[x] != 'undefined' && state.listSocialCampaigns[x].id == socialCampaign.id) {
                	if (state.listSocialCampaigns[x].title != socialCampaign.title) {
                		resortCampaigns = true;
                	}

                    Vue.set(state.listSocialCampaigns, x, socialCampaign);

                }
            }

			if (resortCampaigns) {
				state.listSocialCampaigns = _.sortBy(state.listSocialCampaigns, "title");
			}
        }

    },
    setActiveDepartments: (state, departments) => (state.activeDepartments = departments),
    setTags: (state, tags) => {
        state.tags = tags.allTags;
        state.trendingTags = tags.trendingTags;
    },
    setAllRoles: (state, roles) => {
        state.allRoles = roles;
    },
    setAllAbilities: (state, abilities) => {
        state.allAbilities = abilities;
    },
    setSubjects: (state, subjects) => (state.subjects = subjects),
    toggleEditDeliverableModal: (state, type) =>
        (state.editDeliverableModal =
            typeof type != "undefined" && type == "hide"
                ? false
                : !state.editDeliverableModal),
    toggleEditProjectModal: (state, type) =>
        (state.editProjectModal =
            typeof type != "undefined" && type == "hide"
                ? false
                : !state.editProjectModal),
    toggleAddContactModal: (state, type) =>
        (state.addContactModal =
            typeof type != "undefined" && type == "hide"
                ? false
                : !state.addContactModal),
    toggleEditContactModal: (state, type) =>
        (state.editContactModal =
            typeof type != "undefined" && type == "hide"
                ? false
                : !state.editContactModal),
    toggleEditSocialModal: (state, type) =>
                (state.editSocialModal =
                    typeof type != "undefined" && type == "hide"
                        ? false
                        : !state.editSocialModal),
    setUsers: (state, users) => (state.users = users),
    setActiveUsers: (state, users) => (state.activeUsers = users),
    setGeoData: (state, geoData) => {
        state.countries = geoData.countries;
        state.regions = geoData.regions;
        state.countryStates = geoData.states;
    },
    removeMediaCallFromList: (state, deliverableId) => {
        if(typeof state.allDeliverablesPaginationData.data != 'undefined') {
            let deliverableArr = Object.values(state.allDeliverablesPaginationData.data);
            deliverableArr.splice(-1, 1);
            let filteredArr = deliverableArr.filter(function(value, index, arr){
                return value.id != deliverableId;
            });
            state.allDeliverablesPaginationData.data = {...filteredArr};
            state.allDeliverablesPaginationData.total -= 1;
            state.deliverables = state.allDeliverablesPaginationData.data;
        }
    },
    setDataLoading: (state, loading) => {
        state.dataLoading = Boolean(loading);
    },
    setDeliverableCount: (state, count) => {
        state.deliverableCount = count;
    },
    setTasksToHighlight: (state, taskIdsArray) => {
        state.tasksToHighlight = taskIdsArray;
    },
    setTaskHighlighted: (state, taskId) => {
        state.taskHighlighted = taskId;
    },
    setNewProjectCreated: (state, newProjectCreated) => {
        state.newProjectCreated = newProjectCreated;
    },
    setProjectSort: (state, value) => {
        state.projectSort = value;
    },
    setMyProjectSort: (state, value) => {
        state.myProjectSort = value;
    },
    setSystemSettings:(state, systemSettings) => {
        state.systemSettings = systemSettings;
    },
    setSocialCampaignDetail: (state, socialCampaignDetail) => {
        state.socialCampaignDetail = socialCampaignDetail;
    },
    setSocialGroupBy: (state, socialGroupBy) => {
        state.socialGroupBy = socialGroupBy;
    }
};

const getters = {
	recentProjectIds: state => state.recentProjectIds,
	newDeliverableTypes: state => state.newDeliverableTypes,
    allDeliverableTypes: state => state.allDeliverableTypes,
	showPostContentLangInListing: state => state.showPostContentLangInListing,
	showPostContentNetworkInListing: state => state.showPostContentNetworkInListing,
	openWithFocus: state => state.openWithFocus,
	refreshKey: state => state.refreshKey,
    currentContactData: state => state.currentContactData,
	currentFieldKey: state => state.currentFieldKey,
	deliverableId: state => state.deliverableId,
	newContactName: state => state.newContactName,
	currentContactTemplateType: state => state.currentContactTemplateType,
	calendarFilter: state => state.calendarFilter,
    calendarEvents: state => state.calendarEvents,
	projectCalendarFilter: state => state.projectCalendarFilter,
    projectCalendarEvents: state => state.projectCalendarEvents,
    preferenceData: state => state.preferenceData,
    loggedInUserData: state => state.loggedInUserData,
    deliverableTypes: state => state.deliverableTypes,
    preferenceDeliverableTypes: state => state.preferenceDeliverableTypes,
    auditLogFilter: state => state.auditLogFilter,
    auditLogs: state => state.auditLogs,
    auditLogTags: state => state.auditLogTags,
    auditLogContacts: state => state.auditLogContacts,
    auditLogDepartments: state => state.auditLogDepartments,
    auditLogCoordinators: state => state.auditLogCoordinators,
    auditLogDeliverables: state => state.auditLogDeliverables,
    auditLogProjects: state => state.auditLogProjects,
    activateUndo: state => state.activateUndo,
    activateUndoText: state => state.activateUndoText,
    activateUndoNoScroll: state => state.activateUndoNoScroll,
    activateMyProjectUndo: state => state.activateMyProjectUndo,
    activateMyProjectUndoId: state => state.activateMyProjectUndoId,
    breadcrumbs: state => state.breadcrumbs,
    notification: state => state.notification,
    allAlerts: state => state.allAlerts,
    globalAlertTypes: state => state.globalAlertTypes,
    userAlerts: state => state.userAlerts,
    userNotificationPreferences: state => state.userNotificationPreferences,
    userViews: state => state.userViews,
    GSRecentSearch: state => {
        let uniqueSearches = [];
        uniqueSearches['all'] = [...new Set(state.GSRecentSearch['all'])];
        uniqueSearches['tags'] = [...new Set(state.GSRecentSearch['tags'])];
        uniqueSearches['contacts'] = [...new Set(state.GSRecentSearch['contacts'])];
        uniqueSearches['coordinators'] = [...new Set(state.GSRecentSearch['coordinators'])];
        uniqueSearches['departments'] = [...new Set(state.GSRecentSearch['departments'])];
        return uniqueSearches;
    },
    currentPage: state => state.currentPage,
    allRoles: state => state.allRoles,
    allAbilities: state => state.allAbilities,
    initialContacts: state => state.initialContacts,
    initialTags: state => state.initialTags,
    initialDepartments: state => state.initialDepartments,
    outletCount: state => state.outletCount,
    defaultFilterParams: state => state.defaultFilterParams,
    deliverableFilter: state => state.deliverableFilter,
    mediaCallFilter: state => state.mediaCallFilter,
    myWeekDeliverableFilter: state => state.myWeekDeliverableFilter,
    calendarFilter: state => state.calendarFilter,
    addProjectDeliverableFilter: state => state.addProjectDeliverableFilter,
    projectDeliverableListFilter: state => state.projectDeliverableListFilter,
    globalSearchFilter: state => state.globalSearchFilter,
    projectFilter: state => state.projectFilter,
    myProjectFilter: state => state.myProjectFilter,
    reportFilter: state => state.reportFilter,
	socialMediaFilter: state => state.socialMediaFilter,
	socialMediaCalendarFilter: state => state.socialMediaCalendarFilter,
    globalSearchData: state => state.globalSearchData,
    projects: state => state.projects,
    allProjects: state => state.allProjects,
    systemSettings: state => state.systemSettings,
    userProjects: state => state.userProjects,
    userManageProjects: state => state.userManageProjects,
    userManageSocialCampaigns: state => state.userManageSocialCampaigns,
    userHasProjects: state => state.userHasProjects,
    allDeliverables: state => state.deliverables,
    mergeDeliverables: state=> state.mergeDeliverables,
    listSocialSeries: state => state.listSocialSeries,
    listSocialCampaigns: state => state.listSocialCampaigns,
    socialSeries: state => state.socialSeries,
    socialCampaigns: state => state.socialCampaigns,
    allCalendarDeliverables: state => state.calendarDeliverables,
    allYearCalendarDeliverables: state => state.yearCalendarDeliverables,
	getSocialSeriesWithSocialCampaign:(state) => (social_campaign_id) => {
	  if (state.socialSeries) {
	    return state.socialSeries.filter((entry) => {
	    	if (entry.social_campaign_id == social_campaign_id) {
	    			return true;
	    	}
	      return false;
	    })
	  }
	},

	getComponentState:(state) => (slug, default_obj) => {
		if (typeof state.componentState.length == "undefined" || state.componentState == null) {
			state.componentState = array();
		}
		  if (state.componentState) {
		    var item =  state.componentState.filter((entry) => {
		    	if (entry.slug == slug) {
		    		return true;
		    	}
		    })
		    if (item.length > 0) {
		    	return item[0];
		    }
		  }
		  if (typeof default_obj != "undefined") {
		  	return default_obj;
		  }
		  return {};
	},


	getActiveTabIndex:(state) => (tab_slug) => {
		if (typeof state.activeTab.length == "undefined" || state.activeTab == null) {
			state.activeTab = array();
		}
	  if (state.activeTab) {
	    var item =  state.activeTab.filter((entry) => {
	    	if (entry.slug == tab_slug) {
	    		return true;
	    	}
	    })
	    if (item.length > 0) {
	    	return item[0].index;
	    }
	  }
	  // return the first tab index.
	  return 0;
	},

	getSocialSeriesWithoutSocialCampaign:(state) => () => {
	  if (state.socialSeries) {
	    return state.socialSeries.filter((entry) => {
	    	if (entry.social_campaign_id == null) {
	    			return true;
	    	}
	      return false;
	    })
	  }
	},

	getDeliverableTemplateSlug:(state) => (template_id) => {
	  if (state.allDeliverableTypes) {
	    var item =  state.allDeliverableTypes.filter((entry) => {
	    	if (entry.typeId == template_id) {
	    		return true;
	    	}
	      return false;
	    })
	    if (item) {
	    	if (item.length > 0) {
	    		return item[0].slug;
	    	}
	    }
	    return false;
	  }
	  return false;
	},

	getSystemSetting:(state) => (keyToCheck) => {
		if (state.systemSettings.settings) {
	        const theSetting = state.systemSettings.settings.find(setting => setting.setting_key === keyToCheck);
	        return theSetting?.setting_value?.value || false;
    	} else {
    		return false;
    	}
    },

	getListSocialSeriesWithSocialCampaign:(state) => (social_campaign_id) => {
	  if (state.listSocialSeries) {
	    return state.listSocialSeries.filter((entry) => {
	    	if (entry.social_campaign_id == social_campaign_id) {
	    			return true;
	    	}
	      return false;
	    })
	  }
	},

	getListSocialSeriesWithoutSocialCampaign:(state) => () => {
	  if (state.listSocialSeries) {
	    return state.listSocialSeries.filter((entry) => {
	    	if (entry.social_campaign_id == null) {
	    			return true;
	    	}
	      return false;
	    })
	  }
	},



    getSocialMediaOnlyCalendar (state) {
	  if (state.deliverables) {
	    return state.deliverables.filter((deliverable) => {
	    	if (typeof deliverable.social_campaigns != "undefined") {
	    		if (typeof deliverable.social_series != "undefined") {
			    	if (deliverable.social_series.length == 0 && deliverable.social_campaigns.length == 0) 			{
			    		return true;
			    	}
			    } else {
			    	return true;
			    }
		    } else {
		    	return true;
		    }
	      return false;
	    })
	  }
	},

    getLangNameById:(state) => (id) => {
    	if (state.systemSettings) {
    		if (state.systemSettings.languages) {
    			for (let i in state.systemSettings.languages) {
    				if (state.systemSettings.languages[i].id == id) {
    					return state.systemSettings.languages[i].lang_name;
    				}
    			}
    		}
    	}
    	return null;
	},

    getPublishStatusTextBySlug:(state) => (slug) => {
    	if (state.systemSettings) {
    		if (state.systemSettings.publish_status) {
    			for (let i in state.systemSettings.publish_status) {
    				if (state.systemSettings.publish_status[i].slug == slug) {
    					return state.systemSettings.publish_status[i].name;
    				}
    			}
    		}
    	}
    	return null;
	},



    getLangCodeById:(state) => (id) => {
    	if (state.systemSettings) {
    		if (state.systemSettings.languages) {
    			for (let i in state.systemSettings.languages) {
    				if (state.systemSettings.languages[i].id == id) {
    					return state.systemSettings.languages[i].lang_code;
    				}
    			}
    		}
    	}
    	return null;
	},
    getSocialNetworkTypeById:(state) => (id) => {
    	if (state.systemSettings) {
    		if (state.systemSettings.social_networks) {
    			for (let i in state.systemSettings.social_networks) {
    				if (state.systemSettings.social_networks[i].id == id) {
    					return state.systemSettings.social_networks[i].api_type;
    				}
    			}
    		}
    	}
    	return null;
	},

	getAvailableSocialNetworks:(state) => {
	    let socialNetworkOptions = [];
	      if(state.systemSettings.social_networks && state.systemSettings.social_network_clients){
	        state.systemSettings.social_networks.forEach(function(network){
	        	state.systemSettings.social_network_clients.forEach(function(client){
	        		if (network.id == client.social_network_id && client.is_enabled == 1) {
			        // only show the entry if there are accounts for the
			          socialNetworkOptions.push(network);
		        	}
	        	});
	        })
	      }
	      return socialNetworkOptions;
	 },

    getSocialNetworkClassById:(state, getters) => (id) => {
    	if (state.systemSettings) {
    		if (state.systemSettings.social_networks) {
    			for (let i in state.systemSettings.social_networks) {
    				if (state.systemSettings.social_networks[i].id == id) {
    					return getters.getSocialNetworkClass(state.systemSettings.social_networks[i].api_type);
    				}
    			}
    		}
    	}
    	return null;
	},
	getSocialNetworkClass: (state) => (api_type) => {
		if (api_type == "twitter") {
			return "x-twitter";
		}
		return api_type;
      },


    getSocialCampaignNameById:(state) => (campaign_id) => {
    	if (state.socialCampaigns) {
    		var out = state.socialCampaigns.filter((item) => {
    			if (item.id == campaign_id) {
    				return true;
    			}
    			return false;
    		})
    		if (out) {
    			if (out.length > 0) {
    				return out[0].title;
    			}
    		}
    	}
    	return null;
	},


    getSocialSeriesNameById:(state) => (series_id) => {
    	if (state.socialSeries) {
    		var out = state.socialSeries.filter((item) => {
    			if (item.id == series_id) {
    				return true;
    			}
    			return false;
    		})
    		if (out) {
    			if (out.length > 0) {
    				return out[0].title;
    			}
    		}
    	}
    	return null;
	},





    getSocialMediaWithSocialCampaignAndSeries:(state) => (social_campaign_id, social_series_id) => {
	  if (state.deliverables) {
	    return state.deliverables.filter((deliverable) => {

	    	if (deliverable.social_campaigns.length > 0) {
	    		var foo = deliverable.social_campaigns.filter((id) => {
	    			if (id == social_campaign_id) {
	    				return true;
	    			}
	    			return false;
	    		})

	    		var foo2 = [];
	    		if (deliverable.social_series.length > 0) {
	    			var foo2 = deliverable.social_series.filter((id) => {
	    				if (id == social_series_id) {
	    					return true;
	    				}
	    				return false;
	    			})
	    			if (foo2.length > 0) {
	    				return true;
	    			}
	    			return false;
	    		}


	    		if (foo.length > 0 && foo2.length > 0) {
	    			return true;
	    		}
	    		return false;
	    	}
	      return false;
	    })
	  }
	},


    getSocialMediaWithSocialCampaign:(state) => (social_campaign_id) => {
	  if (state.deliverables) {
	    return state.deliverables.filter((deliverable) => {
	    	if (typeof deliverable.social_campaigns != "undefined") {
		    	if (deliverable.social_campaigns.length > 0) {
		    		var foo = deliverable.social_campaigns.filter((id) => {
		    			if (id == social_campaign_id) {
		    				if (deliverable.social_series.length == 0) {
		    					return true;
		    				}
		    			}
		    			return false;
		    		})
		    		if (foo.length > 0) {
		    			return true;
		    		}
		    		return false;
		    	}
		    }
	      return false;
	    })
	  }
	},

	getSocialMediaWithSocialSeries:(state) => (social_series_id) => {
	  if (state.deliverables) {
	    return state.deliverables.filter((deliverable) => {
	    	if (typeof deliverable.social_series != "undefined") {
		    	if (deliverable.social_series.length > 0) {
		    		var foo = deliverable.social_series.filter((id) => {
		    			if (id == social_series_id) {
		    				return true;
		    			}
		    			return false;
		    		})
		    		if (foo.length > 0) {
		    			return true;
		    		}
		    		return false;
		    	}
		    }
	      return false;
	    })
	  }
	},




    projectDeliverables: state => state.projectDeliverables,
    allDeliverablesPaginationData: state => state.allDeliverablesPaginationData,
    allProjectsPaginationData: state => state.allProjectsPaginationData,
    allUserProjectsPaginationData: state => state.allUserProjectsPaginationData,
    allProjectDeliverablesPaginationData: state => state.allProjectDeliverablesPaginationData,
    userDeliverablesPaginationData: state =>
        state.userDeliverablesPaginationData,
    allCustomFields: state => state.customFields,
    userDeliverables: state => state.userDeliverables,
    unGroupedUserDeliverables: state => state.unGroupedUserDeliverables,
    deliverableContactsLabel: state => deliverable_template_id => {
        let label = "Who";
        switch (deliverable_template_id) {
            case 1:
                // Media Call
                label = "Outlets";
                break;
        }
        return label;
    },
    deliverableContactLabel: state => deliverable_template_id => {
        let label = "Who";
        switch (deliverable_template_id) {
            case 1:
                // Media Call
                label = "Outlet";
                break;
        }
        return label;
    },
    deliverableContactCompanyLabel: state => deliverable_template_id => {
        let label = "Company";
        switch (deliverable_template_id) {
            case 1:
                // Media Call
                label = "Outlet";
                break;
        }
        return label;
    },
    deliverableTask: state => deliverable_template_id => {
        let label = "Company";
        switch (deliverable_template_id) {
            case 1:
                // Media Call
                label = "Outlet";
                break;
        }
        return label;
    },
    allUsers: state => {
        let users = [];
        let isArchived = '';
        if (state.users) {
            state.users.forEach(function(item, index) {
                isArchived = !item.is_enabled ? ' (archived)' : '';
                users.push({ id: item.id, name: item.name+isArchived, userRoles: item.userRoles });
            });
        }

        return users;
    },
    allActiveUsers: state => state.activeUsers,
    allCountries: state => {
        let countries = [];
        state.countries.forEach(country => {
            countries.push(country.name);
        });
        return countries;
    },
    allRegions: state => {
        let regions = [];
        state.regions.forEach(region => {
            regions.push(region.name);
        });
        return regions;
    },
    allStates: state => {
        let countryStates = [];
        state.countryStates.forEach(countryState => {
            countryStates.push(countryState.name);
        });
        return countryStates;
    },
    allCompanies: state => {
        return state.allCompanies;
    },
    allContacts: state => {
        let contacts = [];
        state.contacts.forEach(function(item, index) {
            let obj = item;
            obj.name = item.first_name + " " + item.last_name;
            obj.commaName = item.last_name + ", " + item.first_name;
            contacts.push(item);
            // contacts.push({
            //     id: item.id,
            //     name: item.first_name + " " + item.last_name
            // });
        });
        return contacts;
    },
    allActiveContacts: state => {
        let contacts = [];
        state.contacts.forEach(function(item, index) {
            let obj = item;
            obj.name = item.first_name + " " + item.last_name;
            obj.commaName = item.last_name + ", " + item.first_name;
            contacts.push(item);
            // contacts.push({
            //     id: item.id,
            //     name: item.first_name + " " + item.last_name
            // });
        });
        return contacts;
    },
    allDepartments: state => {
        let departments = [];
        state.departments.forEach(function(item, index) {
            departments.push({ id: item.id, name: item.title });
        });
        return departments;
    },
    allActiveDepartments: state => {
        let entries = [];
        state.activeDepartments.forEach(function(item, index) {
            entries.push({ id: item.id, name: item.title });
        });
        return entries;
    },
    allSocialSeries: state => {
        let entries = [];
        state.socialSeries.forEach(function(item, index) {
            entries.push({ id: item.id, name: item.title, social_campaign_id:item.social_campaign_id,engagement_manager: item.engagement_manager, project_lead: item.project_lead});
        });
        return entries;
    },

    allUserManageSocialCampaigns: state => {
        let entries = [];
        state.userManageSocialCampaigns.forEach(function(item, index) {
            entries.push({ id: item.id, name: item.title,engagement_manager: item.engagement_manager, project_lead: item.project_lead });
        });
        return entries;
    },

    allSocialCampaigns: state => {
        let entries = [];
        state.socialCampaigns.forEach(function(item, index) {
            entries.push({ id: item.id, name: item.title,engagement_manager: item.engagement_manager, project_lead: item.project_lead });
        });
        return entries;
    },
    allTags: state => {
        let tags = [];
        state.tags.forEach(function(item, index) {
            tags.push({ id: item.id, name: item.tag });
        });
        return tags;
    },
    allSubjects: state => {
        let subjects = [];
        state.subjects.forEach(function(item, index) {
            subjects.push({ id: item.id, name: item.title });
        });
        return subjects;
    },
    deliverableDetail: state => state.deliverableDetail,
    parentDeliverableDetail: state => state.parentDeliverableDetail,
    projectDetail: state => state.projectDetail,
    dataLoading: state => state.dataLoading,
    deliverableCount: state => state.deliverableCount,
    duplicatingDeliverable: state => state.duplicatingDeliverable,
    duplicateDeliverableId: state => state.duplicateDeliverableId,
    duplicateProjectNumber: state => state.duplicateProjectNumber,
    tasksToHighlight: state => state.tasksToHighlight,
    taskHighlighted: state => state.taskHighlighted,
    newProjectCreated: state => state.newProjectCreated,
    projectSort: state => state.projectSort,
    myProjectSort: state => state.myProjectSort,
    isDirty: state => state.isDirty,
    dashboardMediaCallsFilter: state => state.dashboardMediaCallsFilter,
    numberStat: state => state.numberStat,
    widgetSettings: state => state.widgetSettings,
    systemSettings: state => state.systemSettings,
    deliverablePublishStatus: state => state.deliverablePublishStatus,
    socialCampaignDetail: state => state.socialCampaignDetail,
    socialGroupBy: state => state.socialGroupBy, // what user last selected, group by date or campaign/series
    socialSeriesDetail: state => state.socialSeriesDetail,
};
let fetchProjectXhrCount = 0;
let fetchUserProjectXhrCount = 0;
let fetchDeliverableXhrCount = 0;
let fetchProjectDeliverableXhrCount = 0;
let fetchUserManageProjectXhrCount = 0;
const actions = {

	addRecentProject({ state, commit }, projectId) {
		console.log("addRecentProject", projectId);
	     if (!projectId) return;
	     const updated = state.recentProjectIds.filter(id => id !== projectId);
	     updated.unshift(projectId);
	     commit('SET_RECENT_PROJECTS', updated.slice(0, MAX_RECENT));
	   },

	async showPostContentLangInListing({ commit, state }, payload) {
	    commit('showPostContentLangInListing', payload)
	},
	async showPostContentNetworkInListing({ commit, state }, payload) {
	    commit('showPostContentNetworkInListing', payload)
	},

    updateNewContactRelatedValues({ commit }, payload) {
        commit('updateNewContactRelatedValues', payload);
    },
    updateEditContactRelatedValues({ commit }, payload) {
        commit('updateEditContactRelatedValues', payload);
    },

    async saveTaskOrder({ commit, state }, payload) {
        commit('setDataLoading', true)
        let response = await axios.post(
            process.env.MIX_API_BASE_URL + 'saveTaskOrder',
            payload
        )
        // commit('updateTasksDisplayOrderValue', {taskData: response.data.taskData, deliverableId: payload.deliverableId})
        state.storedTaskFieldsOrder = response.data.taskOrderPreference
        state.preferenceData.taskOrder = response.data.taskOrderPreference
        commit('setDataLoading', false)
    },
    async saveTaskFieldOrder({ commit, state }, payload) {
        commit('setDataLoading', true)
        let response = await axios.post(
            process.env.MIX_API_BASE_URL + 'saveTaskFieldOrder',
            payload
        )
        state.storedTaskFieldsOrder = response.data.taskFieldOrderPreference
        state.preferenceData.taskFieldOrder = response.data.taskFieldOrderPreference
        commit('updateTaskFieldOrder', payload)
        commit('setDataLoading', false)
    },
    async updateDueDate({ commit }, payload) {
        if(payload.projectData.due_date) {
            var s = Vue.moment(payload.projectData.due_date);
                    s.set("hour", 0);
                    s.set("minute", 0);
                    s.set("second", 0);
                    payload.projectData.due_date = s.utc().format();
        }
        let response = await axios.post(
            process.env.MIX_API_BASE_URL + 'updateProjectDueDate',
            {projectDetail: payload.projectData}
        )
        commit('setProjectDetail', response.data)
    },
    
    showDuplicationWarning({ commit }, payload) {
        commit('SHOW_DUPLICATION_WARNING', payload);
    },
    
    hideDuplicationWarning({ commit }) {
        commit('HIDE_DUPLICATION_WARNING');
    },
    
    async getProjectNumberFromId({ commit }, projectId) {
        let response = await axios.get(
            process.env.MIX_API_BASE_URL + 'getProjectNumberFromId/' + projectId
        )
        return response.data;
    },
    async getProjectNumberFromDeliverableId({ commit }, deliverableId) {
        let response = await axios.get(
            process.env.MIX_API_BASE_URL + 'getProjectNumberFromDeliverableId/' + deliverableId,
            { silent404: true }
        )
        return response.data;
    },
    async createNewField({ commit }, payload) {
        let response = await axios.post(
            process.env.MIX_API_BASE_URL + 'createNewField',
            payload
        );
        return response
        // commit('setDeliverableTypes', response.data)
    },
    async removeCustomField({ commit }, payload) {
        let response = await axios.post(
            process.env.MIX_API_BASE_URL + 'removeCustomField',
            payload
        )
        let deliverableData = response.data.deliverableData
        let deliverableId = payload.deliverableId
        commit('removeFieldFromAllTasks', payload.labelId)
        commit('updateTaskData', {deliverableData, deliverableId})
        commit('groupUserDeliverable')
    },
    async updateFieldLabel({ commit }, payload) {
        let response = await axios.post(
            process.env.MIX_API_BASE_URL + 'updateFieldLabel',
            payload
        )

        commit('updateFieldLabelText', response.data)
    },

    async getRouterNameFromLocationHref({ commit, getters }) {
    	let url = window.location.pathname;

    	// looking at a social media post within the scheduled content tab of an organic social media plan.
		if (url.indexOf("/project/deliverable/") != -1 && url.indexOf("/social-media-schedule/") != -1) {
			return "socialMediaSchedule";
		}

		return router.currentRoute.name;
    },
    async getDeliverableCalendarData({ commit }, deliverableId) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "getDeliverableCalendarData/" +
                deliverableId
        );
        return typeof response.data == "undefined" ? response : response.data
    },
    async getCalendarData({ commit, getters }, payload) {
        let dataSetType = typeof payload == 'undefined' ? 'deliverables' : payload
        let filter = {},
        currentComponent = "calendar";
        filter = JSON.parse(JSON.stringify(getters.calendarFilter));
        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + 'getCalendarData?dataSetType='+dataSetType+'&currentComponent=' + currentComponent,
            filter
        );
        //TODO:: append data or update data
        commit('setCalendarEvents', response.data)
        return response.data;
    },
    async getDeliverableTypes({ commit, dispatch }, payload) {
        let response = await axios.get(
            process.env.MIX_API_BASE_URL + 'getDeliverableTypes?module=' + router.currentRoute.name,
        );
        commit('setDeliverableTypes', response.data.preferenceDeliverableTypes)
        commit('setAllDeliverableTypes', response.data.allDeliverableTypes)

        const newDeliverableTypes = await dispatch("getRevisedDeliverableTypes");
        commit('setNewDeliverableTypes', newDeliverableTypes);

    },
    async filterDeliverablesBasedOnSettings({state, commit, dispatch }) {
        // Check if state.deliverableTypes is empty, then fetch the data
        if (state.allDeliverableTypes.length === 0) {
            await dispatch("getDeliverableTypes");
        }
        const newDeliverableTypes = await dispatch("getRevisedDeliverableTypes");
        commit('setNewDeliverableTypes', newDeliverableTypes);
    },
    async filterNewDeliverablesBasedOnSettings({state, commit, dispatch }) {
        // Check if state.deliverableTypes is empty, then fetch the data
        if (state.allDeliverableTypes.length === 0) {
            await dispatch("getDeliverableTypes");
        }
        const newDeliverableTypes = await dispatch("getRevisedDeliverableTypes");
        commit('setNewDeliverableTypes', newDeliverableTypes);
    },
    async getRevisedDeliverableTypes({ state, dispatch, getters }) {
        const showSocialMediaInPlansOnlySetting = getters.getSystemSetting('social_media___post_requires_plan');
        if (showSocialMediaInPlansOnlySetting) {
            return state.deliverableTypes.filter(item => item.id !== 'social_media') || state.deliverableTypes;
        } else {
            return state.deliverableTypes;
        }
    },
    async getSocialMediaInPlansOnlyFromSettings({ state }, keyToCheck) {
    	if (state.systemSettings.settings) {
	        const socialMediaSetting = state.systemSettings.settings.find(setting => setting.setting_key === keyToCheck);
	        return socialMediaSetting?.setting_value?.value || false;
    	}
    	return false;
    },
    async getSocialMediaInPlansOnlyFromSettings({ state }, keyToCheck) {
    	if (state.systemSettings.settings) {
	        const socialMediaSetting = state.systemSettings.settings.find(setting => setting.setting_key === keyToCheck);
	        return socialMediaSetting?.setting_value?.value || false;
    	}
    	return false;
    },
    async getPreferenceDeliverableTypes({ commit }, payload) {
        let response = await axios.get(
            process.env.MIX_API_BASE_URL + 'getPreferenceDeliverableTypes',
        );
        commit('setPreferenceDeliverableTypes', response.data)
    },
    async idToLabel({}, payload) {
        let response = await axios.get(
            process.env.MIX_API_BASE_URL + 'idToLabel',
            {params: payload}
        );
        return response.data;
    },
    async fetchAuditLogs({ commit, getters }, payload) {
        let page = '';
        let filter = getters.auditLogFilter;
        if(typeof payload != 'undefined' && typeof payload.page != 'undefined') {
            page = '?page='+payload.page;
        }
        let response = await axios.post(
            process.env.MIX_API_BASE_URL + 'fetchAuditLogs' + page,
            filter
        );
        commit('setAuditLogs', response.data);
        return response.data;
    },
    manageUndoActivation({ commit }, text) {
        if(typeof text == 'undefined') {
            text = 'Action has been completed.';
        }
        if(typeof window.undoInterval != 'undefined') {
            commit('setActivateUndo', false);
            clearInterval(window.undoInterval);
        }
        commit('setActivateUndoText', text);
        commit('setActivateUndo', true);
        let sec = 10; // seconds to show the undo dialog
        $('.sec').text(sec);
        window.undoInterval = setInterval(function() {
            sec--;
            $('.sec').text(sec);
            if(sec == 0) {
                commit('setActivateUndo', false);
                clearInterval(window.undoInterval);
            }
        }, 1000)
    },
    manageUndoActivationNoScroll({ commit, dispatch }, text) {
        dispatch("manageUndoActivation", text);
        commit("setActivateUndoNoScroll", true);
    },
    manageProjectUndoActivation({ commit }, id) {
        if(typeof window.projectUndoInterval != 'undefined') {
            commit('setActivateMyProjectUndo', false);
            clearInterval(window.projectUndoInterval);
        }
        commit('setActivateMyProjectUndo', true);
        commit('setActivateMyProjectUndoId', id);
        let sec = 10;
        $(".sec-project").text(sec);
        window.projectUndoInterval = setInterval(function() {
            sec--;
            $(".sec-project").text(sec);
            if (sec == 0) {
                commit('setActivateMyProjectUndo', false);
                clearInterval(window.projectUndoInterval);
            }
        }, 1000);
    },
    async undoAction({ commit, dispatch, state }) {
        let currentComponent = router.currentRoute.name;
        let response = await axios.put(
            process.env.MIX_API_BASE_URL + 'undoAuditLog?currentComponent=' + currentComponent
        );
        if(
            currentComponent == 'dashboard' || 
            currentComponent == 'myWeek'
        ){
            commit(
                "setDeliverables",
                typeof response.data == "undefined" ? response : response.data
            );
        } else if(
            currentComponent == 'socialMedia' || 
            currentComponent == 'socialMediaGrouped'
        ){
            commit('setDeliverables', typeof response.data == "undefined" ? response : response.data);
            if(typeof response.data.allSocialSeries != 'undefined') {
                commit('setListSocialSeries', typeof response.data == "undefined" ? response.allSocialSeries : response.data.allSocialSeries);
            }
            if(typeof response.data.allSocialCampaigns != 'undefined') {
                commit('setListSocialCampaigns', typeof response.data == "undefined" ? response.allSocialCampaigns : response.data.allSocialCampaigns);
            }
        } else if(currentComponent == 'socialMediaSchedule'){
            commit("setCalendarDeliverables", response.data);
        } else if(currentComponent == 'projectDeliverable'){
            // commit("setProjectDetail", response.data.projectDetail);
            // commit("setProjectDeliverables", response.data);
            dispatch('fetchProjectDeliverable', state.currentPage);
        } else if(currentComponent == 'project') {
            commit("setProjects", response.data);
        } else if(currentComponent == 'myProjects') {
            dispatch('fetchUserProjectsNoScroll', state.currentPage);
        }

        if (state.activateUndoNoScroll) {
            commit("setActivateUndoNoScroll", false);
        } else {
            window.scrollTo(0, 0);
        }
    },
    async fetchAllCompanies({ commit }) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "getAllCompanies",
        );
        commit("setAllCompanies", response.data);
    },
    async fetchProjectDeliverable({ commit, getters }, page) {
        let sequenceNumber = ++fetchProjectDeliverableXhrCount;
        let filter = getters.projectDeliverableListFilter
        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        if (typeof page == "undefined") {
            page = 1;
        }
        this.state.currentPage = page;
        /*
        commit(
            "resetProjectDetail"
        )
        commit(
            "resetProjectDeliverables"
        )
        */
        if (
            typeof router.currentRoute.params.projectId != "undefined" && 
            router.currentRoute.params.projectId != "undefined"
        ) {
	        const response = await axios.post(
	            process.env.MIX_API_BASE_URL +
	                "getProjectDeliverable/"+router.currentRoute.params.projectId+"?page=" +
	                page,
	            filter
	        );
	        if (
	            typeof response.data.allDeliverables != "undefined" &&
	            response.data.allDeliverables.to -
	                response.data.allDeliverables.from ==
	                1
	        ) {
	            this.state.currentPage -= 1;
	        }

	        if(sequenceNumber == fetchProjectDeliverableXhrCount) {
	            fetchProjectDeliverableXhrCount = 0;
	            commit(
	                "setProjectDeliverables",
	                typeof response.data == "undefined" ? response : response.data
	            );
	            commit(
	                "setProjectDetail",
	                response.data.projectDetail
	            )
	            // window.scrollTo(0, 0);
	        }
        	return response;
	    } else {
	    	return null;
	    }
    },
    async fetchProjectCalendarDeliverable({ commit, getters }, page) {
        let filter = JSON.parse(JSON.stringify(getters.projectCalendarFilter));
        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        if (typeof page == "undefined") {
            page = 1;
        }
        this.state.currentPage = page;
        if (
            typeof router.currentRoute.params.projectId != "undefined" &&
            router.currentRoute.params.projectId != "undefined"
        ) {
            const response = await axios.post(
                process.env.MIX_API_BASE_URL +
                    "getProjectCalendarDeliverable/" +
                    router.currentRoute.params.projectId,
                filter
            );
            commit(
                "setProjectCalendarEvents",
                typeof response.data == "undefined" ? response : response.data
            );
            return response.data;
        } else {
            return null;
        }
    },
    async fetchProjectDeliverableById({ commit, getters }, id) {
        let sequenceNumber = ++fetchProjectDeliverableXhrCount;

        const defaultFilter = {
            query: "",
            sort: '',
            multiSort: [],
            priorityFilter: 0,
            outletCompany: [],
            filterByDate: false,
            filterByDateCreated: false,
            filterByDateCompleted: false,
            dateRange: {
                startDate: new Date(),
                endDate: new Date()
            },
            dateCreatedRange: {
                startDate: new Date(),
                endDate: new Date()
            },
            dateCompletedRange: {
                startDate: new Date(),
                endDate: new Date()
            },
            status: [],
            outlet: [],
            deliverableType: [],
            tag: [],
            department: [],
            coordinator: [],
            noDeparment: 'not_accepted',
            noOutlet: 'not_accepted',
        }
        const page = 1;

        if (typeof id !== 'undefined') {
            const response = await axios.post(
	            process.env.MIX_API_BASE_URL +
	                "getProjectDeliverable/"+id+"?page=" +
	                page,
                    defaultFilter
	        );
	        if (
	            typeof response.data.allDeliverables != "undefined" &&
	            response.data.allDeliverables.to -
	                response.data.allDeliverables.from ==
	                1
	        ) {
	            this.state.currentPage -= 1;
	        }

            if(sequenceNumber == fetchProjectDeliverableXhrCount) {
	            fetchProjectDeliverableXhrCount = 0;
	            commit(
	                "setProjectDeliverables",
	                typeof response.data == "undefined" ? response : response.data
	            );
	            commit(
	                "setProjectDetail",
	                response.data.projectDetail
	            )
	            // window.scrollTo(0, 0);
	        }
            return response;
        } else {
            return null;
        }

    },
    async fetchProjectUnreadMessageCount({ state, commit, getters }, type) {
        let existingProjectData;
        if(type=="project") {
            existingProjectData = getters.projects;
        } else if(type=="my-projects") {
            existingProjectData = getters.userProjects;
        }
        const projectIdArray = existingProjectData.map(item => item.id);
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "getProjectsUnreadMessageCount",
                { params: { projectIdArray } }
        );
        const newProjectData = response.data.map(obj => ({
            ...obj,
            id: parseInt(obj.id, 10) // Convert id to number using parseInt
          }));
        //set count into each row of existingProjectData in store
        const mergedUserProjectsArray = existingProjectData.map(item1 => {
            let matchingItem = newProjectData.find(item2 => item2.id === item1.id);
            return {...item1, ...matchingItem};
        });
        if (JSON.stringify(existingProjectData) !== JSON.stringify(mergedUserProjectsArray)) {
            if(type=="project") {
                commit(
                    "setProjectsOnly",
                    mergedUserProjectsArray
                );
            } else if(type=="my-projects") {
                commit(
                    "setUserProjectsOnly",
                    mergedUserProjectsArray
                );
            }
        }
    },

    async fetchUserProjects({ state, commit, getters }, page) {
        let sequenceNumberUserProject = ++fetchUserProjectXhrCount;
        let filter = {};

        filter = JSON.parse(JSON.stringify(getters.myProjectFilter));
        filter.sort = getters.myProjectSort;

        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        if (typeof page == "undefined") {
            page = 1;
        }
        state.currentPage = page;
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "getUserProjects?page=" +
                page,
            filter
        );
        if (
            typeof response.data.allDeliverables != "undefined" &&
            response.data.allDeliverables.to -
                response.data.allDeliverables.from ==
                1
        ) {
            state.currentPage -= 1;
        }

        if(sequenceNumberUserProject == fetchUserProjectXhrCount) {
            fetchUserProjectXhrCount = 0;
            commit(
                "setUserProjects",
                typeof response.data == "undefined" ? response : response.data
            );
            window.scrollTo(0, 0);
        }
    },
    async fetchUserProjectsNoScroll({ dispatch }, page) {
        const yOffset = window.pageYOffset;
        const result = await dispatch("fetchUserProjects", page);
        window.scrollTo(0, yOffset);
        return result;
    },
    async fetchUserProjectNoSet({ state, commit, getters }, page) {
        let filter = {};

        filter = JSON.parse(JSON.stringify(getters.myProjectFilter));

        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        if (typeof page == "undefined") {
            page = state.currentPage;
        }
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "getUserProjects?page=" +
                page,
            filter
        );

        return response;
    },
    async fetchUserProjectsCount({ commit, getters }) {
        if (!getters.isLoggedIn) return 0;

        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
            "getUserProjectsCount"
        );

        if (response.data) {
            commit('setUserHasProjects', response.data.count);
        } else {
            commit('setUserHasProjects', response);
        }
    },
    async fetchUserManageProjects({ commit, getters }) {
        let sequenceNumberUserManageProject = ++fetchUserManageProjectXhrCount;
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
            "getUserManageProjects"
        );

        if (sequenceNumberUserManageProject == fetchUserManageProjectXhrCount) {
            fetchUserManageProjectXhrCount = 0;

            commit(
                "setUserManageProjects",
                typeof response.data == "undefined" ? response : response.data
            );
        }
    },
    async fetchUserManageSocialCampaigns({ commit, getters }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
            "getUserManageSocialCampaigns"
        );
        commit(
            "setUserManageSocialCampaigns",
            typeof response.data == "undefined" ? response : response.data
        );
    },

    async fetchProjects({ state, commit, getters }, page) {
        let sequenceNumberProject = ++fetchProjectXhrCount;
        let filter = {};

        filter = JSON.parse(JSON.stringify(getters.projectFilter));
        filter.sort = getters.projectSort;

        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        if (typeof page == "undefined") {
            page = 1;
        }
        state.currentPage = page;
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "getProjects?page=" +
                page,
            filter
        );
        if (
            typeof response.data.allDeliverables != "undefined" &&
            response.data.allDeliverables.to -
                response.data.allDeliverables.from ==
                1
        ) {
            this.state.currentPage -= 1;
        }

        if(sequenceNumberProject == fetchProjectXhrCount) {
            fetchProjectXhrCount = 0;
            commit(
                "setProjects",
                typeof response.data == "undefined" ? response : response.data
            );
            window.scrollTo(0, 0);
        }
    },
    async fetchProjectsNoScroll({ dispatch }, page) {
        const yOffset = window.pageYOffset;
        const result = await dispatch("fetchProjects", page);
        window.scrollTo(0, yOffset);
        return result;
    },
    async fetchAllProjects({ commit, getters }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "getAllProjects"
        );

        commit("setAllProjects", response.data);
    },


    async fetchUserDeliverableUnreadMessageCount({ state, commit, getters }) {
        let existingDeliverableData;
        let currentComponent = router.currentRoute.name;
        let deliverableIdArray={};
        if (currentComponent == "myWeek") {
            existingDeliverableData = getters.userDeliverables;
            Object.keys(existingDeliverableData).forEach(function(key,index) {
                deliverableIdArray[key] = existingDeliverableData[key].map(item => item.id);
            });
            const response = await axios.get(
                process.env.MIX_API_BASE_URL +
                    "getUserDeliverablesUnreadMessageCount",
                    { params: { deliverableIdArray } }
            );
            if (typeof response.data != "undefined" ) {
                let newDeliverablesData = {};
                Object.keys(response.data).forEach(function(key,index) {
                    newDeliverablesData[key] = response.data[key].map(obj => ({
                        ...obj,
                        id: parseInt(obj.id, 10) // Convert id to number using parseInt
                    }));
                });
                //set count into each row of existingDeliverableData in store
                const mergedDeliverableData = {};
                Object.keys(existingDeliverableData).forEach(key => {
                    // Check if key exists in newData
                    if (newDeliverablesData.hasOwnProperty(key)) {
                         // Merge arrays if key exists in both objects
                         mergedDeliverableData[key]  = existingDeliverableData[key].map(item1 => {
                            let matchingItem = newDeliverablesData[key].find(item2 => item2.id === item1.id);
                            return {...item1, ...matchingItem};
                        });
                    } else {
                        // Copy array if key exists only in existingData
                        mergedDeliverableData[key] = existingDeliverableData[key].slice();
                    }
                });
                if (JSON.stringify(existingDeliverableData) !== JSON.stringify(mergedDeliverableData)) {
                    commit(
                        "setGroupedUserDeliverablesOnly",
                        mergedDeliverableData
                    );
                }
            }
        }
    },
    async fetchDeliverableUnreadMessageCount({ state, commit, getters }) {
        let existingDeliverableData;
        let currentComponent = router.currentRoute.name;
        if (currentComponent == "mediaCallPage" || currentComponent == "dashboard") {
            existingDeliverableData = getters.allDeliverables;
        } else if (currentComponent == "projectDeliverable") {
            existingDeliverableData = getters.projectDeliverables;
        } else {
            return;
        }
        const deliverableIdArray = existingDeliverableData.map(item => item.id);
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "getDeliverablesUnreadMessageCount",
                { params: { deliverableIdArray } }
        );
        if (typeof response.data != "undefined" ) {
            const newDeliverableData = response.data.map(obj => ({
                ...obj,
                id: parseInt(obj.id, 10) // Convert id to number using parseInt
              }));
            //set count into each row of existingProjectData in store
            const mergedDeliverableArray = existingDeliverableData.map(item1 => {
                let matchingItem = newDeliverableData.find(item2 => item2.id === item1.id);
                return {...item1, ...matchingItem};
            });
            if (JSON.stringify(existingDeliverableData) !== JSON.stringify(mergedDeliverableArray)) {
                if (currentComponent == "mediaCallPage" || currentComponent == "dashboard") {
                    commit(
                        "setDeliverablesOnly",
                        mergedDeliverableArray
                    );
                } else if (currentComponent == "projectDeliverable") {
                    commit(
                        "setProjectDeliverablesOnly",
                        mergedDeliverableArray
                    );
                } 

            }
        }
    },

    async fetchDeliverableBreadcrumbs({ commit }, payload) {
        try {
          const response = await axios.get(
            process.env.MIX_API_BASE_URL +
            "getDeliverableBreadcrumbs",
            {
              params: {
                ...payload,
              },
            });
            if (response.data && response.data.data) {
                commit('setBreadcrumbs', response.data.data);
            }
        } catch (error) {
          console.error("Error fetching breadcrumb data:", error);
        }
    },

      async fetchDeliverables({ state, commit, getters, dispatch }, page) {
        let sequenceNumber = ++fetchDeliverableXhrCount;
        let filter = {},
        currentComponent = "dashboard";


        if (router.currentRoute.name == "myWeek" || router.currentRoute.name == "myWeekNewDeliverable" || router.currentRoute.name == "myWeekDeliverableDetail") {
            filter = JSON.parse(
                JSON.stringify(getters.myWeekDeliverableFilter)
            );
            currentComponent = "myweek";
        } else if (router.currentRoute.name == "projectDeliverable") {
            filter = JSON.parse(
                JSON.stringify(getters.addProjectDeliverableFilter)
            );
            currentComponent = "projectDeliverable";
        } else if (router.currentRoute.name == "mediaCallPage" || router.currentRoute.name == "mediaCallPageNewMediaCall" || router.currentRoute.name == "mediaCallDetail") {
            filter = JSON.parse(JSON.stringify(getters.mediaCallFilter));
            currentComponent = "mediaCallPage";
        } else if (router.currentRoute.name == "socialMedia") {
            filter = JSON.parse(JSON.stringify(getters.socialMediaFilter));
            currentComponent = "socialMedia";
        } else if (router.currentRoute.name == "socialMediaSchedule") {
            filter = JSON.parse(JSON.stringify(getters.socialMediaCalendarFilter));

            // currentComponent = "socialMediaCalendarPage";
            // Using the component "socialMedia" because the different structure of the return data of "socialMediaCalendarPage" will cause error
            // And also we don't really use this for the calendar page anyways but something keep calling this function
            // --- Indy
            currentComponent = "socialMedia";
        } else {
            filter = JSON.parse(JSON.stringify(getters.deliverableFilter));


            if (typeof filter.parentDeliverableId  != "undefined") {
                if(filter.parentDeliverableId != 0) {
                    // for organic social media plan
                    filter = JSON.parse(JSON.stringify(getters.socialMediaFilter));
                } 
            }

        }


        if (filter.filterByDate == true) {
            // var s = Vue.moment(filter.dateRange.startDate);
            // s.set('hour', 0);
            // s.set('minute', 0);
            // s.set('second', 0);
            // filter.dateRange.startDate = s.toISOString();
            // var e = Vue.moment(filter.dateRange.endDate);
            // e.set('hour', 23);
            // e.set('minute', 59);
            // e.set('second', 59);
            // filter.dateRange.endDate = e.toISOString();
        }
        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        if (typeof filter.filterByPublishDate != "undefined") {
	        if (filter.filterByPublishDate == true) {
	            var s = Vue.moment(filter.datePublishRange.startDate);
	            s.set("hour", 0);
	            s.set("minute", 0);
	            s.set("second", 0);
	            filter.datePublishRange.startDate = s.utc().format();
	            var e = Vue.moment(filter.datePublishRange.endDate);
	            e.set("hour", 23);
	            e.set("minute", 59);
	            e.set("second", 59);
	            filter.datePublishRange.endDate = e.utc().format();
	        }
	    }

        if (currentComponent != "socialMedia") {
        	if (typeof filter.listByPublishDate  != "undefined") {
        		filter.listByPublishDate = false;
        	}
        	filter.socialCampaign = [];
        	filter.socialSeries = [];
        }

        //kevin
        let filter2 = JSON.parse(
            JSON.stringify(getters.deliverableCount)
        );
        filter.pageCount = 18;
        if (typeof page == "undefined") {
            page = 1;
        }
        let overdueCount = filter2.overdue;
        let todayCount = filter2.today;
        let thisWeekCount = filter2.thisWeek;
        let nextWeekCount = filter2.nextWeek;
        let upcomingCount = filter2.upcoming;
        let noDeadlineCount = filter2.noDeadline;
        if (page == 2){
            filter.pageCount = 35;
        }
        filter.overdueCount = overdueCount;
        filter.todayCount = todayCount;
        filter.thisWeekCount = thisWeekCount;
        filter.nextWeekCount = nextWeekCount;
        filter.upcomingCount = upcomingCount;
        filter.noDeadlineCount = noDeadlineCount;
        state.currentPage = page;
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "getDeliverables?page=" +
                page +
                "&currentComponent=" +
                currentComponent,
            filter
        );
        if (
            typeof response.data.allDeliverables != "undefined" &&
            response.data.allDeliverables.to -
                response.data.allDeliverables.from ==
                1
        ) {
            this.state.currentPage -= 1;
        }

        // if(typeof response.data.allDeliverables.outletCount != 'undefined') {
        //     commit('setOutletCount', response.data.allDeliverables.outletCount)
        // }

        if(sequenceNumber == fetchDeliverableXhrCount) {
            fetchDeliverableXhrCount = 0;
            if (router.currentRoute.name != "myWeek" && router.currentRoute.name != "projectDeliverable") {
                dispatch("countOutlets");
            }
            commit(
                "setDeliverables",
                typeof response.data == "undefined" ? response : response.data
            );
            if(currentComponent != 'projectDeliverable') {
                window.scrollTo(0, 0);
            }
        }
    },

    async fetchPartialCalendarDeliverables({ state, commit, getters }, filterQuery) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "getPartialCalendarDeliverables",
            filterQuery
        );
        if(typeof response.data != "undefined" )
        {
            if(filterQuery.mode == "year") {
                commit("setYearCalendarDeliverables", { 
                    allDeliverables: {
                        deliverables: {
                            data: response.data
                        }
                    } 
                });
            } else {
                // If year do not commit response
                commit("setCalendarDeliverables", { 
                    allDeliverables: {
                        deliverables: {
                            data: response.data
                        }
                    } 
                });
            }
        }
        return response.data;
    },

    async fetchCalendarDeliverables({ state, commit, getters }, page) {
        let filter = {};
        let currentComponent = "";
        page = 1;
        if (router.currentRoute.name == "socialMediaSchedule") {
            filter = JSON.parse(JSON.stringify(getters.socialMediaCalendarFilter));
            currentComponent = "socialMediaCalendarPage";
        }

        if (typeof filter != "undefined") {
            if(filter.parentDeliverableId != 0) {
                // for organic social media plan
                filter = JSON.parse(JSON.stringify(getters.socialMediaCalendarFilter));
            } 
        }
        
        // May need to add in additional code for it to work properly, see fetchDeliverables
        if (filter.filterByPublishDate == true) {
            var s = Vue.moment(filter.datePublishRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.datePublishRange.startDate = s.utc().format();
            var e = Vue.moment(filter.datePublishRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.datePublishRange.endDate = e.utc().format();
        }
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "getDeliverablesCalendar?page=" +
                page +
                "&currentComponent=" +
                currentComponent,
            filter
        );

        if (
            typeof response.data.allDeliverables != "undefined" &&
            response.data.allDeliverables.to -
                response.data.allDeliverables.from ==
                1
        ) {
            this.state.currentPage -= 1;
        }

        commit(
            "setCalendarDeliverables",
            typeof response.data == "undefined" ? response : response.data
        );
    },


    async startDownloadDeliverableExcel({ state, commit, getters }, filterQuery) {
        let filter = {};
        let currentComponent = "";
        let page = 1;

        if (filterQuery) {
        	filter = filterQuery;
        } else {
	        if (router.currentRoute.name == "socialMediaSchedule") {
	            filter = JSON.parse(JSON.stringify(getters.socialMediaFilter));
	            currentComponent = "socialMediaCalendarPage";
	        }
	    }
        // May need to add in additional code for it to work properly, see fetchDeliverables
        if (filter.filterByPublishDate == true) {
            var s = Vue.moment(filter.datePublishRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.datePublishRange.startDate = s.utc().format();
            var e = Vue.moment(filter.datePublishRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.datePublishRange.endDate = e.utc().format();
        }

        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "exportSocialMedia?page=" +
                page +
                "&currentComponent=" +
                currentComponent,
            filter
        );
        return response;
    },


    async updateCalendarDeliverablePublishDate({ state, commit, getters }, data) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "updateDeliverablePublishDate",
            data
        );
        
        const calendarDeliverables = getters.allCalendarDeliverables;
        const targetDeliverable = calendarDeliverables.find(x => x.id == response.data.deliverableDetail.id);
        if (targetDeliverable) {
        targetDeliverable.publish_date = response.data.deliverableDetail.publish_date;
        targetDeliverable.publish_status = response.data.deliverableDetail.publish_status;
        }

        commit("setCalendarDeliverables", { 
            allDeliverables: {
                deliverables: {
                    data: calendarDeliverables
                }
            } 
        });

		commit("setDeliverableDetailPublishDetails", response.data.deliverableDetail);



    },

    async undoUpdateDeliverablePublishDate({ state, commit, getters }, data) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "undoUpdateDeliverablePublishDate"
        );

        const calendarDeliverables = getters.allCalendarDeliverables;
        const targetDeliverable = calendarDeliverables.find(x => x.id == response.data.deliverableDetail.id);
        targetDeliverable.publish_date = response.data.deliverableDetail.publish_date;
        targetDeliverable.publish_status = response.data.deliverableDetail.publish_status;

        commit("setCalendarDeliverables", { 
            allDeliverables: {
                deliverables: {
                    data: calendarDeliverables
                }
            } 
        });

		commit("setDeliverableDetailPublishDetails", response.data.deliverableDetail);

    },


    async fetchSocialMediaDeliverablesGrouped({ state, commit, getters, dispatch }, params) {


        let sequenceNumber = ++fetchDeliverableXhrCount;
        let filter = {},
            currentComponent = router.currentRoute.name;
        if (router.currentRoute.name == "myWeek") {
            filter = JSON.parse(
                JSON.stringify(getters.myWeekDeliverableFilter)
            );
        } else if (router.currentRoute.name == "projectDeliverable") {
            filter = JSON.parse(
                JSON.stringify(getters.addProjectDeliverableFilter)
            );
        } else if (router.currentRoute.name == "mediaCallPage") {
            filter = JSON.parse(JSON.stringify(getters.mediaCallFilter));
        } else if (router.currentRoute.name == "socialMedia") {
            filter = JSON.parse(JSON.stringify(getters.socialMediaFilter));
        } else if (router.currentRoute.name == "socialMediaSchedule") {
            filter = JSON.parse(JSON.stringify(getters.socialMediaCalendarFilter));
        } else if (router.currentRoute.name == "socialMediaGrouped") {
            filter = JSON.parse(JSON.stringify(getters.socialMediaFilter));
            currentComponent = "socialMediaGroupPage";            
        } else {
            filter = JSON.parse(JSON.stringify(getters.deliverableFilter));
            currentComponent = "dashboard";

        }
        if (typeof filter.parentDeliverableId  != "undefined") {
            if(filter.parentDeliverableId != 0) {
                // for organic social media plan
                filter = JSON.parse(JSON.stringify(getters.socialMediaFilter));
            } 
        }
        if (typeof filter.pageCount == "undefined") {
        	filter.pageCount = 99999;
        }

        let page = 1;
        if (typeof params != "undefined") {
            if (typeof params.filter != "undefined") {
            	filter = JSON.parse(JSON.stringify(getters.socialMediaFilter));
            }
	        if (typeof params.page != "undefined") {
	        	page = params.page;
	        }
	        if (typeof params.pageCount != "undefined") {
	        	filter.pageCount = params.pageCount;
	        }
        }
        filter.page = page;


        if (filter.filterByDate == true) {
            // var s = Vue.moment(filter.dateRange.startDate);
            // s.set('hour', 0);
            // s.set('minute', 0);
            // s.set('second', 0);
            // filter.dateRange.startDate = s.toISOString();
            // var e = Vue.moment(filter.dateRange.endDate);
            // e.set('hour', 23);
            // e.set('minute', 59);
            // e.set('second', 59);
            // filter.dateRange.endDate = e.toISOString();
        }
        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        if (filter.filterByPublishDate == true) {
            var s = Vue.moment(filter.datePublishRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.datePublishRange.startDate = s.utc().format();
            var e = Vue.moment(filter.datePublishRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.datePublishRange.endDate = e.utc().format();
        }

        let filter2 = JSON.parse(
            JSON.stringify(getters.deliverableCount)
        );


        // overdue , today , thisweek, nextweek, upcoming , overdue
        let overdueCount = filter2.overdue;
        let todayCount = filter2.today;
        let thisWeekCount = filter2.thisWeek;
        let nextWeekCount = filter2.nextWeek;
        let upcomingCount = filter2.upcoming;
        let noDeadlineCount = filter2.noDeadline;
        filter.overdueCount = overdueCount;
        filter.todayCount = todayCount;
        filter.thisWeekCount = thisWeekCount;
        filter.nextWeekCount = nextWeekCount;
        filter.upcomingCount = upcomingCount;
        filter.noDeadlineCount = noDeadlineCount;
        //kevin end
        filter.parentDeliverableId = filter.parentDeliverableId;
        state.currentPage = page;



        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "getSocialMediaDeliverablesGrouped?page=" +
                page +
                "&currentComponent=" +
                currentComponent,
            filter
        );
        if (
            typeof response.data.allDeliverables != "undefined" &&
            response.data.allDeliverables.to -
                response.data.allDeliverables.from ==
                1
        ) {
            this.state.currentPage -= 1;
        }

        // if(typeof response.data.allDeliverables.outletCount != 'undefined') {
        //     commit('setOutletCount', response.data.allDeliverables.outletCount)
        // }



        if(sequenceNumber == fetchDeliverableXhrCount) {
            fetchDeliverableXhrCount = 0;
            if (router.currentRoute.name != "myWeek" && router.currentRoute.name != "projectDeliverable") {
                dispatch("countOutlets");
            }
            commit(state._namespace+"setDeliverables",typeof response.data == "undefined" ? response : response.data);


            if(currentComponent != 'socialMediaGrouped') {
                window.scrollTo(0, 0);
            }
        } else {
        }

        if (typeof response.data == "undefined" ) {
        	if(typeof response.allSocialSeries != 'undefined') {
        	    commit('setListSocialSeries', response.allSocialSeries);
        	} else {
				commit('setListSocialSeries', []);
        	}
        	if(typeof response.allSocialCampaigns != 'undefined') {
        	    commit('setListSocialCampaigns', response.allSocialCampaigns);
        	} else {
        	    commit('setListSocialCampaigns', []);
        	}
        } else {
	        if(typeof response.data.allSocialSeries != 'undefined') {
	            commit('setListSocialSeries', typeof response.data == "undefined" ? response.allSocialSeries : response.data.allSocialSeries);
	    	} else {
				commit('setListSocialSeries', []);
	    	}
	        if(typeof response.data.allSocialCampaigns != 'undefined') {
	            commit('setListSocialCampaigns', typeof response.data == "undefined" ? response.allSocialCampaigns : response.data.allSocialCampaigns);
	        } else {
        	    commit('setListSocialCampaigns', []);
        	}
	    }

    },


    async fetchPublicDeliverablesGraph({ state, commit, getters, dispatch }, page) {
        let sequenceNumber = ++fetchDeliverableXhrCount;
        let filter = {},
            currentComponent = "dashboardMediaCallsGraph";

        filter = JSON.parse(JSON.stringify(getters.dashboardMediaCallsFilter));

        currentComponent = filter.dashboardType;

        if (filter.filterByDate == true) {
        }
        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        //kevin
        let filter2 = JSON.parse(
            JSON.stringify(getters.deliverableCount)
        );
        filter.pageCount = 18;
        if (typeof page == "undefined") {
            page = 1;
        }
        let overdueCount = filter2.overdue;
        let todayCount = filter2.today;
        let thisWeekCount = filter2.thisWeek;
        let nextWeekCount = filter2.nextWeek;
        let upcomingCount = filter2.upcoming;
        let noDeadlineCount = filter2.noDeadline;
        if (page == 2){
            filter.pageCount = 35;
        }
        filter.overdueCount = overdueCount;
        filter.todayCount = todayCount;
        filter.thisWeekCount = thisWeekCount;
        filter.nextWeekCount = nextWeekCount;
        filter.upcomingCount = upcomingCount;
        filter.noDeadlineCount = noDeadlineCount;
        state.currentPage = page;
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "widget/getPublicDeliverableList?page=" +
                page +
                "&currentComponent=" +
                currentComponent,
            filter
        );
        if (
            typeof response.data.allDeliverables != "undefined" &&
            response.data.allDeliverables.to -
                response.data.allDeliverables.from ==
                1
        ) {
            this.state.currentPage -= 1;
        }


        if(sequenceNumber == fetchDeliverableXhrCount) {
            fetchDeliverableXhrCount = 0;
            if(currentComponent != 'projectDeliverable') {
                window.scrollTo(0, 0);
            }
            return response.data;
        }
        return response.data;
    },

    async fetchPublicDeliverables({ state, commit, getters, dispatch }, page) {
        let sequenceNumber = ++fetchDeliverableXhrCount;
        let filter = {},
            currentComponent = "dashboardMediaCalls";

        filter = JSON.parse(JSON.stringify(getters.dashboardMediaCallsFilter));

        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        if (typeof page == "undefined") {
            page = 1;
        }

        state.currentPage = page;
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "widget/getPublicDeliverableList?page=" +
                page +
                "&currentComponent=" +
                currentComponent,
            filter
        );

        if (
            typeof response.data.allDeliverables != "undefined" &&
            response.data.allDeliverables.to -
                response.data.allDeliverables.from ==
                1
        ) {
            this.state.currentPage -= 1;
        }

        if(sequenceNumber == fetchDeliverableXhrCount) {
            fetchDeliverableXhrCount = 0;
            commit(
                "setDeliverables",
                typeof response.data == "undefined" ? response : response.data
            );
            if(currentComponent != 'projectDeliverable') {
                window.scrollTo(0, 0);
            }
        }
    },
    async fetchDeliverableNoScroll({ dispatch }, page) {
        const yOffset = window.pageYOffset;
        const result = await dispatch("fetchDeliverables", page);
        window.scrollTo(0, yOffset);
        return result;
    },
    async fetchWidgetSettings({ commit, getters }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "getWidgetSettings",
            {
                params: {
                    currentRoute: router.currentRoute.path
                }
            }
        );
        commit("setWidgetSettings", response.data);
        return response.data;
    },
    async fetchNumberStat({ commit, getters }) {
        let currentComponent = router.currentRoute.name;

        let filter = JSON.parse(
            JSON.stringify(getters.dashboardMediaCallsFilter)
        );
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "widget/getNumberStat?currentComponent=" + currentComponent,
            filter
        );
        commit("setNumberStat", response.data);
        return response.data;
    },
    async fetchDeliverableCount({ commit, getters }) {
        let filter = JSON.parse(
            JSON.stringify(getters.myWeekDeliverableFilter)
        );

        if (filter.filterByDate == true) {
            // var s = Vue.moment(filter.dateRange.startDate);
            // s.set('hour', 0);
            // s.set('minute', 0);
            // s.set('second', 0);
            // filter.dateRange.startDate = s.toISOString();
            // var e = Vue.moment(filter.dateRange.endDate);
            // e.set('hour', 23);
            // e.set('minute', 59);
            // e.set('second', 59);
            // filter.dateRange.endDate = e.toISOString();
        }
        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }
        if (filter.filterByPublishDate == true) {
            var s = Vue.moment(filter.datePublishRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.datePublishRange.startDate = s.utc().format();
            var e = Vue.moment(filter.datePublishRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.datePublishRange.endDate = e.utc().format();
        }
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "getDeliverableCount",
            filter
        );
        commit("setDeliverableCount", response.data);
    },

    async filterDeliverables({ commit }, filter) {
        commit("clearMergeDeliverables", null);

        let page = 1;
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "getDeliverables?page=" + page,
            filter
        );
        commit("setDeliverables", response.data);
        window.scrollTo(0, 0);
    },

    async saveGlobalAlert({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "saveGlobalAlert",
            payload
        );
        commit('setAllAlerts', response.data.allAlerts);
        commit('setUserAlerts', response.data.userAlerts);
    },

    async makeAlertRead({ commit }, payload) {
        let param = {loggedout: 'false'};
        if (payload && typeof payload.alert.loggedout != 'undefined') {
            param = {loggedout: 'true'}
        }
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + 'makeAlertRead/' + payload.alert.id,
            param
        );

        // Skip store mutation when caller opts into silent mode (used by local-only UI updates)
        const isSilent = payload && payload.silent;
        if (!isSilent) {
        commit('setAllAlerts', response.data.allAlerts);
        commit('setUserAlerts', response.data.userAlerts);
        commit('setNotification', response.data.notification);
        }

        return response;
    },

    async makeAllAlertsRead({ commit }, payload) {
        let param = {loggedout: 'false'};
        // if(typeof payload.alert.loggedout != 'undefined') {
        //     param = {loggedout: 'true'}
        // }
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + 'makeAllAlertsRead',
            param
        );
        commit('setAllAlerts', response.data.allAlerts);
        commit('setUserAlerts', response.data.userAlerts);
        commit('setNotification', response.data.notification);
    },

    async makeAlertsRead({ commit }, payload) {
        // Mark multiple specific alerts as read by their IDs
        let param = {
            loggedout: 'false',
            alertIds: payload.alertIds,
            markAllFiltered: payload.markAllFiltered || false,
            markGroupAll: payload.markGroupAll || false,
            group_type: payload.group_type || null,
            group_id: payload.group_id || null,
            deliverable_group_id: payload.deliverable_group_id || null,
            project_section_only: payload.project_section_only || false,
            filters: payload.filters || null
        };
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + 'makeAlertsRead',
            param
        );
        commit('setAllAlerts', response.data.allAlerts);
        commit('setUserAlerts', response.data.userAlerts);
        commit('setNotification', response.data.notification);
        return response;
    },



    async makeAllRelatedAlertRead({ commit }, payload) {
        if(payload.groupedModelId !== undefined) {
            const response = await axios.put(
                process.env.MIX_API_BASE_URL + 'makeAllRelatedAlertRead/' + payload.groupedModelId,
                payload
            );
            commit('setAllAlerts', response.data.allAlerts);
            commit('setUserAlerts', response.data.userAlerts);
            commit('setNotification', response.data.notification);
        } else {
            console.warn('Cannot make All Related Alert as Read, id is undefined');
        }
    },

    async deleteAlert({ commit }, payload) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL + "deleteAlert/" + payload.alertId
        );
        commit('setAllAlerts', response.data.allAlerts);
        commit('setUserAlerts', response.data.userAlerts);
        commit('setNotification', response.data.notification);
    },

    async fetchNotification({ commit }, payload) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + 'fetchNotification/' + payload.userId,
        );
        if(response.data.length) {
            response.data.forEach(function(value, key) {
                if(value.status == 0 && value.alert_type == 'system-update') {
                    toast.permanentWarning(value.message + ' <a href="#" data-alertId="'+value.id+'" id="update-logout">Click here</a> to logout', value.title);
                }
            });
        }
        commit('setNotification', response.data);
    },

    async fetchUserAlerts({ commit }, payload) {
        let params = {};
        
        if (typeof payload === 'object' && payload !== null) {
            // If payload is an object with filter parameters
            params = { ...payload };
            
            // Ensure alert_types is properly formatted as array of slugs
            if (params.alert_types && Array.isArray(params.alert_types)) {
                params.alert_types = params.alert_types.map(type => 
                    typeof type === 'object' ? type.slug : type
                ).filter(slug => slug); // Remove any null/undefined values
            }
            
            // Ensure page is set
            if (!params.page) {
                params.page = 1;
            }
        } else {
            // Legacy behavior - payload is just page number
            params.page = payload || 1;
        }
        
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "fetchUserAlerts",
            params
        );
        commit('setUserAlerts', response.data);
    },

    /**
     * Fetch alerts for a specific group (deliverable or project)
     * Used for Load More functionality when grouping by deliverable/project
     */
    async fetchGroupedAlerts({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "fetchGroupedAlerts",
            payload
        );
        return response.data;
    },

    async fetchAlerts({ commit }, payload) {
        let page = payload;
        if(typeof page == 'undefined') {
            page = 1;
        }
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "fetchAlerts",
            {params: {page: page}}
        );
        commit('setAllAlerts', response.data);
    },

    async fetchAlertById({ commit }, alertId) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "fetchAlert/" + alertId
        );
        return response.data;
    },

    async fetchUsers({ commit, getters }, payload) {
        if(getters.allUsers.length == 0 || (typeof payload != 'undefined' && payload.forced)) {
            const response = await axios.get(
                process.env.MIX_API_BASE_URL + "getUsers"
            );
            commit("setUsers", response.data);
        }
    },

    async fetchActiveUsers({ commit, getters }, payload) {
        if(getters.allUsers.length == 0 || (typeof payload != 'undefined' && payload.forced)) {
            const response = await axios.get(
                process.env.MIX_API_BASE_URL + "getActiveUsers"
            );
            commit("setActiveUsers", response.data);
        }
    },

    async fetchGlobalAlertTypes({ commit, getters }, payload) {
        if(getters.globalAlertTypes.length == 0 || (typeof payload != 'undefined' && payload.forced)) {
            const response = await axios.get(
                process.env.MIX_API_BASE_URL + "getGlobalAlertTypes"
            );
            commit("setGlobalAlertTypes", response.data);
        }
    },

    async fetchUserNotificationPreferences({ commit }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "notification-preferences"
        );
        if (response.data) {
            commit("setUserNotificationPreferences", response.data);
        }
        return response.data;
    },

    async fetchUserPermissions() {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + 'fetchUserPermissions'
        );
        this.state.userAbilities = response.data;
    },

    async fetchViews({ commit }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + 'fetchViews'
        );
        commit('setViews', response.data);
    },

    async saveView({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + 'saveView',
            payload
        );
        let that = this;
        if (router.currentRoute.name == "dashboard") {
            payload.viewId != '' ? localStorage.setItem('dashboardActiveSave', payload.viewId) : localStorage.setItem('dashboardActiveSave', response.data[0].id);
        } else if (router.currentRoute.name == "myWeek") {
            payload.viewId != '' ? localStorage.setItem('myWeekActiveSave', payload.viewId) : localStorage.setItem('myWeekActiveSave', response.data[0].id);
        } else if (router.currentRoute.name == "mediaCallPage") {
            payload.viewId != '' ? localStorage.setItem('mediaCallActiveSave', payload.viewId) : localStorage.setItem('mediaCallActiveSave', response.data[0].id);
        } else if (router.currentRoute.name == "project") {
            payload.viewId != '' ? localStorage.setItem('projectActiveSave', payload.viewId) : localStorage.setItem('projectActiveSave', response.data[0].id);
        } else if (router.currentRoute.name == "myProjects") {
            payload.viewId != '' ? localStorage.setItem('myProjectsActiveSave', payload.viewId) : localStorage.setItem('myProjectsActiveSave', response.data[0].id);
        } else if (router.currentRoute.name == "userGlobalAlert") {
            payload.viewId != '' ? localStorage.setItem('notificationActiveSave', payload.viewId) : localStorage.setItem('notificationActiveSave', response.data[0].id);
        }
        commit('setViews', response.data);
    },

    async removeView({ commit }, payload) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL + 'removeView/' + payload
        );
        commit('setViews', response.data);
    },

    async fetchSystemSettings({ commit, getters, dispatch }) {
        // Check if system settings is empty (default)
        if(Object.keys(getters.systemSettings).length === 0) {
            const response = await axios.get(
                process.env.MIX_API_BASE_URL + "getSystemSettings"
            );
            commit("setSystemSettings", response.data);

            // Dispatch the action to update deliverable types based on system settings social_media___post_requires_plan
            await dispatch("filterDeliverablesBasedOnSettings");
        }
    },



    async fetchAllGroupedDeliverables({ commit, getters }, payload) {
    	let filter = {};
		filter.socialCampaign = [];
		filter.socialSeries = [];

    	if (payload) {
    		if (payload.social_campaign_id) {
    			filter.socialCampaign.push({"id":payload.social_campaign_id});
    		}
    		if (payload.social_series_id) {
    			filter.socialSeries.push({"id":payload.social_series_id});
    		}
    	}
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "getDeliverables?page=" +
                1 +
                "&currentComponent=" +
                "socialMedia",
            filter
        );
            commit(
                "mergeDeliverables",
                typeof response.data == "undefined" ? response : response.data
            );
    },

    async fetchSocialSeries({ commit, getters }) {
        if(getters.allSocialSeries.length == 0) {
            const response = await axios.post(
                process.env.MIX_API_BASE_URL + "getSocialSeries"
            );
            commit("setAllSocialSeries", response.data.allSocialSeries.data);
        }
    },

    async fetchSocialCampaigns({ commit, getters }) {
        if(getters.allSocialCampaigns.length == 0) {
            const response = await axios.post(
                process.env.MIX_API_BASE_URL + "getSocialCampaigns"
            );
            commit("setAllSocialCampaigns", response.data.allSocialCampaigns.data);
        }
    },

    async fetchDepartments({ commit, getters }) {
        if(getters.allDepartments.length == 0) {
            const response = await axios.get(
                process.env.MIX_API_BASE_URL + "getDepartments"
            );
            commit("setDepartments", response.data);
        }
    },

    async fetchActiveDepartments({ commit, getters }) {
        if(getters.allDepartments.length == 0) {
            const response = await axios.get(
                process.env.MIX_API_BASE_URL + "getActiveDepartments"
            );
            commit("setActiveDepartments", response.data);
        }
    },

    async fetchTags({ commit, getters }) {
        if(getters.allTags.length == 0) {
            const response = await axios.get(
                process.env.MIX_API_BASE_URL + "getTags"
            );
            commit("setTags", response.data);
        }
    },

    async fetchSubjects({ commit }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "getSubjects"
        );
        commit("setSubjects", response.data);
    },

    async fetchContacts({ commit }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "getContacts"
        );
        commit("setContacts", response.data);
    },

    async fetchActiveContacts({ commit }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "getActiveContacts"
        );
        commit("setActiveContacts", response.data);
    },

    async getProjectDetail({ commit }, projectId) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "getProjectDetail/" +
                projectId.projectId
        );
        commit(
            "setProjectDetail",
            typeof response.data == "undefined" ? response : response.data
        );
    },

    async getDeliverableDetailOnly({ commit }, deliverableId) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "getDeliverableDetail/" +
                deliverableId.deliverableId
        );
        commit(
            "setDeliverableDetailOnly",
            typeof response.data == "undefined" ? response : response.data
        );
    },

    async getDeliverableDetail({ commit }, deliverableId) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "getDeliverableDetail/" +
                deliverableId.deliverableId
        );
        commit(
            "setDeliverableDetail",
            typeof response.data == "undefined" ? response : response.data
        );
    },

    async getParentDeliverableDetail({ commit }, deliverableId) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "getDeliverableDetail/" +
                deliverableId.deliverableId
        );
        commit(
            "setParentDeliverableDetail",
            typeof response.data == "undefined" ? response : response.data
        );
    },

    async downloadSocialPostsFiles({ commit }, payload) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "downloadSocialPostsFiles/" +
                payload.deliverableId,
            {
                responseType: "blob"
            }
        );
        const contentDisposition = response.headers['content-disposition'];
        let fileName = 'download.zip';
        if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
            if (fileNameMatch && fileNameMatch.length > 1) {
                fileName = fileNameMatch[1];
            }
        }
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
    },

    async getSocialSeriesDetail({ commit }, payload) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "getSocialSeriesDetail/" +
                payload.seriesId
        );
        commit(
            "setSocialSeriesDetail",
            typeof response.data == "undefined" ? response : response.data.data
        );
    },

    async getDeliverableDetailByTask({ commit }, taskId) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL +
                "getDeliverableDetailByTask/" +
                taskId.taskId
        );
        commit(
            "setDeliverableDetail",
            typeof response.data == "undefined" ? response : response.data
        );
    },

    async sendFeedback({ commit }, feedbackData) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "sendFeedback",
            feedbackData
        );
        return response;
    },

    async createTask({ commit, dispatch }, deliverableId) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "createTask/media_call/" +
                deliverableId
        );
        commit("setDeliverable", response.data.deliverableDetail);
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setProjectDeliverable", response.data.deliverableDetail);
        commit("groupUserDeliverable");

        // if(router.currentRoute.name == 'projectDeliverable') {
        //     let projectId = router.currentRoute.params.projectId
        //     dispatch(
        //         "fetchProjectDeliverable"
        //     )
        // }

        if (typeof response.data.newTaskId != 'undefined') {
            return response.data.newTaskId
        }
    },

    async removeTask({ commit,dispatch }, payload) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "removeTask/" +
                payload.deliverableId +
                "/" +
                payload.taskId
        );
        if(router.currentRoute.name == 'projectDeliverable') {
            dispatch(
                "fetchProjectDeliverable"
            )
        }
        return response.data;
    },

    async updateDepartmentFieldValue({ getters, commit, dispatch }, fieldUpdateData) {
        let updateData = {};
        if(typeof fieldUpdateData.field_id != 'undefined') {
            let fieldValueData = fieldUpdateData.field_key != 'color_picker' ? fieldUpdateData.value : fieldUpdateData.value.hex;
            updateData = {
                compontent: router.currentRoute.name,
                id: fieldUpdateData.id,
                parentId: fieldUpdateData.parent_id,
                fieldId: fieldUpdateData.field_id,
                departmentFieldId: fieldUpdateData.departmentFieldId,
                value: fieldValueData,
                departmentId: typeof fieldUpdateData.department_id == 'undefined' ? fieldUpdateData.id : fieldUpdateData.department_id
            };
        } else {
            updateData = {
                compontent: router.currentRoute.name,
                value: fieldUpdateData.title,
                departmentId:
                    typeof fieldUpdateData.departmentId != "undefined"
                        ? fieldUpdateData.departmentId
                        : fieldUpdateData.id,
                departments: fieldUpdateData.departments,
            };
        }
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateDepartmentFieldValue",
            updateData
        );
        // if(router.currentRoute.name == 'department') {
        //     dispatch('fetchDepartmentsNoScroll', getters.currentPage);
        // }
        // if(router.currentRoute.name == 'departmentDeliverable') {
        //     // commit("setDepartment", response.data.departmentDetail);
        //     commit("setDepartmentWithField", response.data);
        // }
        commit("setIsDirty",false);
    },


    async updateProjectDepartments({ getters, commit, dispatch, state }, fieldUpdateData) {
        let updateData = {};
        let x = 0;
        updateData = {
            projectId:
                typeof fieldUpdateData.projectId != "undefined"
                    ? fieldUpdateData.projectId
                    : fieldUpdateData.id,
            departments: fieldUpdateData.departments,
        };
        let existing_project = null;

        if(router.currentRoute.name == 'myProjects') {
        	if (
        	    typeof state.userProjects != "undefined"
        	) {
        	    for (x = 0; x < Object.keys(state.userProjects).length; x++) {
        	        if (typeof state.userProjects[x] != 'undefined' && state.userProjects[x].id == updateData.projectId) {
        	        	existing_project = _.cloneDeep(state.userProjects[x]);
						break;
        	        }
        	    }
        	}
        }
        if(router.currentRoute.name == 'project') {
        	if (
        	    typeof state.projects != "undefined"
        	) {
        	    for (x = 0; x < Object.keys(state.projects).length; x++) {
        	        if (typeof state.projects[x] != 'undefined' && state.projects[x].id == updateData.projectId) {
        	        	existing_project = _.cloneDeep(state.projects[x]);
						break;
        	        }
        	    }
        	}
        }
        if(router.currentRoute.name == 'projectDeliverable') {
			existing_project = _.cloneDeep(state.projectDetail);
        }
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateProjectDepartments",
            updateData
        );
        if(router.currentRoute.name == 'myProjects') {
            await dispatch('fetchUserProjectsNoScroll', getters.currentPage)
        }
        if(router.currentRoute.name == 'project') {
            await dispatch('fetchProjectsNoScroll', getters.currentPage);
        }
        if(router.currentRoute.name == 'projectDeliverable') {
            if (state.projectDetail) {
            	if (state.projectDetail.id == response.data.projectDetail.id) {
            		if (state.projectDetail.engagement_manager.id != response.data.projectDetail.engagement_manager.id) {
            			let msg = "Project Engagement Manager has been changed from '" + state.projectDetail.engagement_manager.name + "' to '" + response.data.projectDetail.engagement_manager.name + "' for the chosen department";

            			if (state.projectDetail.engagement_manager_locked  != response.data.projectDetail.engagement_manager_locked) {
            				if (!response.data.projectDetail.engagement_manager_locked) {
            					msg += " and is UNLOCKED";
            				}
            			}
            			msg += ".";
            			commit("setProject", response.data.projectDetail);
						toast.infoTop(msg);
            		}
            	}
            }
        }
        if(router.currentRoute.name == 'myProjects') {
        	if (
        	    typeof state.userProjects != "undefined"
        	) {
        	    for (x = 0; x < Object.keys(state.userProjects).length; x++) {

        	        if (typeof state.userProjects[x] != 'undefined' && state.userProjects[x].id == updateData.projectId) {
	            		if (state.userProjects[x].engagement_manager.id != existing_project.engagement_manager.id) {
    	            			let msg = "Project Engagement Manager has been changed from '" + existing_project.engagement_manager.name + "' to '" + state.userProjects[x].engagement_manager.name + "' for the chosen department";

	            			if (state.userProjects[x].engagement_manager_locked  != existing_project.engagement_manager_locked && existing_project.engagement_manager_locked != null) {
	            				if (!state.userProjects[x].engagement_manager_locked) {
	            					msg += " and is UNLOCKED";
	            				}
	            			}
	            			msg += ".";
							toast.infoTop(msg);
							break;
	            		}
        	        }
        	    }
        	}
        }

        if(router.currentRoute.name == 'project') {
        	if (
        	    typeof state.projects != "undefined"
        	) {
        	    for (x = 0; x < Object.keys(state.projects).length; x++) {

        	        if (typeof state.projects[x] != 'undefined' && state.projects[x].id == updateData.projectId) {
	            		if (state.projects[x].engagement_manager.id != existing_project.engagement_manager.id) {
    	            			let msg = "Project Engagement Manager has been changed from '" + existing_project.engagement_manager.name + "' to '" + state.projects[x].engagement_manager.name + "' for the chosen department";

	            			if (state.projects[x].engagement_manager_locked  != existing_project.engagement_manager_locked && existing_project.engagement_manager_locked != null) {
	            				if (!state.projects[x].engagement_manager_locked) {
	            					msg += " and is UNLOCKED";
	            				}
	            			}
	            			msg += ".";
							toast.infoTop(msg);
							break;
	            		}
        	        }
        	    }
        	}
        }


        commit("setIsDirty",false);
    },


    async updateProjectFieldValue({ getters, commit, dispatch }, fieldUpdateData) {
        let updateData = {};
        let responseData = {};
        if(typeof fieldUpdateData.field_id != 'undefined') {
            let fieldValueData = fieldUpdateData.field_key != 'color_picker' ? fieldUpdateData.value : fieldUpdateData.value.hex;
            updateData = {
                compontent: router.currentRoute.name,
                id: fieldUpdateData.id,
                parentId: fieldUpdateData.parent_id,
                fieldId: fieldUpdateData.field_id,
                projectFieldId: fieldUpdateData.projectFieldId,
                value: fieldValueData,
                projectId: typeof fieldUpdateData.project_id == 'undefined' ? fieldUpdateData.id : fieldUpdateData.project_id
            };
        } else {
            updateData = {
                compontent: router.currentRoute.name,
                value: fieldUpdateData.title,
                projectId:
                    typeof fieldUpdateData.projectId != "undefined"
                        ? fieldUpdateData.projectId
                        : fieldUpdateData.id,
                departments: fieldUpdateData.departments,
            };
        }
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateProjectFieldValue",
            updateData
        );


        if (response.data.original) {
        	responseData = response.data.original;
        } else {
        	responseData = response.data;
        }

        if(
            responseData.updatedField == "title" &&
            responseData.projectDetail.id === getters.breadcrumbs.parent_project_id
        ) {
            // set new title to breadcrumbs if project_id same
            commit(
                "setBreadcrumbs",
                {
                    ...getters.breadcrumbs,
                    "parent_project_title": responseData.projectDetail.title,
                }
            );
        }
        if(router.currentRoute.name == 'myProjects') {
            dispatch('fetchUserProjectsNoScroll', getters.currentPage)
        }
        if(router.currentRoute.name == 'project') {
            dispatch('fetchProjectsNoScroll', getters.currentPage);
        }
        if(router.currentRoute.name == 'projectDeliverable') {
            // commit("setProject", responseData.projectDetail);
            commit("setProjectWithField", responseData);
        }
        if(
            responseData.updatedField == "project_status"
        ) {
            var newStatus = null;
            for (var x = 0; x < responseData.projectDetail.customFields.length ;x++) {
            	if (responseData.projectDetail.customFields[x].field_key == responseData.updatedField) {
            		newStatus = JSON.parse(responseData.projectDetail.customFields[x].value);
            	}
            }
            commit(
                "setProjectStatusForDeliverables",
                {
                	"project_id":responseData.projectDetail.projectId,
                	"value":newStatus
                }
            );
        }

        commit("setIsDirty",false);
    },


    async toggleProjectEngagementManagerLock({ getters, commit, dispatch }, fieldUpdateData) {
        let updateData = {};

        updateData = {
            projectId:
                typeof fieldUpdateData.projectId != "undefined"
                    ? fieldUpdateData.projectId
                    : fieldUpdateData.id,
        };
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "toggleProjectEngagementManagerLock",
            updateData
        );
        commit("setProjectEngagementManagerLock", response.data);
        // if(router.currentRoute.name == 'myProjects') {
        //     dispatch('fetchUserProjectsNoScroll', getters.currentPage)
        // }
        // if(router.currentRoute.name == 'project') {
        //     dispatch('fetchProjectsNoScroll', getters.currentPage);
        // }
        if(router.currentRoute.name == 'projectDeliverable') {
            commit("setProjectWithField", response.data);
        }
        commit("setIsDirty",false);
    },



    async setDeliverableProject({ commit, dispatch }, data) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL +
                "setDeliverableProject/",
            data
        );

        if(router.currentRoute.name == 'projectDeliverable') {
            dispatch('fetchProjectDeliverable');
        }
        // dispatch('manageUndoActivation', 'Project assigned.');
        return response.data;
    },

    async addDeliverableToProject({ commit }, data) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL +
                "addDeliverableToProject/",
            data
        );
        commit(
            "setProjectDeliverables",
            response.data
        );
        commit(
            "setProjectDetail",
            response.data.projectDetail
        );
        return response.data;
    },

    async updateMultipleUsersFieldValue({ commit, state, dispatch}, fieldUpdateData) {
        dispatch("updateDeliverableFieldValue", {
            parentId: fieldUpdateData.parent_id,
            fieldId: fieldUpdateData.id,
            fieldKey: fieldUpdateData.field_key,
            value: fieldUpdateData.value,
            deliverableId: fieldUpdateData.deliverableId
        }).then(function(resp) {
            if(router.currentRoute.name == 'projectDeliverable') {
                dispatch(
                    "fetchProjectDeliverable"
                )
            }
        });
    },

    async updateFieldValue({ commit, state, dispatch}, fieldUpdateData) {
        if (fieldUpdateData.field_type == "datepicker") {
            // convert the value to utc?
            if (fieldUpdateData.value != null) {
                let date = new Date();
                let currentTime =
                    date.getUTCHours() +
                    ":" +
                    date.getUTCMinutes() +
                    ":" +
                    date.getUTCSeconds();
                fieldUpdateData.value =
                    Vue.moment(fieldUpdateData.value).format("YYYY-MM-DD") +
                    " " +
                    currentTime;
            }

            // convert the value to UTC?
            // fieldUpdateData.value = Vue.moment(fieldUpdateData.value).utc();
        }
        if (
            typeof fieldUpdateData.taskFields !== "undefined" ||
            typeof fieldUpdateData.taskFieldId !== "undefined"
        ) {
            let taskFieldId = fieldUpdateData.taskFieldId,
                fieldValue = fieldUpdateData.title
                    ? fieldUpdateData.title
                    : fieldUpdateData.value,
                taskId =
                    typeof fieldUpdateData.id == "undefined"
                        ? fieldUpdateData.taskId
                        : fieldUpdateData.id;
            return await dispatch("updateTaskFieldValue", {
                taskFieldId,
                fieldValue,
                taskId
            }).then(function(resp) {
                	if(router.currentRoute.name == 'projectDeliverable') {
                	    dispatch(
                	        "fetchProjectDeliverable"
                	    )
                	}
                    return Promise.resolve(resp);
                }).catch(function(error) {
                    return Promise.reject(error);
                });
        } else {
            if (typeof fieldUpdateData.field_id !== "undefined") {
                dispatch("updateDeliverableFieldValue", {
                    id: fieldUpdateData.id,
                    parentId: fieldUpdateData.parent_id,
                    fieldId: fieldUpdateData.field_id,
                    fieldKey: fieldUpdateData.field_key,
                    value: fieldUpdateData.value,
                    deliverableId: fieldUpdateData.deliverable_id
                }).then(function(resp) {
                	if(router.currentRoute.name == 'projectDeliverable') {
                	    dispatch(
                	        "fetchProjectDeliverable"
                	    )
                	}
                });
            } else {
                dispatch("updateDeliverableFieldValue", {
                    value: fieldUpdateData.title,
                    deliverableId:
                        typeof fieldUpdateData.deliverableId != "undefined"
                            ? fieldUpdateData.deliverableId
                            : fieldUpdateData.id,
                    departments: fieldUpdateData.departments,
                    subjects: fieldUpdateData.subjects,
                    tags: fieldUpdateData.tags
                }).then(function(resp) {
                	if(router.currentRoute.name == 'projectDeliverable') {
                	    dispatch(
                	        "fetchProjectDeliverable"
                	    )
                	}
                    if(
                        resp.data.updatedField == "title" &&
                        resp.data.deliverableDetail.id === getters.breadcrumbs.parent_deliverable_id
                    ) {
                        // set new title to breadcrumbs if deliverable_id same
                        commit(
                            "setBreadcrumbs",
                            {
                                ...getters.breadcrumbs,
                                "parent_deliverable_title": resp.data.deliverableDetail.title,
                            }
                        );
                    }
                });
            }
        }

    },

    async updateTaskFieldValue({ commit, state, dispatch}, fieldUpdateData) {
        try {
        let response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateTaskFieldValue",
            fieldUpdateData
        );

        if(response.data.taskFieldDetail != 'status_note') {
            commit("setDeliverable", response.data.deliverableDetail);
            commit("setDeliverableDetail", response.data.deliverableDetail);
            if(router.currentRoute.name == 'myWeek') {
                commit("groupUserDeliverable");
            }
        }
        let refreshListQuery = ["title", "status_note"];
        if (router.currentRoute.name == "myWeek") {
            if (typeof response.data.taskFieldDetail != "undefined") {
                if (
                    (state.myWeekDeliverableFilter.query !=
                        "" &&
                        refreshListQuery.indexOf(
                            response.data.taskFieldDetail
                        ) != -1) ||
                    (state.myWeekDeliverableFilter.status
                        .length > 0 &&
                        response.data.taskFieldDetail == "status") ||
                    (state.myWeekDeliverableFilter.coordinator
                        .length > 0 &&
                        response.data.taskFieldDetail == "user") ||
                    (state.myWeekDeliverableFilter
                        .filterByDate &&
                        response.data.taskFieldDetail == "due_date") ||
                    (state.myWeekDeliverableFilter
                        .filterNoDueDate &&
                        response.data.taskFieldDetail == "due_date")
                ) {
                    dispatch(
                        "refreshWithMatchingTaskFilters",
                        response.data.taskFieldDetail
                    );
                }
            }
        }
        else if (router.currentRoute.name == "mediaCallPage") {
            if (typeof response.data.taskFieldDetail != "undefined") {
                if (
                    (state.mediaCallFilter.query != "" &&
                        refreshListQuery.indexOf(
                            response.data.taskFieldDetail
                        ) != -1) ||
                    (state.mediaCallFilter.status.length >
                        0 &&
                        response.data.taskFieldDetail == "status") ||
                    (state.mediaCallFilter.coordinator
                        .length > 0 &&
                        response.data.taskFieldDetail == "user") ||
                    (state.mediaCallFilter.filterByDate &&
                        response.data.taskFieldDetail == "due_date") ||
                    (state.mediaCallFilter
                        .filterNoDueDate &&
                        response.data.taskFieldDetail == "due_date")
                ) {
                    dispatch(
                        "refreshWithMatchingTaskFilters",
                        response.data.taskFieldDetail
                    );
                }
            }
        } else {
            if (typeof response.data.taskFieldDetail != "undefined") {
                if (
                    (state.deliverableFilter.query != "" &&
                        refreshListQuery.indexOf(
                            response.data.taskFieldDetail
                        ) != -1) ||
                    (state.deliverableFilter.status.length >
                        0 &&
                        response.data.taskFieldDetail == "status") ||
                    (state.deliverableFilter.coordinator
                        .length > 0 &&
                        response.data.taskFieldDetail == "user") ||
                    (state.deliverableFilter.filterByDate &&
                        response.data.taskFieldDetail == "due_date") ||
                    (state.myWeekDeliverableFilter
                        .filterNoDueDate &&
                        response.data.taskFieldDetail == "due_date")
                ) {
                    dispatch(
                        "refreshWithMatchingTaskFilters",
                        response.data.taskFieldDetail
                    );
                }
            }
        }
        if(response.data.taskFieldDetail == 'user') {
            commit("changeForceUpdateCounter");
        }
        if(response.data.taskFieldDetail == 'due_date') {
            dispatch("fetchDeliverableCount");
        }
            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error.response);
        }
    },

    async refreshWithMatchingTaskFilters({ commit, getters, dispatch }, taskFieldDetail) {
        dispatch("fetchDeliverableNoScroll", getters.currentPage);
    },

    async getPreference({ commit }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "getPreference"
        );
        commit("setUserPreference", response.data.preferenceDetail);
        commit("setLoggedInUserData", response.data.loggedInUserData);
    },

    async savePreference({ commit }, preferenceData) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "savePreference",
            {
                preferenceData: preferenceData
            }
        );

        if (router.currentRoute.name == "myWeek") {
            commit("changeForceUpdateCounter");
        }
        commit("setUserPreference", response.data.preferenceDetail);
    },

    async updateDeliverableFieldValue({ commit, getters, dispatch  }, fieldUpdateData) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateDeliverableFieldValue",
            fieldUpdateData
        );
        // commit setDeliverableDetail is commented because update field calls this api again 
        //commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
        if (router.currentRoute.name == "myWeek") {
            commit("groupUserDeliverable");
        }
        return response;
    },

    async addDeliverableContact({ commit, dispatch }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL +
                "addDeliverableContact/" +
                payload.deliverableId,
            payload.contactData
        );
        // commit("setDeliverables", response.data);
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
        if (response.data.contactAdded) {
            // dispatch('countOutlets')
            dispatch("fetchDeliverables");
        }
    },

    async countOutlets({ commit, getters }) {
        let filter = false;
        if (router.currentRoute.name == "myWeek") {
            filter = JSON.parse(
                JSON.stringify(getters.myWeekDeliverableFilter)
            );
        } else if (router.currentRoute.name == "mediaCallPage") {
            filter = JSON.parse(JSON.stringify(getters.mediaCallFilter));
        } else {
            filter = JSON.parse(JSON.stringify(getters.deliverableFilter));
        }
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "countOutlets",
            filter
        );
        commit("setOutletCount", response.data);
    },

    async changeLeadCoordinator({ commit }, payload) {
        if(payload.lead_coordinator != null) {
            const response = await axios.put(
                process.env.MIX_API_BASE_URL +
                    "changeLeadCoordinator/" +
                    payload.id,
                payload.lead_coordinator
            );
            // commit("setDeliverables", response.data);
            commit("setDeliverableDetail", response.data.deliverableDetail);
            commit("setDeliverable", response.data.deliverableDetail);
        }
    },

    async changeProjectEngagementManager({ getters, commit, dispatch }, payload) {
        if (payload.engagement_manager != null) {
            const response = await axios.put(
                process.env.MIX_API_BASE_URL +
                    "changeProjectEngagementManager/" +
                    payload.id,
                payload.engagement_manager
            );
            commit("setProjectDetail", response.data);
            if (router.currentRoute.name == 'myProjects') {
                dispatch('fetchUserProjectNoSet', getters.currentPage).then((r) => {
                    const list = r.data.userProjects.data;
                    for (let i in list) {
                        if (list[i].id == response.data.data.id) {
                            return;
                        }
                    }

                    // const orgProject = getters.userProjects.find(x => x.id == response.data.data.id);
                    // orgProject.notAvailable = true;
                    $(`#my__projects__list .project-id-${response.data.data.id} .project-list-no-relation-area`).addClass("show");
                    $(`#my__projects__list .project-id-${response.data.data.id} .project-list-content`).addClass("hide");
                    dispatch("manageProjectUndoActivation", response.data.data.id);
                });
            }
        }
    },
    async changeProjectLead({ getters, commit, dispatch }, payload) {
        if (payload.project_lead != null) {
            const response = await axios.put(
                process.env.MIX_API_BASE_URL +
                    "changeProjectLead/" +
                    payload.id,
                payload.project_lead
            );
            commit("setProjectDetail", response.data);
            if (router.currentRoute.name == 'myProjects') {
                dispatch('fetchUserProjectNoSet', getters.currentPage).then((r) => {
                    const list = r.data.userProjects.data;
                    for (let i in list) {
                        if (list[i].id == response.data.data.id) {
                            return;
                        }
                    }

                    // const orgProject = getters.userProjects.find(x => x.id == response.data.data.id);
                    // orgProject.notAvailable = true;
                    $(`#my__projects__list .project-id-${response.data.data.id} .project-list-no-relation-area`).addClass("show");
                    $(`#my__projects__list .project-id-${response.data.data.id} .project-list-content`).addClass("hide");
                    dispatch("manageProjectUndoActivation", response.data.data.id);
                });
            }
        }
    },

    async changeProjectLeaders({ getters, commit, dispatch }, payload) {
        if (payload.project_leaders != null) {
            const response = await axios.put(
                process.env.MIX_API_BASE_URL +
                    "changeProjectLead/" +
                    payload.id,
                payload.project_leaders
            );
            commit("setProjectDetail", response.data);
            if (router.currentRoute.name == 'myProjects') {
                dispatch('fetchUserProjectNoSet', getters.currentPage).then((r) => {
                    const list = r.data.userProjects.data;
                    for (let i in list) {
                        if (list[i].id == response.data.data.id) {
                            return;
                        }
                    }

                    // const orgProject = getters.userProjects.find(x => x.id == response.data.data.id);
                    // orgProject.notAvailable = true;
                    $(`#my__projects__list .project-id-${response.data.data.id} .project-list-no-relation-area`).addClass("show");
                    $(`#my__projects__list .project-id-${response.data.data.id} .project-list-content`).addClass("hide");
                    dispatch("manageProjectUndoActivation", response.data.data.id);
                });
            }
        }
    },

    async createRole({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + 'createRole',
            {newRole: payload.newRole}
        );
        commit('setAllRoles', response.data.allRoles);
        return response.data.newRole;
    },

    async updateRole({ commit }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + 'updateRole/' + payload.id,
            payload
        );
        if(response.data == false) {
            toast.error('Role name must be unique.');
        } else {
            commit('setAllRoles', response.data);
        }
        return response.data;
    },

    async removeRole({ commit }, payload) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL + 'removeRole/' + payload.id
        );
        if(response.data == false) {
            toast.error('Role is in use and cannot be removed at the moment.');
        } else {
            commit('setAllRoles', response.data);
        }
        return response.data;
    },

    async createDeliverableType({ commit }, deliverableType) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "createDeliverableType",
            deliverableType
        );
        commit("setDeliverableTypes", response.data);
    },

    async createTag({ commit }, newTag) {
        let deliverableId =
            typeof newTag.deliverableId == "undefined"
                ? this.getters.deliverableDetail.deliverableId
                : newTag.deliverableId;
        if (deliverableId == null) {
            if (this.getters.deliverableDetail.deliverableId > 0) {
                deliverableId = this.getters.deliverableDetail.deliverableId;
            }
        }

        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "createTag/" + deliverableId,
            {
                newTag:
                    typeof newTag.newTag != "undefined" ? newTag.newTag : newTag
            }
        );
        commit("setTags", response.data.tags);
        // commit("setDeliverables", response.data);
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
        commit("groupUserDeliverable");
    },

    async createCustomDeliverableTag({ commit }, payload) {
        let deliverableId =
            typeof payload.deliverableId == "undefined"
                ? this.getters.deliverableDetail.deliverableId
                : payload.deliverableId;
        if (deliverableId == null) {
            if (this.getters.deliverableDetail.deliverableId > 0) {
                deliverableId = this.getters.deliverableDetail.deliverableId;
            }
        }

        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "createCustomDeliverableTag/" + deliverableId,
            {
                newTag:
                    typeof payload.newTag != "undefined" ? payload.newTag : payload
            }
        );
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
    },

    async removeDeliverableContact({ commit }, contactId) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "removeDeliverableContact/" +
                this.getters.deliverableDetail.deliverableId +
                "/contactId/" +
                contactId
        );
        // commit("setDeliverables", response.data);
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
    },

    async addDeliverableSpokesperson({ commit }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL +
                "addDeliverableSpokesperson/" +
                payload.deliverableId,
            payload.contactData
        );
        // commit("setDeliverables", response.data);
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
    },
    async removeDeliverableSpokesperson({ commit }, contactId) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "removeDeliverableSpokesperson/" +
                this.getters.deliverableDetail.deliverableId +
                "/contactId/" +
                contactId
        );
        // commit("setDeliverables", response.data);
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
    },

    async addDeliverableWho({ commit, dispatch }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL +
                "addDeliverableWho/" +
                payload.deliverableId,
            payload.contactData
        );
        // commit("setDeliverables", response.data);
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
        if (response.data.contactAdded) {
            dispatch("countOutlets");
        }
    },
    async removeDeliverableWho({ commit, dispatch, state }, contactId) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "removeDeliverableWho/" +
                state.deliverableDetail.deliverableId +
                "/contactId/" +
                contactId
        );
        // commit("setDeliverables", response.data);
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
        dispatch("countOutlets");
    },

    
    async addDeliverableCfContact({ commit }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL +
                "addDeliverableCfContact/" +
                payload.deliverableId,
            {...payload},
        );
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
    },
    async removeDeliverableCfContact({ commit }, payload) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "removeDeliverableCfContact/" +
                payload.belongsToId +
                "/contactId/" +
                payload.contactId,
                {
                    params: {
                        currentFieldKey: payload.currentFieldKey
                    }
                }
        );
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
    },

    async createProjectCfContact({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "createProjectCfContact",
            {
                component: router.currentRoute.name,
                ...payload
            }
        );
        commit("setProjectDetail", response.data.projectDetail);
        commit("setContacts", response.data.contacts);
    },

    async updateProjectCfContact({ commit, state }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateProjectCfContact",
            payload
        );
        commit("setProjectDetail", response.data.projectDetail);
        commit("setContacts", response.data.contacts);
    },

    async addProjectContact({ commit, dispatch }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL +
                "addProjectContact/" +
                payload.projectId,
                {
                    component: router.currentRoute.name,
                    ...payload.contactData,
                    ...payload
                }
        );
        commit("setProjectDetail", response.data.projectDetail);
        if (response.data.contactAdded) {
            dispatch("fetchDeliverables");
        }
    },

    async removeProjectCfContact({ commit }, payload) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "removeProjectCfContact/" +
                payload.belongsToId +
                "/contactId/" +
                payload.contactId ,
                {
                    params: {
                        currentFieldKey: payload.currentFieldKey
                    }
                }
        );
        commit("setProjectDetail", response.data.projectDetail);
    },

    async createNewProject({ commit }, payload) {
        toast.info('Creating project...');
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
            'createNewProject'
        );
        toast.hide();
        commit("setProjectDetail", response.data.projectDetail);
        return response.data.projectDetail;
    },

    async createNewDeliverable({ commit, dispatch }, payload) {
        toast.info("Creating deliverable...");

        var more_fields = {};
        if (typeof payload.social_campaign_id != "undefined") {
        	if (payload.social_campaign_id > 0) {
        		more_fields.social_campaign_id = payload.social_campaign_id;
        	}
        }
        if (typeof payload.social_series_id != "undefined") {
        	if (payload.social_series_id > 0) {
        		more_fields.social_series_id = payload.social_series_id;
        	}
        }
        if (typeof payload.parent_deliverable_id != "undefined") {
        	if (payload.parent_deliverable_id > 0) {
        		more_fields.parent_deliverable_id = payload.parent_deliverable_id;
        	}
        }

        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "createNewDeliverable/" +
                payload.type,
                more_fields
        );
        toast.hide();
        // commit("setDeliverables", response.data);
        // dispatch("fetchDeliverables");
        commit("setDeliverableDetail", response.data.deliverableDetail);
    },

    async createDeliverableForProject({ commit }, payload) {
        toast.info("Creating deliverable...");
        var type = payload.type;
        if (payload.deliverableTemplateId) {
        	type = 	payload.deliverableTemplateId;
        }

        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
                "createDeliverableForProject/" +
                payload.projectId + "/" + type
        );
        toast.hide();
        commit("setDeliverableDetail", response.data.deliverableDetail);
    },

    async removeDeliverable({ commit, state, dispatch }, payload) {
        toast.info("Removing deliverable...");
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "removeDeliverable/" +
                payload.deliverableId
        ).then(response => {
        		let x = 0;
			    if (
			        typeof state.deliverables != "undefined"
			    ) {
			        for (x = 0; x < Object.keys(state.deliverables).length; x++) {
			            if (typeof state.deliverables[x] != 'undefined' && state.deliverables[x].id == payload.deliverableId) {
			                Vue.delete(state.deliverables, x);
			            }
			        }
			    }
			    if (
			        typeof state.calendarDeliverables != "undefined"
			    ) {
			        for (x = 0; x < Object.keys(state.calendarDeliverables).length; x++) {
			            if (typeof state.calendarDeliverables[x] != 'undefined' && state.calendarDeliverables[x].id == payload.deliverableId) {
			                Vue.delete(state.calendarDeliverables, x);
			            }
			        }
			    }
			    if (
			        typeof state.projectDeliverables != "undefined"
			    ) {
			        for (x = 0; x < Object.keys(state.projectDeliverables).length; x++) {
			            if (typeof state.projectDeliverables[x] != 'undefined' && state.projectDeliverables[x].id == payload.deliverableId) {
			                Vue.delete(state.projectDeliverables, x);
			            }
			        }
			    }
			    if (
			        typeof state.mergeDeliverables != "undefined"
			    ) {
			        for (x = 0; x < Object.keys(state.mergeDeliverables).length; x++) {
			            if (typeof state.mergeDeliverables[x] != 'undefined' && state.mergeDeliverables[x].id == payload.deliverableId) {
			                Vue.delete(state.mergeDeliverables, x);
			            }
			        }
			    }
			    if (
			        typeof state.unGroupedUserDeliverables.data != "undefined" &&
			        state.unGroupedUserDeliverables.data.length
			    ) {

			        for (
			            x = 0;
			            x < Object.keys(state.unGroupedUserDeliverables.data).length;
			            x++
			        ) {
			            if (
			                state.unGroupedUserDeliverables.data[x].id ==
			                payload.deliverableId
			            ) {
			                Vue.delete(
			                    state.unGroupedUserDeliverables.data,
			                    x
			                );
			            }
			        }
			    }
                return {...response};
  			})
		.catch(error => {
            console.error(error);
            if (error.response && error.response.status) {
                return {
                    success: false,
                    status: error.response.status,
                    message: error.response.data?.message || 'Deliverable could not be deleted.',
                };
            }
    	});
        return response;
    },

    async removeProjectDeliverable({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL +
            'removeProjectDeliverable',
            payload
        );
        commit("setProjectDetail", response.data.projectDetail);
    },

    async removeProject({ commit, dispatch }, payload) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "removeProject/" +
                payload.projectId
        );
        commit("setProjects", response.data);
        // dispatch("fetchDeliverables");
        //commit("setDeliverables", response.data);
        //commit("toggleEditDeliverableModal");
    },

    async mergeProjects({ commit, dispatch }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "mergeProjects",
            payload
        );
        return response;
    },

    async duplicateProject({ commit, dispatch}, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "duplicateProject",
            payload
        );
        return response;
    },

    async duplicateOrganicSocialMediaPlan({ commit, dispatch}, payload) {
    	const response = await axios.put(
    	    process.env.MIX_API_BASE_URL +
    	    'duplicateOrganicSocialMediaPlan/' + payload.deliverableId
    	);
        return response;
    },

    async removeSocialCampaign({ commit, dispatch }, payload) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "removeSocialCampaign/" +
                payload.socialCampaignId
        );
        commit("setAllSocialCampaigns", response.data.allSocialCampaigns.data);


		if(router.currentRoute.name == 'socialMediaGrouped') {
			dispatch("fetchSocialMediaDeliverablesGrouped");
		}
		if(router.currentRoute.name == 'socialMedia') {
			dispatch("fetchSocialMediaDeliverablesGrouped");
		}
        if(router.currentRoute.name == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverables");
        }
    },

    async removeSocialSeries({ commit, dispatch }, payload) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "removeSocialSeries/" +
                payload.socialSeriesId
        );
        commit("setAllSocialCampaigns", response.data);

		if(router.currentRoute.name == 'socialMediaGrouped') {
			dispatch("fetchSocialMediaDeliverablesGrouped");
		}
		if(router.currentRoute.name == 'socialMedia') {
			dispatch("fetchSocialMediaDeliverablesGrouped");
		}
        if(router.currentRoute.name == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverables");
        }
    },



    async removeProjectLinkUrl({ commit, state}, payload) {
        payload.projectId = state.projectDetail.projectId;
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL + "removeProjectLinkUrl/"
                + payload.projectId + "/" + payload.linkUrl
            ,
        );
        commit("setProjectDetail", response.data.projectDetail);
        commit("setProject", response.data.projectDetail);
    },

    async duplicateMediaCall({ commit }, payload) {

        // toast.info('Copying media call...');
        toast.info('Copying deliverable...');
        const response = await axios.put(
            process.env.MIX_API_BASE_URL +
            'duplicateMediaCall/' + payload.deliverableId
        );
        if(response.data.deliverableDetail) {
        	commit("setDeliverableDetail", response.data.deliverableDetail);
        	commit("setDeliverable", response.data.deliverableDetail);
        }
        toast.hide();
    },

    async duplicateMediaCallFromProject({ commit }, payload) {
        // toast.info('Copying media call...');
        toast.info('Copying deliverable...');
        const response = await axios.put(
            process.env.MIX_API_BASE_URL +
            'duplicateMediaCallFromProject/' + payload.deliverableId
        );
        toast.hide();
    },

    async duplicateSocialMedia({ commit, state }, payload) {
        toast.info('Copying social media...');
        const response = await axios.put(
            process.env.MIX_API_BASE_URL +
            'duplicateSocialMedia/' + payload.deliverableId
        );
        if(response.data.deliverableDetail) {
        	commit("setDeliverableDetail", response.data.deliverableDetail);
        	commit("setDeliverable", response.data.deliverableDetail);
        }
        toast.hide();
    },

    async createContact({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "addContact",
            payload
        );
        // commit("setDeliverables", response.data);
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
        commit("setContacts", response.data.contacts);
    },

    async createSpokesperson({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "addSpokesperson",
            payload
        );
        // commit("setDeliverables", response.data);
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
        commit("setContacts", response.data.contacts);
    },
    async createWho({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "addWho",
            payload
        );
        // commit("setDeliverables", response.data);
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
        commit("setContacts", response.data.contacts);
        commit("setActiveContacts", response.data.activeContacts);
        commit("increaseOutletCount");
    },

    async fetchGeoData({ commit, getters }) {
        if(getters.allCountries.length == 0) {
            const response = await axios.get(
                process.env.MIX_API_BASE_URL + "fetchGeoData"
            );
            commit("setGeoData", response.data);
        }
    },

    async fetchAllRoles({ commit }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "fetchAllRoles"
        );
        commit('setAllRoles', response.data);
    },

    async fetchAllAbilities({ commit }) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + 'fetchAllAbilities'
        );
        commit('setAllAbilities', response.data);
    },

    async fetchRolePermissions({ commit }, payload) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + 'fetchRolePermissions/' + payload.roleId
        );
        return response.data;
    },
    async fetchRolePreference({ commit }, payload) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + 'fetchRolePreference/' + payload.roleId
        );
        return response.data;
    },

    async saveRolePermissions({}, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + 'saveRolePermissions/' + payload.roleId,
            {
                ability: payload.ability,
                preference: payload.preference,
            }
        );
    },

    async updateContact({ commit, state }, payload) {
        payload.deliverableId = state.deliverableDetail.deliverableId;
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateContact",
            payload
        );
        // commit('setDeliverables', response.data)
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
        commit("setContacts", response.data.contacts);
    },

    async updateDepartment({ commit, dispatch }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateDepartment",
            payload
        );
        commit('updateDepartmentList', response.data)
    },

    async updateTag({ commit }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateTag",
            payload
        );
    },

    async updateContactGS({ commit }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateContactGS",
            payload
        );
    },

    async updateUser({ commit, dispatch }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateUser",
            payload
        );
        dispatch('fetchActiveUsers', {forced: true});
        dispatch('fetchUsers', {forced: true});
        return response;
    },

    async sendAdminResetPasswordLinkEmail({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "sendAdminResetPasswordLinkEmail",
            payload
        )
        return response;
    },

    async updateProfile({ commit, dispatch }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateProfile",
            payload
        );
        // dispatch("getPreferenceDeliverableTypes");
        dispatch("getDeliverableTypes");
        if (router.currentRoute.name == "myWeek" || router.currentRoute.name == "dashboard" || router.currentRoute.name == "mediaCallPage") {
            commit("setDataLoading", true);
            dispatch("fetchDeliverables").then(() => {
                commit("setDataLoading", false);
            });
        }
        if (router.currentRoute.name == "projectDeliverable") {
            commit("setDataLoading", true);
            dispatch("fetchProjectDeliverable").then(() => {
                commit("setDataLoading", false);
            });
        }
        return response;
    },

    async updateSpokesperson({ commit, state }, payload) {
        payload.deliverableId = state.deliverableDetail.deliverableId;
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateSpokesperson",
            payload
        );
        // commit('setDeliverables', response.data)
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
        commit("setContacts", response.data.contacts);
    },

    async updateWho({ commit, state }, payload) {
        payload.deliverableId = state.deliverableDetail.deliverableId;
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateWho",
            payload
        );
        // commit('setDeliverables', response.data)
        if(response.data.deliverableDetail) {
            commit("setDeliverableDetail", response.data.deliverableDetail);
            commit("setDeliverable", response.data.deliverableDetail);
        }
        //commit("setContacts", response.data.contacts);
    },

    async createCfContact({ commit }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "addCfContact",
            payload
        );
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
        commit("setContacts", response.data.contacts);
    },

    async updateCfContact({ commit, state }, payload) {
        payload.deliverableId = state.deliverableDetail.deliverableId;
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateCfContact",
            payload
        );
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
        commit("setContacts", response.data.contacts);
    },

    async updateDeliverableLinkUrl({ commit, state }, payload) {
        payload.deliverableId = state.deliverableDetail.deliverableId;
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateLinkUrl",
            payload
        );
        // commit('setDeliverables', response.data)
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
    },
    async updateProjectLinkUrl({ commit, state }, payload) {
        payload.projectId = state.projectDetail.projectId;
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateProjectLinkUrl",
            payload
        );
        // commit('setDeliverables', response.data)
        commit("setProjectDetail", response.data.projectDetail);
        commit("setProject", response.data.projectDetail);
    },
    async createDeliverableLinkUrl({ commit, state }, payload) {
        payload.deliverableId = state.deliverableDetail.deliverableId;
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "addLinkUrl",
            payload
        );
        // commit('setDeliverables', response.data)
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
    },
    async createProjectLinkUrl({ commit, state}, payload) {
        payload.projectId = state.projectDetail.projectId;
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "addProjectLinkUrl",
            payload
        );
        // commit('setDeliverables', response.data)
        commit("setProjectDetail", response.data.projectDetail);
        commit("setProject", response.data.projectDetail);
    },
    async removeDeliverableLinkUrl({ commit }, value_id) {
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL +
                "removeDeliverableFieldValue/" +
                this.getters.deliverableDetail.deliverableId +
                "/fieldValueId/" +
                value_id
        );
        // commit("setDeliverables", response.data);
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
    },

    async fetchAllCustomFields({ commit, getters }) {
        if(getters.allCustomFields.length == 0) {
            const response = await axios.get(
                process.env.MIX_API_BASE_URL + "fetchAllCustomFields"
            );
            commit("setCustomFields", response.data);
        }
    },

    async globalSearch({ commit, getters }, payload) {
        let urlQuery = '';
        let globalSearchFilter = getters.globalSearchFilter;
        let activeTab = 'all';
        if(typeof payload != 'undefined' && payload != '') {
            if(payload.type != 'all') {
                activeTab = payload.type;
                urlQuery = '?page='+payload.page+'&type='+payload.type;
                if(typeof payload.query != 'undefined') {
                    globalSearchFilter.query = payload.query;
                }
            }
        }

        if (globalSearchFilter.filterByDateCreated == true) {
            var s = Vue.moment(globalSearchFilter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            globalSearchFilter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(globalSearchFilter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            globalSearchFilter.dateCreatedRange.endDate = e.utc().format();
        }
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "globalSearch" + urlQuery,
            { params: globalSearchFilter }
        );

        commit('setGlobalSearchData', response.data);
    },

    async getReport({ getters }, payload) {
        let filter = JSON.parse(JSON.stringify(getters.reportFilter));

        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        let reportType = payload.reportType;
        let page = '';
        if(typeof payload.page != 'undefined') {
            page = '?page='+payload.page;
        }
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "getReport/" + reportType + page,
            filter
        );
        return response.data;
    },

    async downloadReport({getters}, payload) {
        let filter = JSON.parse(JSON.stringify(getters.reportFilter));

        if (filter.filterByDateCreated == true) {
            var s = Vue.moment(filter.dateCreatedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCreatedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCreatedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCreatedRange.endDate = e.utc().format();
        }
        if (filter.filterByDateCompleted == true) {
            var s = Vue.moment(filter.dateCompletedRange.startDate);
            s.set("hour", 0);
            s.set("minute", 0);
            s.set("second", 0);
            filter.dateCompletedRange.startDate = s.utc().format();
            var e = Vue.moment(filter.dateCompletedRange.endDate);
            e.set("hour", 23);
            e.set("minute", 59);
            e.set("second", 59);
            filter.dateCompletedRange.endDate = e.utc().format();
        }

        let reportType = payload.reportType;
        if(typeof payload.page != 'undefined') {
            filter.page = payload.page;
        }
        const response = await axios({
            method: 'post',
            url: process.env.MIX_API_BASE_URL + "getReport/" + reportType + "/" + payload.exportType,
            responseType: 'arrayBuffer',
            data: filter
          });
        return response.data;
    },

    async highlightRelatedTasks({ commit, state }, groupedModelId) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + 'fetchTaskIdsToHighlight/' + groupedModelId,
        );
        if (Array.isArray(response.data) && response.data.length > 0) {
            commit("setTasksToHighlight", response.data);
        } else {
            console.log('No task alerts found.');
        }        
        
        if (state.taskHighlightTimeout) {
            clearTimeout(state.taskHighlightTimeout)
        }

        state.taskHighlightTimeout = setTimeout(() => {
            commit("setTasksToHighlight", []);
        }, 7 * 1000);
    },

    highlightTask({ commit, state }, taskId) {
        commit("setTaskHighlighted", taskId);

        if (state.taskHighlightTimeout) {
            clearTimeout(state.taskHighlightTimeout)
        }

        state.taskHighlightTimeout = setTimeout(() => {
            commit("setTaskHighlighted", null);
        }, 7 * 1000);
    },
    clearTaskHightlighted({ commit, state }) {
        if (state.taskHighlightTimeout) {
            clearTimeout(state.taskHighlightTimeout)
        }
        commit("setTaskHighlighted", null);
    },

    async changeDeliverableType({ commit, state, dispatch }, payload) {
        if(payload.id != null && payload.deliverable_type != null) {
            const response = await axios.put(
                process.env.MIX_API_BASE_URL +
                    "changeDeliverableType/" +
                    payload.id,
                    {deliverable_type: payload.deliverable_type}
            );
            // commit("setDeliverables", response.data);
            if(response.data.deliverableDetail) {
            commit("setDeliverableDetail", response.data.deliverableDetail);
            commit("setDeliverable", response.data.deliverableDetail);
            }
            if (router.currentRoute.name == "myWeek") {
                commit("groupUserDeliverable");
            }
            if(router.currentRoute.name == 'projectDeliverable') {
                dispatch("fetchProjectDeliverable");
            }
        }
    },

    async updateDeliverableTitle({commit}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateDeliverableTitle/" + payload.id,
            payload
        )
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail)
    },

    async updateDeliverableDescription({commit}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateDeliverableDescription/" + payload.id,
            payload
        )
        commit("setDeliverableDetail", response.data.deliverableDetail);
    },
    
    async addDeliverableLanguage({commit}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "addDeliverableLanguage",
            payload
        );
        commit("setDeliverableDetail", response.data.deliverableDetail);
    },

    async removeDeliverableLanguage({commit}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "removeDeliverableLanguage",
            payload
        );
        commit("setDeliverableDetail", response.data.deliverableDetail);
    },

    async addDeliverableSocialNetwork({commit, dispatch}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "addDeliverableSocialNetwork",
            payload
        );
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);

        if(router.currentRoute.name == 'socialMediaGrouped') {
			dispatch("fetchSocialMediaDeliverablesGrouped");
		}
		if(router.currentRoute.name == 'socialMedia') {
			dispatch("fetchSocialMediaDeliverablesGrouped");
		}
        if(router.currentRoute.name == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverables");
        }
    },

    async removeDeliverableSocialNetwork({commit, dispatch}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "removeDeliverableSocialNetwork",
            payload
        );
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);

        if(router.currentRoute.name == 'socialMediaGrouped') {
			dispatch("fetchSocialMediaDeliverablesGrouped");
		}
		if(router.currentRoute.name == 'socialMedia') {
			dispatch("fetchSocialMediaDeliverablesGrouped");
		}
        if(router.currentRoute.name == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverables");
        }
    },

    async updateSocialMediaPostCaption({commit}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateSocialMediaPostCaption",
            payload
        );
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
    },

    async updateSocialMediaPostUrl({commit}, payload){
    	try {
	        const response = await axios.put(
	            process.env.MIX_API_BASE_URL + "updateSocialMediaPostUrl",
	            payload
	        );
	        commit("setDeliverableDetail", response.data.deliverableDetail);
            commit("setDeliverable", response.data.deliverableDetail);
	    } catch(e) {

	    }

    },

    async getSocialMediaPostShorterUrl({commit}, payload){
    	try {
	        const response = await axios.post(
	            process.env.MIX_API_BASE_URL + "getSocialMediaPostShorterUrl",
	            payload
	        );
            commit("setDeliverableDetail", response.data.deliverableDetail);
            commit("setDeliverable", response.data.deliverableDetail);
	    } catch(e) {
	    }
    },
    async getSocialMediaPostLongerUrl({commit}, payload){
    	try {
	        const response = await axios.post(
	            process.env.MIX_API_BASE_URL + "getSocialMediaPostLongerUrl",
	            payload
	        );
            commit("setDeliverableDetail", response.data.deliverableDetail);
            commit("setDeliverable", response.data.deliverableDetail);
	    } catch(e) {
	    }
    },

    async updateSocialMediaPostShortUrl({commit}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateSocialMediaPostShortUrl",
            payload
        );
        commit("setDeliverableDetail", response.data.deliverableDetail);

    },

    async updateSocialPostNetworkShareUrl({commit}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateSocialPostNetworkShareUrl",
            payload
        );

        // commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverableDetailSocialPostNetworkShareUrl", {payload:payload, response:response.data.deliverableDetail});

    },

    async createSocialPostComment({commit}, payload){
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "createSocialPostComment",
            payload
        );
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
    },

    async removeSocialPostComment({commit}, payload){
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "removeSocialPostComment",
            payload
        );
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
    },

    async getSocialPostImage({commit}, payload){
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "viewFileSocialMediaPost/" + payload.post_id + "/" + payload.file_id + "/" + payload.token + "/" + p
        );
        return response.data;
        //$socialPostId, $fileId, $token)
    },

    async updateFileAltTextSocialMediaPost({commit}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateFileAltTextSocialMediaPost",
            payload
        );
        commit("setDeliverableDetail", response.data.deliverableDetail);
    },

    async updateOrderFileSocialMediaPost({commit}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "reorderFileSocialMediaPost",
            payload
        );
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
    },

    async removeFileSocialMediaPost({commit}, payload){
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "removeFileSocialMediaPost",
            payload
        );
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
    },

    async splitSocialMediaNetworkPost({commit}, payload){
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "splitSocialMediaPostNetwork",
            payload
        );
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
    },

    async returnSocialMediaPostNetwork({commit}, payload){
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "returnSocialMediaPostNetwork",
            payload
        );
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
    },

    async updateDeliverablePublishDate({commit, dispatch, getters}, payload){


    	let myRouteName = await dispatch("getRouterNameFromLocationHref");


        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "updateDeliverablePublishDate",
            payload
        );

        // only update the deliverable detail if changing the one we are editing.
        if (payload.id == getters.deliverableDetail.id || payload.return_id == getters.deliverableDetail.id ) {
        	commit("setDeliverableDetail", response.data.deliverableDetail);
	        commit("setDeliverable", response.data.deliverableDetail);
        } else {
        	commit("setDeliverablePublishDate", {id:payload.id, value:payload.value});
        }
        commit("increaseRefreshKey");

        /*
        // don't fetch all of the results again, because it will blow away the listing.
        if(myRouteName == 'socialMediaGrouped') {
			dispatch("fetchSocialMediaDeliverablesGrouped");
		}
		if(myRouteName == 'socialMedia') {
			dispatch("fetchSocialMediaDeliverablesGrouped");
		}
        if(myRouteName == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverables");
        }
        */
    },


    async updateDeliverablePublishStatusBySlug({commit, dispatch}, payload){
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "updateDeliverablePublishStatusBySlug",
            payload
        );
        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);

        /*
        if(router.currentRoute.name == 'socialMediaGrouped') {
			dispatch("fetchSocialMediaDeliverablesGrouped");
		}
		if(router.currentRoute.name == 'socialMedia') {
			dispatch("fetchSocialMediaDeliverablesGrouped");
		}
        if(router.currentRoute.name == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverables");
        }
        */
    },

    async createSocialCampaign({ commit, getters, state, dispatch }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "createSocialCampaign",
            payload
        );

        const socialCampaigns = state.socialCampaigns;
        socialCampaigns.push({id: response.data.campaignDetails.id, title: response.data.campaignDetails.title});
        commit("setAllSocialCampaigns", socialCampaigns);
        commit("setSocialCampaignDetail", response.data.campaignDetails);


        let listSocialCampaigns = state.listSocialCampaigns;
        listSocialCampaigns.push({id: response.data.campaignDetails.id, title: response.data.campaignDetails.title, campaign_id:null, start_date:response.data.campaignDetails.start_date,end_date:response.data.campaignDetails.end_date,total_count:0, list_count:0});

        // resort the campaigns listing by title
         listSocialCampaigns = _.sortBy(listSocialCampaigns,"title");


        commit('setListSocialCampaigns', listSocialCampaigns);
        if(router.currentRoute.name == 'socialMediaGrouped') {
			dispatch("fetchSocialMediaDeliverablesGrouped");
		}
		if(router.currentRoute.name == 'socialMedia') {
			dispatch("fetchSocialMediaDeliverablesGrouped");
		}
        if(router.currentRoute.name == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverables");
        }
    },

    async createSocialCampaignForDeliverable({ commit, getters, state, dispatch }, payload) {
        const rCampaign = await axios.post(
            process.env.MIX_API_BASE_URL + "createSocialCampaign",
            payload
        );

        const socialCampaigns = state.socialCampaigns;
        socialCampaigns.push({id: rCampaign.data.campaignDetails.id, title: rCampaign.data.campaignDetails.title});
        commit("setAllSocialCampaigns", socialCampaigns);
        commit("setSocialCampaignDetail", rCampaign.data.campaignDetails);

        dispatch("setDeliverableSocialCampaign", {
            id: payload.id,
            social_campaign_id: rCampaign.data.campaignDetails.id
        });

        
    },

    async updateSocialCampaign({ commit }, payload) {
        let campaignDetail = {};
        campaignDetail = {
            id: payload.id,
            title: payload.title,
            slug: payload.slug,
            client_id: payload.client_id,
            campaign_id: payload.campaign_id,
            // engagement_manager: payload.engagement_manager,
            // project_lead: payload.project_lead,
            start_date: payload.start_date,
            end_date: payload.end_date
        }

        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateSocialCampaign/" + campaignDetail.id,
            campaignDetail
        );


        commit("setSocialCampaign", response.data.socialCampaignDetail);
    },
    async updateSocialCampaignFieldValue({}, fieldUpdateData) {
        let updateData = {};
        if(typeof fieldUpdateData.field_id != 'undefined') {
            let fieldValueData = fieldUpdateData.field_key != 'color_picker' ? fieldUpdateData.value : fieldUpdateData.value.hex;
            updateData = {
                compontent: router.currentRoute.name,
                id: fieldUpdateData.id,
                parentId: fieldUpdateData.parent_id,
                fieldId: fieldUpdateData.field_id,
                socialCampaignFieldId: fieldUpdateData.socialCampaignFieldId,
                value: fieldValueData,
                socialCampaignId: typeof fieldUpdateData.social_campaign_id == 'undefined' ? fieldUpdateData.id : fieldUpdateData.social_campaign_id
            };
        } else {
            updateData = {
                compontent: router.currentRoute.name,
                value: fieldUpdateData.title,
                socialCampaignId:
                    typeof fieldUpdateData.social_campaign_id != "undefined"
                        ? fieldUpdateData.social_campaign_id
                        : fieldUpdateData.id,
                departments: fieldUpdateData.departments,
            };
        }

        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateSocialCampaignFieldValue",
            updateData
        );
    },
    async updateSocialSeries({ commit }, payload) {
        let seriesDetail = {};
        seriesDetail = {
            id: payload.id,
            title: payload.title,
            slug: payload.slug,
            client_id: payload.client_id,
            series_id: payload.series_id,
            // engagement_manager: payload.engagement_manager,
            // project_lead: payload.project_lead,
            start_date: payload.start_date,
            end_date: payload.end_date
        }

        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateSocialSeries/" + seriesDetail.id,
            seriesDetail
        );

        commit("setAllSocialSeries", response.data.allSocialSeries.data);
        commit("setSocialSeries", response.data.allSocialSeries.data);

    },
    async updateSocialSeriesFieldValue({}, fieldUpdateData) {
        let updateData = {};
        if(typeof fieldUpdateData.field_id != 'undefined') {
        	let fieldValueData = fieldUpdateData.value;
        	if (fieldUpdateData.field_key == 'color_picker') {
        		if (typeof fieldUpdateData.value.hex != "undefined") {
        			fieldValueData = fieldUpdateData.value.hex;
        		}
        	}
            updateData = {
                compontent: router.currentRoute.name,
                id: fieldUpdateData.id,
                parentId: fieldUpdateData.parent_id,
                fieldId: fieldUpdateData.field_id,
                socialSeriesFieldId: fieldUpdateData.socialSeriesFieldId,
                value: fieldValueData,
                socialSeriesId: typeof fieldUpdateData.social_series_id == 'undefined' ? fieldUpdateData.id : fieldUpdateData.social_series_id
            };
        } else {
            updateData = {
                compontent: router.currentRoute.name,
                value: fieldUpdateData.title,
                socialSeriesId:
                    typeof fieldUpdateData.social_series_id != "undefined"
                        ? fieldUpdateData.social_campaign_id
                        : fieldUpdateData.id,
                departments: fieldUpdateData.departments,
            };
        }

        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateSocialSeriesFieldValue",
            updateData
        );
    },
    async changeSocialCampaignEngagementManager({}, payload) {
        let data = payload.engagement_manager;
        data.social_campaign_id = payload.social_campaign_id;

        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "changeSocialCampaignEngagementManager/" + payload.id,
            data
        );
    },
    async changeSocialCampaignLead({}, payload) {
        let data = payload.project_lead;
        data.social_campaign_id = payload.social_campaign_id;

        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "changeSocialCampaignLead/" + payload.id,
            data
        );
    },
    async changeSocialSeriesEngagementManager({}, payload) {
        let data = payload.engagement_manager;
        data.social_series_id = payload.social_series_id;

        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "changeSocialSeriesEngagementManager/" + payload.id,
            data
        );
    },
    async changeSocialSeriesLead({}, payload) {
        let data = payload.project_lead;
        data.social_series_id = payload.social_series_id;

        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "changeSocialSeriesLead/" + payload.id,
            data
        );
    },
    async fetchSocialMediaCampaignDetail({ commit }, id) {
        const response = await axios.get(
            process.env.MIX_API_BASE_URL + "getSocialCampaignDetail/" + id
        );

        commit("setSocialCampaignDetail", response.data.data);
    },
    async setDeliverableSocialCampaign({ commit, dispatch }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "setDeliverableSocialCampaign/",
            payload
        );

        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
        if(router.currentRoute.name == 'socialMediaGrouped') {
			dispatch("fetchSocialMediaDeliverablesGrouped");
		}
		if(router.currentRoute.name == 'socialMedia') {
			dispatch("fetchSocialMediaDeliverablesGrouped");
		}
        if(router.currentRoute.name == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverables");
        }
    },
    async setDeliverableSocialSeries({ dispatch }, payload) {
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "setDeliverableSocialSeries/",
            payload
        );

        // dispatch("fetchSocialMediaDeliverablesGrouped");
    },

    async setAsManuallyUpdated({commit}, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "setAsManuallyUpdated",
            payload
        );

        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
    },

    async setAsAutomaticallyUpdated({commit}, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "setAsAutomaticallyUpdated",
            payload
        );

        commit("setDeliverableDetail", response.data.deliverableDetail);

        // API returns master deliverable so this doesn't work, need to update PHP function
        // commit("setDeliverable", response.data.deliverableDetail);
    },

    async createSocialSeriesMultipleDates({commit, dispatch}, payload){
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "createSocialSeriesMultipleDates",
            payload
        );

        commit("setDeliverableDetail", response.data.deliverableDetail);
        commit("setDeliverable", response.data.deliverableDetail);
        if(router.currentRoute.name == 'socialMediaGrouped') {
			dispatch("fetchSocialMediaDeliverablesGrouped");
		}
		if(router.currentRoute.name == 'socialMedia') {
			dispatch("fetchSocialMediaDeliverablesGrouped");
		}
        if(router.currentRoute.name == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverables");
        }
    },

    async createSocialSeries({ commit, dispatch }, payload) {
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "createSocialSeries",
            payload
        );
        if (payload.from_deliverable_id) {
    	    commit("replaceDeliverableDetail", {deliverableDetail:response.data.deliverableDetail, id:payload.from_deliverable_id});
    	}
        commit("setDeliverableDetail", response.data.deliverableDetail);


        if(router.currentRoute.name == 'socialMediaGrouped') {
			dispatch("fetchSocialMediaDeliverablesGrouped");
		}
		if(router.currentRoute.name == 'socialMedia') {
			dispatch("fetchSocialMediaDeliverablesGrouped");
		}
        if(router.currentRoute.name == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverables");
        }
    },

    async addSocialCampaignResourceUrl({ commit, getters }, payload) {
        payload.socialCampaignId = getters.socialCampaignDetail.id;
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "addSocialCampaignResourceUrl",
            payload
        );

        commit("setSocialCampaignDetail", response.data.socialCampaignDetail);
    },
    async updateSocialCampaignResourceUrl({ commit, getters }, payload) {
        payload.socialCampaignId = getters.socialCampaignDetail.id;
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateSocialCampaignResourceUrl",
            payload
        );

        commit("setSocialCampaignDetail", response.data.socialCampaignDetail);
    },
    async removeSocialCampaignResourceUrl({ commit, getters }, payload) {
        payload.socialCampaignId = getters.socialCampaignDetail.id;
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL + "removeSocialCampaignResourceUrl/"
                + payload.socialCampaignId + "/" + payload.linkUrl
        );
        commit("setSocialCampaignDetail", response.data.socialCampaignDetail);
    },

    async addSocialSeriesResourceUrl({ commit, getters }, payload) {
        payload.socialSeriesId = getters.socialSeriesDetail.id;
        const response = await axios.post(
            process.env.MIX_API_BASE_URL + "addSocialSeriesResourceUrl",
            payload
        );

        commit("setSocialSeriesDetail", response.data.socialSeriesDetail);
    },
    async updateSocialSeriesResourceUrl({ commit, getters }, payload) {
        payload.socialSeriesId = getters.socialSeriesDetail.id;
        const response = await axios.put(
            process.env.MIX_API_BASE_URL + "updateSocialSeriesResourceUrl",
            payload
        );

        commit("setSocialSeriesDetail", response.data.socialSeriesDetail);
    },
    async removeSocialSeriesResourceUrl({ commit, getters }, payload) {
        payload.socialSeriesId = getters.socialSeriesDetail.id;
        const response = await axios.delete(
            process.env.MIX_API_BASE_URL + "removeSocialSeriesResourceUrl/"
                + payload.socialSeriesId + "/" + payload.linkUrl
        );
        commit("setSocialSeriesDetail", response.data.socialSeriesDetail);
    },
    async doAction({commit, getters, dispatch}, payload){
        let response = null;
        try{
            response = await axios.put(
                process.env.MIX_API_BASE_URL + "doAction",
                payload
            );
            if (typeof response.data.deliverableDetail != "undefined") {
	            commit("setDeliverableDetail", response.data.deliverableDetail);
	        	commit("setDeliverable", response.data.deliverableDetail);
	        } else {
	        	 if(typeof response.data.message != 'undefined')
                    toast.error(response.data.message);
                else
                    toast.error('You do not have permission to perform this action.');
                return new Promise((resolve, reject) => {
                    reject("");
                });
	        }
        } catch(e){
            console.error(e);
        }
        
        if(router.currentRoute.name == 'socialMediaSchedule') {
        	dispatch("fetchCalendarDeliverables");
        }

    },

    async checkActiveJobs({ commit }) {
        try {
            const response = await axios.get(
                process.env.MIX_API_BASE_URL + "checkActiveJobs"
            );
            return response.data;
        } catch (error) {
            console.error("Error checking active jobs:", error);
            throw error;
        }
    },

    async getJobStatus({ commit }, jobKey) {
        try {
            const response = await axios.get(
                process.env.MIX_API_BASE_URL + `job-status/${jobKey}`
            );
            return response.data;
        } catch (error) {
            console.error("Error fetching job status:", error);
            throw error;
        }
    },

    async cancelDuplicateJob({ commit }, jobKey) {
        try {
            const response = await axios.delete(
                process.env.MIX_API_BASE_URL + `cancel-job/${jobKey}`
            );
            return response.data;
        } catch (error) {
            // Log detailed error information
            if (error.response) {
                console.error("Error cancelling job:", {
                    status: error.response.status,
                    statusText: error.response.statusText,
                    data: error.response.data,
                    jobKey: jobKey
                });
            } else if (error.request) {
                console.error("Network error cancelling job - no response received:", error.message);
            } else {
                console.error("Error setting up cancel job request:", error.message);
            }
            
            return { success: false, error: error.message };
        }
    },
};

export const deliverables = {
    state,
    mutations,
    getters,
    actions,
    namespaceKey: ""
};


export const relatedDeliverables = {
	namespaced: true,
    state,
    mutations,
    getters,
    actions
};


export const relatedDeliverablesCalendar = {
	namespaced: true,
    state,
    mutations,
    getters,
    actions
};

